import { useCallback, useEffect, useMemo, useState } from 'react';

export type AdminLanguage = 'en' | 'ar';

const STORAGE_KEY = 'forgedos_admin_language';
const LANGUAGE_EVENT = 'forgedos-admin-language-change';

type TranslationKey =
  | 'language.english'
  | 'language.arabic'
  | 'language.switchToEnglish'
  | 'language.switchToArabic'
  | 'nav.logout'
  | 'nav.dashboard'
  | 'nav.role.admin'
  | 'nav.role.driver'
  | 'nav.role.call_center'
  | 'admin.restaurantOperations'
  | 'admin.dashboard'
  | 'admin.enableOrderAlarm'
  | 'admin.notificationsOn'
  | 'admin.notificationsOff'
  | 'admin.notificationSettings'
  | 'admin.dismiss'
  | 'admin.hideTools'
  | 'admin.tools'
  | 'admin.opsCommand'
  | 'admin.setupWizard'
  | 'admin.menuWizard'
  | 'admin.orders'
  | 'admin.pos'
  | 'admin.callCenter'
  | 'admin.kitchen'
  | 'admin.drivers'
  | 'admin.printing'
  | 'admin.menu'
  | 'admin.inventory'
  | 'admin.reports'
  | 'admin.phoneSetup'
  | 'admin.categories'
  | 'admin.bulkImport'
  | 'admin.importAddonsRemovables'
  | 'admin.discounts'
  | 'admin.feeAdjustments'
  | 'admin.deliveryFees'
  | 'printing.eyebrow'
  | 'printing.title'
  | 'printing.subtitle'
  | 'printing.stations'
  | 'printing.agents'
  | 'printing.unmapped'
  | 'printing.failed'
  | 'printing.enabledStations'
  | 'printing.enabledStationsReady'
  | 'printing.createStationFirst'
  | 'printing.localAgentOnline'
  | 'printing.agentsOnline'
  | 'printing.pairAgentBeforeService'
  | 'printing.categoryRouting'
  | 'printing.allCategoriesMapped'
  | 'printing.categoriesNeedRoute'
  | 'printing.fallbackStation'
  | 'printing.fallbackAvailable'
  | 'printing.markFallback'
  | 'printing.failedJobs'
  | 'printing.noFailedJobs'
  | 'printing.failedJobsNeedRetry'
  | 'printing.registerReadiness'
  | 'printing.readyTitle'
  | 'printing.attentionTitle'
  | 'printing.readinessHelp'
  | 'printing.fixFirst'
  | 'printing.retryFailed'
  | 'printing.findingPrinters'
  | 'printing.findPrinters'
  | 'printing.generating'
  | 'printing.pairAgent'
  | 'printing.printStations'
  | 'printing.printStationsHelp'
  | 'printing.noStations'
  | 'printing.noStationsHelp'
  | 'printing.routingNeedsStation'
  | 'printing.routingNeedsStationHelp'
  | 'printing.noPairedAgents'
  | 'printing.noPairedAgentsHelp'
  | 'printing.noJobs'
  | 'printing.noJobsHelp'
  | 'printing.stationNameWindowsRequired'
  | 'printing.stationNameIpRequired'
  | 'printing.stationSaved'
  | 'printing.stationSaveFailed'
  | 'printing.stationUpdated'
  | 'printing.stationUpdateFailed'
  | 'printing.routeSaved'
  | 'printing.routeSaveFailed'
  | 'printing.noApiUrl'
  | 'printing.signInRequired'
  | 'printing.pairingCreateFailed'
  | 'printing.pairingCreated'
  | 'printing.pairingErrorWithUrls'
  | 'printing.pairingCopied'
  | 'printing.askingLocalPrinters'
  | 'printing.checkingLocalPrinters'
  | 'printing.foundWindowsPrinters'
  | 'printing.noWindowsPrinters'
  | 'printing.localAgentTimeout'
  | 'printing.localPrintersLoadFailed'
  | 'printing.selectedPrinter'
  | 'printing.retryQueued'
  | 'printing.retryPrintJobsFailed'
  | 'printing.stationName'
  | 'printing.stationNamePlaceholder'
  | 'printing.stationType'
  | 'printing.typeKitchen'
  | 'printing.typeCashier'
  | 'printing.typeBar'
  | 'printing.typeCustom'
  | 'printing.printerConnection'
  | 'printing.networkPrinter'
  | 'printing.windowsUsbPrinter'
  | 'printing.windowsPrinterName'
  | 'printing.windowsPrinterHelp'
  | 'printing.printerIp'
  | 'printing.port'
  | 'printing.enabled'
  | 'printing.fallback'
  | 'printing.saveStation'
  | 'printing.fallbackBadge'
  | 'printing.disabledBadge'
  | 'printing.test'
  | 'printing.disable'
  | 'printing.enable'
  | 'printing.archive'
  | 'printing.categoryRoutingHelp'
  | 'printing.includesSubcategories'
  | 'printing.topLevelCategory'
  | 'printing.unmappedOption'
  | 'printing.localAgentTitle'
  | 'printing.localAgentHelp'
  | 'printing.downloadWindowsAgent'
  | 'printing.generateCode'
  | 'printing.windowsSetupFlow'
  | 'printing.windowsSetupHelp'
  | 'printing.localUsbTitle'
  | 'printing.localUsbHelp'
  | 'printing.finding'
  | 'printing.findLocalPrinters'
  | 'printing.defaultPrinter'
  | 'printing.copyName'
  | 'printing.useForStation'
  | 'printing.pairingCode'
  | 'printing.expiresOnce'
  | 'printing.copyCode'
  | 'printing.lastHeartbeat'
  | 'printing.online'
  | 'printing.offline'
  | 'printing.unifiedAgentPaired'
  | 'printing.recentPrintJobs'
  | 'printing.recentPrintJobsHelp'
  | 'printing.itemsCount'
  | 'printing.retry'
  | 'printing.cancel'
  | 'category.title'
  | 'category.subtitle'
  | 'category.addCategory'
  | 'category.topLevel'
  | 'category.subcategories'
  | 'category.withAddons'
  | 'category.withRemovables'
  | 'category.structure'
  | 'category.structureHelp'
  | 'category.emptyTitle'
  | 'category.emptyHelp'
  | 'category.addonsCount'
  | 'category.removablesCount'
  | 'category.subcategoriesCount'
  | 'category.addSubcategory'
  | 'category.edit'
  | 'category.delete'
  | 'category.saved'
  | 'category.created'
  | 'category.deleted'
  | 'category.imageTypeError'
  | 'category.imageSizeError'
  | 'category.nameRequired'
  | 'category.saveFailed'
  | 'category.deleteFailed'
  | 'category.deleteTitle'
  | 'category.deleteBody'
  | 'category.cancel'
  | 'category.deleting'
  | 'category.deleteConfirm'
  | 'category.editTitle'
  | 'category.addSubcategoryTitle'
  | 'category.addTitle'
  | 'category.categoryName'
  | 'category.subcategoryName'
  | 'category.namePlaceholder'
  | 'category.parentCategory'
  | 'category.noParent'
  | 'category.categoryImage'
  | 'category.previewAlt'
  | 'category.changeImage'
  | 'category.uploadImage'
  | 'category.imageHelp'
  | 'category.defaultAddonsHelp'
  | 'category.defaultRemovablesHelp'
  | 'category.saving'
  | 'category.update'
  | 'category.create'
  | 'addonsImport.title'
  | 'addonsImport.subtitle'
  | 'addonsImport.step.upload'
  | 'addonsImport.step.preview'
  | 'addonsImport.step.importing'
  | 'addonsImport.step.complete'
  | 'addonsImport.uploadTitle'
  | 'addonsImport.uploadSubtitle'
  | 'addonsImport.whatImport'
  | 'addonsImport.type.both'
  | 'addonsImport.type.addons'
  | 'addonsImport.type.removables'
  | 'addonsImport.addonsFile'
  | 'addonsImport.removablesFile'
  | 'addonsImport.addonsHelp'
  | 'addonsImport.removablesHelp'
  | 'addonsImport.dropAddons'
  | 'addonsImport.dropRemovables'
  | 'addonsImport.browse'
  | 'addonsImport.expectedFormat'
  | 'addonsImport.errorsFound'
  | 'addonsImport.preview'
  | 'addonsImport.previewTitle'
  | 'addonsImport.previewSubtitle'
  | 'addonsImport.reset'
  | 'addonsImport.filesLoaded'
  | 'addonsImport.addons'
  | 'addonsImport.removables'
  | 'addonsImport.addonsPreview'
  | 'addonsImport.removablesPreview'
  | 'addonsImport.categoryName'
  | 'addonsImport.addonName'
  | 'addonsImport.removableName'
  | 'addonsImport.price'
  | 'addonsImport.showingAddons'
  | 'addonsImport.showingRemovables'
  | 'addonsImport.import'
  | 'addonsImport.starting'
  | 'addonsImport.progress'
  | 'addonsImport.completeTitle'
  | 'addonsImport.completeBody'
  | 'addonsImport.issuesTitle'
  | 'addonsImport.issuesBody'
  | 'addonsImport.importMore'
  | 'addonsImport.addonsCsvError'
  | 'addonsImport.removablesCsvError'
  | 'addonsImport.addonsParseError'
  | 'addonsImport.removablesParseError'
  | 'addonsImport.importFailed'
  | 'bulkImport.title'
  | 'bulkImport.subtitle'
  | 'bulkImport.step.upload'
  | 'bulkImport.step.preview'
  | 'bulkImport.step.categories'
  | 'bulkImport.step.importing'
  | 'bulkImport.step.complete'
  | 'bulkImport.uploadTitle'
  | 'bulkImport.uploadSubtitle'
  | 'bulkImport.dropTitle'
  | 'bulkImport.dropHelp'
  | 'bulkImport.selectFile'
  | 'bulkImport.csvFormat'
  | 'bulkImport.csvColumns'
  | 'bulkImport.requiredColumns'
  | 'bulkImport.addonsNote'
  | 'bulkImport.errorsFound'
  | 'bulkImport.rowsSkipped'
  | 'bulkImport.previewTitle'
  | 'bulkImport.previewSubtitle'
  | 'bulkImport.reset'
  | 'bulkImport.menuItems'
  | 'bulkImport.categories'
  | 'bulkImport.subcategories'
  | 'bulkImport.newCategories'
  | 'bulkImport.newCategoriesHelp'
  | 'bulkImport.name'
  | 'bulkImport.category'
  | 'bulkImport.subcategory'
  | 'bulkImport.price'
  | 'bulkImport.showingItems'
  | 'bulkImport.setupCategories'
  | 'bulkImport.importItems'
  | 'bulkImport.setupSubcategory'
  | 'bulkImport.setupCategory'
  | 'bulkImport.categoryStep'
  | 'bulkImport.previewAlt'
  | 'bulkImport.underParent'
  | 'bulkImport.itemCount'
  | 'bulkImport.categoryImage'
  | 'bulkImport.changeImage'
  | 'bulkImport.uploadImage'
  | 'bulkImport.imageHelp'
  | 'bulkImport.addonsHelp'
  | 'bulkImport.removablesHelp'
  | 'bulkImport.back'
  | 'bulkImport.nextCategory'
  | 'bulkImport.startImport'
  | 'bulkImport.starting'
  | 'bulkImport.progress'
  | 'bulkImport.completeTitle'
  | 'bulkImport.completeBody'
  | 'bulkImport.issuesTitle'
  | 'bulkImport.issuesBody'
  | 'bulkImport.importMore'
  | 'bulkImport.csvError'
  | 'bulkImport.parseError'
  | 'bulkImport.importFailed'
  | 'notificationSettings.title'
  | 'notificationSettings.subtitle'
  | 'notificationSettings.close'
  | 'notificationSettings.testSuccess'
  | 'notificationSettings.testFailed'
  | 'notificationSettings.alarmTitle'
  | 'notificationSettings.alarmHelp'
  | 'notificationSettings.disableAlarm'
  | 'notificationSettings.enableAlarm'
  | 'notificationSettings.volume'
  | 'notificationSettings.tipTitle'
  | 'notificationSettings.tipBody'
  | 'notificationSettings.testing'
  | 'notificationSettings.testAlarm'
  | 'notificationSettings.done'
  | 'branding.title'
  | 'branding.subtitle'
  | 'branding.currentLogo'
  | 'branding.logoAlt'
  | 'branding.noLogo'
  | 'branding.previewHelp'
  | 'branding.uploadTitle'
  | 'branding.uploadHelp'
  | 'branding.dropTitle'
  | 'branding.fileRules'
  | 'branding.chooseFile'
  | 'branding.uploading'
  | 'branding.tipTitle'
  | 'branding.tipBody'
  | 'branding.imageTypeError'
  | 'branding.imageSizeError'
  | 'branding.uploaded'
  | 'branding.uploadFailed'
  | 'feeSettings.title'
  | 'feeSettings.subtitle'
  | 'feeSettings.loadFailed'
  | 'feeSettings.saved'
  | 'feeSettings.saveFailed'
  | 'feeSettings.save'
  | 'feeSettings.saving'
  | 'feeSettings.reset'
  | 'feeSettings.unsaved'
  | 'feeSettings.perMile'
  | 'feeSettings.deliveryRange'
  | 'feeSettings.freeOver'
  | 'feeSettings.baseRule'
  | 'feeSettings.disabled'
  | 'feeSettings.miles'
  | 'feeSettings.feeConfig'
  | 'feeSettings.feeConfigHelp'
  | 'feeSettings.ratePerMile'
  | 'feeSettings.rateHelp'
  | 'feeSettings.freeThreshold'
  | 'feeSettings.freeThresholdHelp'
  | 'feeSettings.baseEnabled'
  | 'feeSettings.baseEnabledHelp'
  | 'feeSettings.baseFee'
  | 'feeSettings.baseFeeHelp'
  | 'feeSettings.baseMin'
  | 'feeSettings.baseMinHelp'
  | 'feeSettings.baseMax'
  | 'feeSettings.baseMaxHelp'
  | 'feeSettings.noMinimum'
  | 'feeSettings.distanceLimits'
  | 'feeSettings.distanceLimitsHelp'
  | 'feeSettings.minDistance'
  | 'feeSettings.minDistanceHelp'
  | 'feeSettings.maxDistance'
  | 'feeSettings.maxDistanceHelp'
  | 'feeSettings.validationTitle'
  | 'feeSettings.validationHelp'
  | 'feeSettings.readyHelp'
  | 'feeSettings.readyTitle'
  | 'feeSettings.livePreview'
  | 'feeSettings.previewHelp'
  | 'feeSettings.previewOrderTotal'
  | 'feeSettings.previewOrderTotalHelp'
  | 'feeSettings.previewDistance'
  | 'feeSettings.freeDeliveryDetail'
  | 'feeSettings.baseApplies'
  | 'feeSettings.baseSkipped'
  | 'feeSettings.distanceFeeDetail'
  | 'feeSettings.noDeliveryCharge'
  | 'feeSettings.rateNegative'
  | 'feeSettings.baseNegative'
  | 'feeSettings.baseMaxNegative'
  | 'feeSettings.baseMinNegative'
  | 'feeSettings.baseRangeInvalid'
  | 'feeSettings.minNegative'
  | 'feeSettings.maxInvalid'
  | 'feeSettings.freeNegative'
  | 'feeSettings.locationPresets'
  | 'feeSettings.locationPresetsHelp'
  | 'feeSettings.noLocationPresets'
  | 'feeSettings.presetName'
  | 'feeSettings.presetNameAr'
  | 'feeSettings.presetFee'
  | 'feeSettings.sort'
  | 'feeSettings.active'
  | 'feeSettings.inactive'
  | 'feeSettings.addLocationPreset'
  | 'feeAdjustments.title'
  | 'feeAdjustments.subtitle'
  | 'feeAdjustments.visible'
  | 'feeAdjustments.pending'
  | 'feeAdjustments.credits'
  | 'feeAdjustments.net'
  | 'feeAdjustments.notAvailable'
  | 'feeAdjustments.signInRequired'
  | 'feeAdjustments.approvedNotice'
  | 'feeAdjustments.rejectedNotice'
  | 'feeAdjustments.appliedNotice'
  | 'feeAdjustments.actionFailed'
  | 'feeAdjustments.loadErrorTitle'
  | 'feeAdjustments.emptyTitle'
  | 'feeAdjustments.emptyHelp'
  | 'feeAdjustments.anyStatus'
  | 'feeAdjustments.order'
  | 'feeAdjustments.adjustment'
  | 'feeAdjustments.feeImpact'
  | 'feeAdjustments.originalFee'
  | 'feeAdjustments.adjustedFee'
  | 'feeAdjustments.reportedBy'
  | 'feeAdjustments.locationDetails'
  | 'feeAdjustments.lockedDistance'
  | 'feeAdjustments.actualDistance'
  | 'feeAdjustments.difference'
  | 'feeAdjustments.rejectionReason'
  | 'feeAdjustments.appliedOn'
  | 'feeAdjustments.approve'
  | 'feeAdjustments.reject'
  | 'feeAdjustments.apply'
  | 'feeAdjustments.approving'
  | 'feeAdjustments.rejecting'
  | 'feeAdjustments.applying'
  | 'feeAdjustments.cancel'
  | 'feeAdjustments.approveTitle'
  | 'feeAdjustments.approveBody'
  | 'feeAdjustments.approveConfirm'
  | 'feeAdjustments.approveProcessing'
  | 'feeAdjustments.rejectTitle'
  | 'feeAdjustments.rejectBody'
  | 'feeAdjustments.rejectPlaceholder'
  | 'feeAdjustments.rejectConfirm'
  | 'feeAdjustments.rejectProcessing'
  | 'feeAdjustments.applyTitle'
  | 'feeAdjustments.applyBody'
  | 'feeAdjustments.applyConfirm'
  | 'feeAdjustments.applyProcessing'
  | 'feeAdjustments.status.all'
  | 'feeAdjustments.status.pending'
  | 'feeAdjustments.status.approved'
  | 'feeAdjustments.status.rejected'
  | 'feeAdjustments.status.applied'
  | 'feeAdjustments.reason.location_mismatch'
  | 'feeAdjustments.reason.driver_extra_distance'
  | 'feeAdjustments.reason.admin_correction'
  | 'feeAdjustments.reason.customer_complaint'
  | 'admin.staff'
  | 'admin.customers'
  | 'admin.trustScores'
  | 'trustScores.title'
  | 'trustScores.subtitle'
  | 'trustScores.refresh'
  | 'trustScores.loadFailed'
  | 'trustScores.loadErrorTitle'
  | 'trustScores.tryAgain'
  | 'trustScores.customersShown'
  | 'trustScores.avgScore'
  | 'trustScores.critical'
  | 'trustScores.manualApproval'
  | 'trustScores.upfrontPayment'
  | 'trustScores.allCustomers'
  | 'trustScores.belowScore'
  | 'trustScores.sort.score'
  | 'trustScores.sort.mismatches'
  | 'trustScores.sort.complaints'
  | 'trustScores.sort.orders'
  | 'trustScores.emptyTitle'
  | 'trustScores.emptyHelp'
  | 'trustScores.customer'
  | 'trustScores.trustScore'
  | 'trustScores.mismatches'
  | 'trustScores.complaints'
  | 'trustScores.totalOrders'
  | 'trustScores.flags'
  | 'trustScores.restrictions'
  | 'trustScores.none'
  | 'trustScores.restriction.manualApproval'
  | 'trustScores.restriction.upfrontPayment'
  | 'trustScores.restriction.maxDistance'
  | 'trustScores.lastUpdated'
  | 'trustScores.infoTitle'
  | 'trustScores.infoRange'
  | 'trustScores.infoSignals'
  | 'trustScores.infoRestrictions'
  | 'trustScores.infoReview'
  | 'admin.branding'
  | 'admin.reviews'
  | 'reviews.title'
  | 'reviews.subtitle'
  | 'reviews.refresh'
  | 'reviews.loadFailed'
  | 'reviews.loadErrorTitle'
  | 'reviews.tryAgain'
  | 'reviews.deleted'
  | 'reviews.deleteFailed'
  | 'reviews.totalReviews'
  | 'reviews.averageRating'
  | 'reviews.deliveryRating'
  | 'reviews.verified'
  | 'reviews.lowRatings'
  | 'reviews.ratingDistribution'
  | 'reviews.photoReviews'
  | 'reviews.photoReviewsHelp'
  | 'reviews.filter.all'
  | 'reviews.filter.low'
  | 'reviews.filter.high'
  | 'reviews.filter.photos'
  | 'reviews.filter.unverified'
  | 'reviews.sort.newest'
  | 'reviews.sort.lowest'
  | 'reviews.sort.highest'
  | 'reviews.sort.helpful'
  | 'reviews.emptyTitle'
  | 'reviews.emptyHelp'
  | 'reviews.verifiedBadge'
  | 'reviews.order'
  | 'reviews.overallRating'
  | 'reviews.menuItemReviews'
  | 'reviews.photos'
  | 'reviews.helpful'
  | 'reviews.delete'
  | 'reviews.deleteTitle'
  | 'reviews.deleteBody'
  | 'reviews.cancel'
  | 'reviews.deleting'
  | 'reviews.deleteConfirm'
  | 'stats.activeOrders'
  | 'stats.preparing'
  | 'stats.outForDelivery'
  | 'stats.completedToday'
  | 'manager.available'
  | 'manager.unavailable'
  | 'manager.cancel'
  | 'manager.delete'
  | 'manager.deleteAddonTitle'
  | 'manager.deleteAddonBody'
  | 'manager.deleteRemovableTitle'
  | 'manager.deleteRemovableBody'
  | 'callCenter.title'
  | 'callCenter.subtitle'
  | 'callCenter.liveCalls'
  | 'callCenter.waiting'
  | 'callCenter.active'
  | 'callCenter.cancelDraft'
  | 'callCenter.keepDraft'
  | 'callCenter.cancelDraftBody'
  | 'callCenter.cancelling'
  | 'callCenter.workstation'
  | 'callCenter.workstationHelp'
  | 'callCenter.extensionPlaceholder'
  | 'callCenter.save'
  | 'callCenter.allExtensions'
  | 'callCenter.filteringExtension'
  | 'callCenter.extensionShort'
  | 'callCenter.incomingCalls'
  | 'callCenter.live'
  | 'callCenter.recent'
  | 'callCenter.manualCallerFallback'
  | 'callCenter.manualExtension'
  | 'callCenter.manualCallerNumber'
  | 'callCenter.add'
  | 'callCenter.noLiveShowRecent'
  | 'callCenter.noCallsLive'
  | 'callCenter.noCallsRecent'
  | 'callCenter.callerProfile'
  | 'callCenter.noCaller'
  | 'callCenter.selectOrEnterPhone'
  | 'callCenter.remembered'
  | 'callCenter.startDraft'
  | 'callCenter.failedMessageAlert'
  | 'callCenter.duplicateOrderAlert'
  | 'callCenter.duplicateDraftAlert'
  | 'callCenter.callerRiskSummary'
  | 'callCenter.openDraft'
  | 'callCenter.openOrder'
  | 'callCenter.sendLocationOnly'
  | 'callCenter.duplicateDraftBlocked'
  | 'callCenter.stage.none'
  | 'callCenter.stage.draft'
  | 'callCenter.stage.location_sent'
  | 'callCenter.stage.waiting_confirmation'
  | 'callCenter.stage.submitted'
  | 'callCenter.stage.cancelled'
  | 'callCenter.stage.failed_message'
  | 'callCenter.draftWorkspace'
  | 'callCenter.startDraftHelp'
  | 'callCenter.draftReadOnly'
  | 'callCenter.sendWhatsapp'
  | 'callCenter.sendSms'
  | 'callCenter.copyLink'
  | 'callCenter.manualFinish'
  | 'callCenter.placeOrderNow'
  | 'callCenter.sendLocationWhatsapp'
  | 'callCenter.holdForConfirmation'
  | 'callCenter.hold'
  | 'callCenter.customerName'
  | 'callCenter.addressNotes'
  | 'callCenter.orderNotes'
  | 'callCenter.deliveryFee'
  | 'callCenter.deliveryArea'
  | 'callCenter.fulfillment'
  | 'callCenter.pickup'
  | 'callCenter.deliveryOrder'
  | 'callCenter.paymentIntent'
  | 'callCenter.cash'
  | 'callCenter.card'
  | 'callCenter.pickupClearsDelivery'
  | 'callCenter.keepDelivery'
  | 'callCenter.switchToPickup'
  | 'callCenter.deliveryAreaName'
  | 'callCenter.deliveryAreaPlaceholder'
  | 'callCenter.customDelivery'
  | 'callCenter.locationNeedsReview'
  | 'callCenter.needsFollowUp'
  | 'callCenter.waitingTooLong'
  | 'callCenter.locationLink'
  | 'callCenter.locationLinkSent'
  | 'callCenter.requestExactPin'
  | 'callCenter.whatsappConsent'
  | 'callCenter.saveDetails'
  | 'callCenter.details'
  | 'callCenter.total'
  | 'callCenter.subtotal'
  | 'callCenter.delivery'
  | 'callCenter.whatsapp'
  | 'callCenter.sms'
  | 'callCenter.noDraft'
  | 'callCenter.whatsappReplies'
  | 'callCenter.whatsappRepliesHelp'
  | 'callCenter.useSmsFallback'
  | 'callCenter.noWhatsappMessages'
  | 'callCenter.replyPlaceholder'
  | 'callCenter.send'
  | 'callCenter.searchMenu'
  | 'callCenter.menuBuilder'
  | 'callCenter.menuBuilderHelp'
  | 'callCenter.allMenu'
  | 'callCenter.quickPicks'
  | 'callCenter.fastPicks'
  | 'callCenter.fastPicksHelp'
  | 'callCenter.popular'
  | 'callCenter.noMenuMatches'
  | 'callCenter.unavailable'
  | 'callCenter.variants'
  | 'callCenter.singleItem'
  | 'callCenter.customOptions'
  | 'callCenter.quickAdd'
  | 'callCenter.customize'
  | 'callCenter.itemUnavailable'
  | 'callCenter.chooseVariant'
  | 'callCenter.addons'
  | 'callCenter.removals'
  | 'callCenter.quantity'
  | 'callCenter.no'
  | 'callCenter.addToDraft'
  | 'callCenter.removeLine'
  | 'callCenter.draftCart'
  | 'callCenter.noItems'
  | 'callCenter.connectedOrders'
  | 'callCenter.noPreviousOrders'
  | 'callCenter.drafts'
  | 'callCenter.noDrafts'
  | 'callCenter.phoneDraft'
  | 'callCenter.itemsCount'
  | 'callCenter.finish'
  | 'callCenter.open'
  | 'callCenter.repeat'
  | 'callCenter.orderActive'
  | 'callCenter.orderRecent'
  | 'callCenter.manualCallCreated'
  | 'callCenter.manualCallError'
  | 'callCenter.extensionSaved'
  | 'callCenter.allExtensionsSaved'
  | 'callCenter.enterCallerPhone'
  | 'callCenter.draftStarted'
  | 'callCenter.draftFirst'
  | 'callCenter.detailsSaved'
  | 'callCenter.saveDraftError'
  | 'callCenter.whatsappConsentRequired'
  | 'callCenter.linkCopied'
  | 'callCenter.confirmationSent'
  | 'callCenter.sendChannelError'
  | 'callCenter.replyQueued'
  | 'callCenter.replyError'
  | 'callCenter.manualSubmitted'
  | 'callCenter.manualSubmitError'
  | 'callCenter.draftCancelled'
  | 'callCenter.cancelDraftError'
  | 'callCenter.loadedOrder'
  | 'customer.title'
  | 'customer.subtitle'
  | 'customer.total'
  | 'customer.verified'
  | 'customer.restricted'
  | 'customer.blocked'
  | 'customer.searchPlaceholder'
  | 'customer.shown'
  | 'customer.clear'
  | 'customer.selectTitle'
  | 'customer.selectHelp'
  | 'customer.loadFailed'
  | 'customer.loadFailedDetail'
  | 'customer.tryAgain'
  | 'customer.notFound'
  | 'customer.notFoundHelp'
  | 'customer.blacklisted'
  | 'customer.review'
  | 'customer.phoneVerified'
  | 'customer.phoneUnverified'
  | 'customer.lastOrder'
  | 'customer.removeBlacklist'
  | 'customer.blacklist'
  | 'customer.blacklistWarning'
  | 'customer.confirmBlacklistTitle'
  | 'customer.confirmBlacklistBody'
  | 'customer.confirmBlacklistAction'
  | 'customer.confirmUnblacklistTitle'
  | 'customer.confirmUnblacklistBody'
  | 'customer.confirmUnblacklistAction'
  | 'customer.keepStatus'
  | 'customer.updatingStatus'
  | 'customer.totalOrders'
  | 'customer.activeOrders'
  | 'customer.cancelRate'
  | 'customer.totalSpent'
  | 'customer.averageOrderValue'
  | 'customer.completedOrders'
  | 'customer.orderMix'
  | 'customer.orderMixValues'
  | 'customer.orderHistory'
  | 'customer.orderHistoryHelp'
  | 'customer.ordersCount'
  | 'customer.noOrders'
  | 'customer.orderNumber'
  | 'customer.date'
  | 'customer.status'
  | 'customer.type'
  | 'customer.totalAmount'
  | 'orders.liveBoard'
  | 'orders.title'
  | 'orders.subtitle'
  | 'orders.active'
  | 'orders.scheduled'
  | 'orders.delivery'
  | 'orders.assignedDrivers'
  | 'orders.completed'
  | 'orders.searchPlaceholder'
  | 'orders.compact'
  | 'orders.comfortable'
  | 'orders.hideClosed'
  | 'orders.showClosed'
  | 'orders.activeSection'
  | 'orders.rejectedSection'
  | 'orders.cancelledSection'
  | 'orders.completedSection'
  | 'orders.notFound'
  | 'orders.notFoundHelp'
  | 'orders.noLiveMatches'
  | 'orders.closedHidden'
  | 'orders.showClosedOrders'
  | 'orders.filter'
  | 'orders.all'
  | 'orders.pickup'
  | 'pos.searchPlaceholder'
  | 'pos.keyboard'
  | 'pos.repeat'
  | 'pos.allMenu'
  | 'pos.itemsCount'
  | 'pos.loadingMenu'
  | 'pos.noMenuMatches'
  | 'pos.currentOrder'
  | 'pos.lineItems'
  | 'pos.parked'
  | 'pos.clear'
  | 'pos.startCounterOrder'
  | 'pos.tapMenuTiles'
  | 'pos.available'
  | 'pos.unavailable'
  | 'pos.variants'
  | 'pos.singleItem'
  | 'pos.add'
  | 'pos.searchKeyboardPlaceholder'
  | 'pos.customerNamePlaceholder'
  | 'pos.customerPhonePlaceholder'
  | 'pos.lookup'
  | 'pos.discountCodePlaceholder'
  | 'pos.apply'
  | 'pos.subtotal'
  | 'pos.deliveryFee'
  | 'pos.discount'
  | 'pos.total'
  | 'pos.resumedParkedOrder'
  | 'pos.payNowOrParkAgain'
  | 'pos.settlingMobileOrder'
  | 'pos.editItemsNoReprint'
  | 'pos.parkOrder'
  | 'pos.cannotParkSettlement'
  | 'pos.updateParkedOrder'
  | 'pos.cash'
  | 'pos.card'
  | 'pos.closeShiftTitle'
  | 'pos.closeoutReport'
  | 'pos.expected'
  | 'pos.counted'
  | 'pos.overShort'
  | 'pos.starting'
  | 'pos.cashSales'
  | 'pos.cardSales'
  | 'pos.cashIn'
  | 'pos.cashOut'
  | 'pos.voids'
  | 'pos.refunds'
  | 'pos.countedCashPlaceholder'
  | 'pos.closeoutNotesPlaceholder'
  | 'pos.printCloseoutReport'
  | 'pos.closeShift'
  | 'pos.cashPaymentTitle'
  | 'pos.amountDue'
  | 'pos.cashReceived'
  | 'pos.changeDue'
  | 'pos.exact'
  | 'pos.back'
  | 'pos.completeCashPayment'
  | 'pos.savedForLater'
  | 'pos.lineItemsStayEditable'
  | 'pos.nameOrLabel'
  | 'pos.parkLabelPlaceholder'
  | 'pos.internalNoteOptional'
  | 'pos.parkNotesPlaceholder'
  | 'pos.writeKeyboardPlaceholder'
  | 'pos.saveParkedChanges'
  | 'pos.parkThisOrder'
  | 'pos.parkedOrdersTitle'
  | 'pos.parkedOrdersHelp'
  | 'pos.parkedSearchPlaceholder'
  | 'pos.noParkedOrders'
  | 'pos.open'
  | 'pos.edit'
  | 'pos.payCash'
  | 'pos.payCard'
  | 'pos.cancel'
  | 'pos.pendingReplaceTitle'
  | 'pos.pendingCancelTitle'
  | 'pos.pendingReplaceBody'
  | 'pos.pendingCancelBody'
  | 'pos.keepCurrentCart'
  | 'pos.keepParkedOrder'
  | 'pos.working'
  | 'pos.replaceCart'
  | 'pos.cancelParkedOrder'
  | 'pos.cardTerminalPaymentTitle'
  | 'pos.amountToApprove'
  | 'pos.cardTerminalHelp'
  | 'pos.cardTerminalApproved'
  | 'pos.marketplaceOrderTitle'
  | 'pos.marketplaceClipReceipt'
  | 'pos.marketplaceHelp'
  | 'pos.orderTotal'
  | 'pos.ticketNumber'
  | 'pos.marketplaceTicketPlaceholder'
  | 'pos.ticketNumberKeyboard'
  | 'pos.printAndSendToKitchen'
  | 'pos.managerApproval'
  | 'pos.drawerApprovalHelp'
  | 'pos.openDrawer'
  | 'pos.orderComplete'
  | 'pos.reprintReceipt'
  | 'pos.retryFailedPrints'
  | 'pos.viewOrder'
  | 'pos.newOrder'
  | 'pos.amountPlaceholder'
  | 'pos.reasonPlaceholder'
  | 'pos.saveMovement'
  | 'pos.voidTransaction'
  | 'pos.voidApproval'
  | 'pos.voidTransactionButton'
  | 'pos.refund'
  | 'pos.refunded'
  | 'pos.refundTransaction'
  | 'pos.refundApproval'
  | 'pos.voidHelp'
  | 'pos.refundHelp'
  | 'pos.adjustmentReason'
  | 'pos.adminPassword'
  | 'pos.adminPasswordPlaceholder'
  | 'pos.approveWithGoogle'
  | 'pos.googleApprovalHelp'
  | 'pos.secureManagerApprovalHelp'
  | 'pos.approveVoid'
  | 'pos.approveRefund'
  | 'pos.managerApprovalUnavailable'
  | 'pos.voidSuccess'
  | 'pos.refundSuccess'
  | 'pos.adjustmentFailed'
  | 'pos.cashControlsRequireOnline'
  | 'pos.manualDrawerAccess'
  | 'pos.drawerReasonPlaceholder'
  | 'pos.approveAndOpenDrawer'
  | 'pos.drawerRequestQueued'
  | 'pos.drawerRequestFailed'
  | 'pos.cashInHelp'
  | 'pos.cashOutHelp'
  | 'pos.cashMovementApprovalHelp'
  | 'pos.approveAndSaveMovement'
  | 'pos.cashMovementSaved'
  | 'pos.cashMovementSavedNoDrawer'
  | 'pos.cashMovementFailed'
  | 'pos.fastActions'
  | 'pos.fastActionsHelp'
  | 'pos.repeatLastOrder'
  | 'pos.noFastActions'
  | 'pos.recent'
  | 'pos.popular'
  | 'pos.quickPicks'
  | 'pos.undo'
  | 'pos.menuFallback'
  | 'pos.printJob'
  | 'pos.itemReady'
  | 'pos.chooseVariant'
  | 'pos.addons'
  | 'pos.remove'
  | 'pos.noModifier'
  | 'pos.kitchenNotePlaceholder'
  | 'pos.kitchenNoteKeyboard'
  | 'pos.quantity'
  | 'pos.addToCart'
  | 'pos.counterPos'
  | 'pos.openCashRegister'
  | 'pos.openRegisterHelp'
  | 'pos.startingCashInDrawer'
  | 'pos.openPosShift'
  | 'pos.registerConnection'
  | 'pos.onlineReady'
  | 'pos.offlineCannotOpenShift'
  | 'pos.noStartingCash'
  | 'pos.openShiftRequiresOnline'
  | 'pos.invalidStartingCash'
  | 'pos.shiftOpenedSuccess'
  | 'pos.existingShiftJoined'
  | 'pos.duplicateShiftsDetected'
  | 'pos.openShiftFailed'
  | 'pos.cashRegister'
  | 'pos.menuTiles'
  | 'pos.backToAdmin'
  | 'pos.registerTools'
  | 'pos.printerReady'
  | 'pos.printerOffline'
  | 'pos.parkedLabel'
  | 'pos.orders'
  | 'pos.orderDetails'
  | 'pos.online'
  | 'pos.offlinePos'
  | 'pos.localAgent'
  | 'pos.agentOff'
  | 'pos.cloudAgent'
  | 'pos.cloudOff'
  | 'pos.pending'
  | 'pos.expectedDrawer'
  | 'pos.availableMenu'
  | 'pos.offlineQueue'
  | 'pos.sync'
  | 'pos.drawer'
  | 'pos.closeShiftAction'
  | 'pos.recentPos'
  | 'pos.noPosSalesYet'
  | 'pos.pendingOrder'
  | 'pos.void'
  | 'pos.park'
  | 'pos.space'
  | 'pos.hide'
  | 'pos.printDrawerStatus'
  | 'pos.waitingPrintJobs'
  | 'pos.offlinePrintStatus'
  | 'pos.printKitchen'
  | 'pos.printReceipt'
  | 'pos.printMarketplaceReceipt'
  | 'pos.printSalesReport'
  | 'pos.printDrawer'
  | 'pos.printTest'
  | 'pos.printCloseout'
  | 'pos.cardReceiptCustomer'
  | 'pos.cardReceiptMerchant'
  | 'pos.cardTwoReceiptsNow'
  | 'pos.cardTwoReceiptsKitchenNow'
  | 'pos.cardTwoReceiptsInstant'
  | 'pos.cardTwoReceiptsKitchenInstant'
  | 'pos.cardTwoReceiptsQueued'
  | 'pos.cardTwoReceiptsKitchenQueued'
  | 'staff.title'
  | 'staff.subtitle'
  | 'staff.createCallCenterDesk'
  | 'staff.createDriver'
  | 'staff.totalStaff'
  | 'staff.admins'
  | 'staff.drivers'
  | 'staff.callCenterDesks'
  | 'staff.searchPlaceholder'
  | 'staff.allUsers'
  | 'staff.noStaff'
  | 'staff.noStaffHelp'
  | 'staff.email'
  | 'staff.phone'
  | 'staff.noPhone'
  | 'staff.editStaff'
  | 'staff.loadFailed'
  | 'staff.loadFailedDetail'
  | 'staff.tryAgain'
  | 'staff.createTitle'
  | 'staff.editTitle'
  | 'staff.fullName'
  | 'staff.fullNamePlaceholder'
  | 'staff.emailPlaceholder'
  | 'staff.password'
  | 'staff.passwordPlaceholder'
  | 'staff.role'
  | 'staff.deliveryDriver'
  | 'staff.callCenterDesk'
  | 'staff.callCenterRoleHelp'
  | 'staff.driverRoleHelp'
  | 'staff.adminConfigOnly'
  | 'staff.cancel'
  | 'staff.creating'
  | 'staff.createAccount'
  | 'staff.saving'
  | 'staff.saveChanges'
  | 'staff.emailLocked'
  | 'staff.phoneNumber'
  | 'staff.phonePlaceholder'
  | 'staff.roleLocked'
  | 'staff.invalidPhone'
  | 'phoneSetup.eyebrow'
  | 'phoneSetup.title'
  | 'phoneSetup.subtitle'
  | 'phoneSetup.onlineAgents'
  | 'phoneSetup.phoneReady'
  | 'phoneSetup.addWorkstation'
  | 'phoneSetup.addWorkstationHelp'
  | 'phoneSetup.downloadWindows'
  | 'phoneSetup.singlePhone'
  | 'phoneSetup.singlePhoneHelp'
  | 'phoneSetup.callCenter'
  | 'phoneSetup.callCenterHelp'
  | 'phoneSetup.workstationName'
  | 'phoneSetup.workstationPlaceholder'
  | 'phoneSetup.expectedExtension'
  | 'phoneSetup.optional'
  | 'phoneSetup.extensionPlaceholder'
  | 'phoneSetup.generating'
  | 'phoneSetup.generateCode'
  | 'phoneSetup.enterInAgent'
  | 'phoneSetup.expires'
  | 'phoneSetup.copyCode'
  | 'phoneSetup.codeCreated'
  | 'phoneSetup.codeCopied'
  | 'phoneSetup.createCodeError'
  | 'phoneSetup.revokeConfirm'
  | 'phoneSetup.revokeTitle'
  | 'phoneSetup.revokeBody'
  | 'phoneSetup.cancel'
  | 'phoneSetup.revoking'
  | 'phoneSetup.revoked'
  | 'phoneSetup.revokeError'
  | 'phoneSetup.afterPairing'
  | 'phoneSetup.afterPairingHelp'
  | 'phoneSetup.adminDesk'
  | 'phoneSetup.adminDeskHelp'
  | 'phoneSetup.workstationHealth'
  | 'phoneSetup.workstationHealthHelp'
  | 'phoneSetup.extensionShort'
  | 'phoneSetup.notSelected'
  | 'phoneSetup.driverPending'
  | 'phoneSetup.agentOnline'
  | 'phoneSetup.agentOffline'
  | 'phoneSetup.phoneConnected'
  | 'phoneSetup.phoneNeedsSetup'
  | 'phoneSetup.version'
  | 'phoneSetup.lastHeartbeat'
  | 'phoneSetup.lastPhoneEvent'
  | 'phoneSetup.mode'
  | 'phoneSetup.callCenterDesk'
  | 'phoneSetup.revoke'
  | 'phoneSetup.emptyTitle'
  | 'phoneSetup.emptyHelp'
  | 'phoneSetup.guideReadonlyTitle'
  | 'phoneSetup.guideReadonlyText'
  | 'phoneSetup.guideMissingTitle'
  | 'phoneSetup.guideMissingText'
  | 'phoneSetup.guideFallbackTitle'
  | 'phoneSetup.guideFallbackText'
  | 'phoneSetup.never'
  | 'phoneSetup.unknown'
  | 'discount.eyebrow'
  | 'discount.title'
  | 'discount.subtitle'
  | 'discount.create'
  | 'discount.totalCodes'
  | 'discount.activeCodes'
  | 'discount.pausedCodes'
  | 'discount.expiringCodes'
  | 'discount.searchPlaceholder'
  | 'discount.all'
  | 'discount.active'
  | 'discount.paused'
  | 'discount.noCodes'
  | 'discount.noCodesHelp'
  | 'discount.noMatches'
  | 'discount.noMatchesHelp'
  | 'discount.dismiss'
  | 'discount.discount'
  | 'discount.minOrder'
  | 'discount.usage'
  | 'discount.expires'
  | 'discount.status'
  | 'discount.pause'
  | 'discount.activate'
  | 'discount.inactive'
  | 'discount.createTitle'
  | 'discount.code'
  | 'discount.description'
  | 'discount.type'
  | 'discount.value'
  | 'discount.percentage'
  | 'discount.fixed'
  | 'discount.minOrderOptional'
  | 'discount.maxUsesOptional'
  | 'discount.expiryOptional'
  | 'discount.activeOnCreate'
  | 'discount.cancel'
  | 'discount.creating'
  | 'discount.createButton'
  | 'discount.loadFailed'
  | 'discount.requiredError'
  | 'discount.percentError'
  | 'discount.createFailed'
  | 'discount.created'
  | 'discount.statusUpdated'
  | 'discount.statusFailed';

const translations: Record<AdminLanguage, Record<TranslationKey, string>> = {
  en: {
    'language.english': 'English',
    'language.arabic': 'العربية',
    'language.switchToEnglish': 'Switch admin UI to English',
    'language.switchToArabic': 'Switch admin UI to Arabic',
    'nav.logout': 'Logout',
    'nav.dashboard': 'Dashboard',
    'nav.role.admin': 'Admin',
    'nav.role.driver': 'Driver',
    'nav.role.call_center': 'Call Center',
    'admin.restaurantOperations': 'Restaurant operations',
    'admin.dashboard': 'Admin Dashboard',
    'admin.enableOrderAlarm': 'Enable Order Alarm',
    'admin.notificationsOn': 'Notifications On',
    'admin.notificationsOff': 'Notifications Off',
    'admin.notificationSettings': 'Notification Settings',
    'admin.dismiss': 'Dismiss',
    'admin.hideTools': 'Hide Tools',
    'admin.tools': 'Tools',
    'admin.opsCommand': 'Ops Command',
    'admin.setupWizard': 'Setup Wizard',
    'admin.menuWizard': 'Menu Wizard',
    'admin.orders': 'Orders',
    'admin.pos': 'POS',
    'admin.callCenter': 'Call Center',
    'admin.kitchen': 'Kitchen',
    'admin.drivers': 'Drivers',
    'admin.printing': 'Printing',
    'admin.inventory': 'Inventory',
    'admin.menu': 'Menu',
    'admin.reports': 'Reports',
    'admin.phoneSetup': 'Phone Setup',
    'admin.categories': 'Categories',
    'admin.bulkImport': 'Bulk Import',
    'admin.importAddonsRemovables': 'Import Addons & Removables',
    'admin.discounts': 'Discounts',
    'admin.feeAdjustments': 'Fee Adjustments',
    'admin.deliveryFees': 'Delivery Fees',
    'category.title': 'Menu categories',
    'category.subtitle': 'Organize the customer menu, category imagery, subcategories, and inherited add-ons/removables.',
    'category.addCategory': 'Add category',
    'category.topLevel': 'Top-level',
    'category.subcategories': 'Subcategories',
    'category.withAddons': 'With add-ons',
    'category.withRemovables': 'With removables',
    'category.structure': 'Category structure',
    'category.structureHelp': 'Keep the menu hierarchy clear so customers and staff can find items quickly.',
    'category.emptyTitle': 'No menu categories yet',
    'category.emptyHelp': 'Create the first category to start organizing the menu.',
    'category.addonsCount': '{{count}} add-ons',
    'category.removablesCount': '{{count}} removables',
    'category.subcategoriesCount': '{{count}} subcategories',
    'category.addSubcategory': 'Add subcategory',
    'category.edit': 'Edit category',
    'category.delete': 'Delete category',
    'category.saved': 'Category updated.',
    'category.created': 'Category created.',
    'category.deleted': 'Category deleted.',
    'category.imageTypeError': 'Only image files are allowed.',
    'category.imageSizeError': 'Image size exceeds the 10MB limit.',
    'category.nameRequired': 'Category name is required.',
    'category.saveFailed': 'Failed to save category.',
    'category.deleteFailed': 'Failed to delete category.',
    'category.deleteTitle': 'Delete category',
    'category.deleteBody': 'Delete "{{name}}"? {{count}} subcategories will also be affected and menu items may be unassigned.',
    'category.cancel': 'Cancel',
    'category.deleting': 'Deleting...',
    'category.deleteConfirm': 'Delete category',
    'category.editTitle': 'Edit category',
    'category.addSubcategoryTitle': 'Add subcategory',
    'category.addTitle': 'Add category',
    'category.categoryName': 'Category name',
    'category.subcategoryName': 'Subcategory name',
    'category.namePlaceholder': 'e.g., Shawarma, Burgers',
    'category.parentCategory': 'Parent category',
    'category.noParent': 'None (top-level category)',
    'category.categoryImage': 'Category image',
    'category.previewAlt': 'Category image preview',
    'category.changeImage': 'Change image',
    'category.uploadImage': 'Upload image',
    'category.imageHelp': 'This image is used for items in this category that do not have their own image.',
    'category.defaultAddonsHelp': 'These add-ons will be available for all items in this category.',
    'category.defaultRemovablesHelp': 'These removable items will be available for all items in this category.',
    'category.saving': 'Saving...',
    'category.update': 'Update',
    'category.create': 'Create',
    'addonsImport.title': 'Add-ons and removables import',
    'addonsImport.subtitle': 'Load category defaults from CSV files so menu items inherit the right add-ons and removable ingredients without manual entry.',
    'addonsImport.step.upload': 'Upload',
    'addonsImport.step.preview': 'Preview',
    'addonsImport.step.importing': 'Importing',
    'addonsImport.step.complete': 'Complete',
    'addonsImport.uploadTitle': 'Upload category default files',
    'addonsImport.uploadSubtitle': 'Choose whether you are importing add-ons, removables, or both. Files are parsed before anything is saved.',
    'addonsImport.whatImport': 'What would you like to import?',
    'addonsImport.type.both': 'Both add-ons and removables',
    'addonsImport.type.addons': 'Add-ons only',
    'addonsImport.type.removables': 'Removables only',
    'addonsImport.addonsFile': 'Add-ons CSV',
    'addonsImport.removablesFile': 'Removables CSV',
    'addonsImport.addonsHelp': 'Category, add-on name, and price.',
    'addonsImport.removablesHelp': 'Category and removable ingredient name.',
    'addonsImport.dropAddons': 'Drop add-ons CSV here',
    'addonsImport.dropRemovables': 'Drop removables CSV here',
    'addonsImport.browse': 'Browse CSV file',
    'addonsImport.expectedFormat': 'Expected format',
    'addonsImport.errorsFound': 'Errors found',
    'addonsImport.preview': 'Preview import',
    'addonsImport.previewTitle': 'Review before importing',
    'addonsImport.previewSubtitle': '{{addons}} add-ons and {{removables}} removables are ready to import.',
    'addonsImport.reset': 'Reset',
    'addonsImport.filesLoaded': 'Files loaded',
    'addonsImport.addons': 'Add-ons',
    'addonsImport.removables': 'Removables',
    'addonsImport.addonsPreview': 'Add-ons preview',
    'addonsImport.removablesPreview': 'Removables preview',
    'addonsImport.categoryName': 'Category',
    'addonsImport.addonName': 'Add-on',
    'addonsImport.removableName': 'Removable',
    'addonsImport.price': 'Price',
    'addonsImport.showingAddons': 'Showing {{shown}} of {{total}} add-ons',
    'addonsImport.showingRemovables': 'Showing {{shown}} of {{total}} removables',
    'addonsImport.import': 'Import now',
    'addonsImport.starting': 'Starting import...',
    'addonsImport.progress': '{{current}} of {{total}} processed',
    'addonsImport.completeTitle': 'Import complete',
    'addonsImport.completeBody': 'Added {{addons}} add-ons and {{removables}} removables to {{categories}} categories. Items in these categories will inherit the new defaults.',
    'addonsImport.issuesTitle': 'Import had issues',
    'addonsImport.issuesBody': 'Added {{addons}} add-ons and {{removables}} removables to {{categories}} categories, but some rows need attention.',
    'addonsImport.importMore': 'Import more',
    'addonsImport.addonsCsvError': 'Please upload a CSV file for add-ons.',
    'addonsImport.removablesCsvError': 'Please upload a CSV file for removables.',
    'addonsImport.addonsParseError': 'Failed to parse the add-ons CSV file.',
    'addonsImport.removablesParseError': 'Failed to parse the removables CSV file.',
    'addonsImport.importFailed': 'Import failed.',
    'bulkImport.title': 'Bulk menu import',
    'bulkImport.subtitle': 'Create menu items from a CSV file, review detected categories, and prepare category defaults before importing.',
    'bulkImport.step.upload': 'Upload',
    'bulkImport.step.preview': 'Preview',
    'bulkImport.step.categories': 'Categories',
    'bulkImport.step.importing': 'Importing',
    'bulkImport.step.complete': 'Complete',
    'bulkImport.uploadTitle': 'Upload menu CSV',
    'bulkImport.uploadSubtitle': 'Import multiple menu items at once. The file is parsed first so you can review items and categories before saving.',
    'bulkImport.dropTitle': 'Drop menu CSV here',
    'bulkImport.dropHelp': 'Use CSV format with menu item name, price, category, and optional subcategory.',
    'bulkImport.selectFile': 'Select CSV file',
    'bulkImport.csvFormat': 'CSV format',
    'bulkImport.csvColumns': 'Your CSV should include these columns:',
    'bulkImport.requiredColumns': 'Required columns: name, price, category. Optional: description, subcategory.',
    'bulkImport.addonsNote': 'After importing menu items, use Add-ons & Removables Import to apply category defaults in bulk.',
    'bulkImport.errorsFound': 'Errors found',
    'bulkImport.rowsSkipped': '{{count}} rows had issues and were skipped',
    'bulkImport.previewTitle': 'Preview menu import',
    'bulkImport.previewSubtitle': '{{count}} items found in {{file}}.',
    'bulkImport.reset': 'Reset',
    'bulkImport.menuItems': 'Menu items',
    'bulkImport.categories': 'Categories',
    'bulkImport.subcategories': 'Subcategories',
    'bulkImport.newCategories': '{{count}} new categories will be created',
    'bulkImport.newCategoriesHelp': 'You can set images and default add-ons/removables for each new category before importing.',
    'bulkImport.name': 'Name',
    'bulkImport.category': 'Category',
    'bulkImport.subcategory': 'Subcategory',
    'bulkImport.price': 'Price',
    'bulkImport.showingItems': 'Showing {{shown}} of {{total}} items',
    'bulkImport.setupCategories': 'Set up categories',
    'bulkImport.importItems': 'Import items',
    'bulkImport.setupSubcategory': 'Set up subcategory',
    'bulkImport.setupCategory': 'Set up category',
    'bulkImport.categoryStep': 'Step {{current}} of {{total}}',
    'bulkImport.previewAlt': 'Category image preview',
    'bulkImport.underParent': 'Under: {{parent}}',
    'bulkImport.itemCount': '{{count}} items',
    'bulkImport.categoryImage': 'Category image',
    'bulkImport.changeImage': 'Change image',
    'bulkImport.uploadImage': 'Upload image',
    'bulkImport.imageHelp': 'This image will be used for all items in this category.',
    'bulkImport.addonsHelp': 'These add-ons will be available for all {{count}} items.',
    'bulkImport.removablesHelp': 'Customers can remove these from any of the {{count}} items.',
    'bulkImport.back': 'Back',
    'bulkImport.nextCategory': 'Next category',
    'bulkImport.startImport': 'Start import',
    'bulkImport.starting': 'Starting import...',
    'bulkImport.progress': '{{current}} of {{total}} processed',
    'bulkImport.completeTitle': 'Import complete',
    'bulkImport.completeBody': 'Imported {{items}} menu items and created {{categories}} categories.',
    'bulkImport.issuesTitle': 'Import had issues',
    'bulkImport.issuesBody': 'Imported {{items}} items, but some rows need attention.',
    'bulkImport.importMore': 'Import more',
    'bulkImport.csvError': 'Please upload a CSV file.',
    'bulkImport.parseError': 'Failed to parse the CSV file.',
    'bulkImport.importFailed': 'Import failed.',
    'notificationSettings.title': 'Order alarm',
    'notificationSettings.subtitle': 'Control the loud order alarm used by the admin dashboard when new orders arrive.',
    'notificationSettings.close': 'Close notification settings',
    'notificationSettings.testSuccess': 'Test order alarm played. If you did not hear it, check device volume and browser audio permissions.',
    'notificationSettings.testFailed': 'Could not play the alarm. Click inside the admin page first, check device volume, then try again.',
    'notificationSettings.alarmTitle': 'Noisy order alarm',
    'notificationSettings.alarmHelp': 'Repeat a loud alarm until the order is acknowledged.',
    'notificationSettings.disableAlarm': 'Disable order alarm',
    'notificationSettings.enableAlarm': 'Enable order alarm',
    'notificationSettings.volume': 'Volume',
    'notificationSettings.tipTitle': 'Keep this tab ready',
    'notificationSettings.tipBody': 'After login, click Enable Sound or test the alarm once. New orders will ring loudly until dismissed or opened.',
    'notificationSettings.testing': 'Testing...',
    'notificationSettings.testAlarm': 'Test alarm',
    'notificationSettings.done': 'Done',
    'branding.title': 'Restaurant branding',
    'branding.subtitle': 'Upload the logo customers and staff see across the ordering experience, receipts, and admin surfaces.',
    'branding.currentLogo': 'Current logo',
    'branding.logoAlt': 'Restaurant logo',
    'branding.noLogo': 'No logo uploaded yet',
    'branding.previewHelp': 'Use a clean logo with enough contrast so it works on receipts and customer screens.',
    'branding.uploadTitle': 'Upload a new logo',
    'branding.uploadHelp': 'The new logo is saved immediately after upload and becomes the active restaurant brand mark.',
    'branding.dropTitle': 'Click to upload or drag an image here',
    'branding.fileRules': 'PNG or JPG up to 2MB',
    'branding.chooseFile': 'Choose file',
    'branding.uploading': 'Uploading logo...',
    'branding.tipTitle': 'Branding tip',
    'branding.tipBody': 'Square or horizontal logos work best. Avoid tiny text because it may be unreadable on receipts and compact screens.',
    'branding.imageTypeError': 'Please select an image file.',
    'branding.imageSizeError': 'Image size must be less than 2MB.',
    'branding.uploaded': 'Logo uploaded successfully.',
    'branding.uploadFailed': 'Failed to upload logo. Please try again.',
    'feeSettings.title': 'Delivery fee settings',
    'feeSettings.subtitle': 'Control delivery pricing, distance limits, free-delivery thresholds, and preview how customers will be charged before saving.',
    'feeSettings.loadFailed': 'Failed to load delivery fee configuration.',
    'feeSettings.saved': 'Delivery fee settings saved. Changes take effect immediately.',
    'feeSettings.saveFailed': 'Failed to save delivery fee settings.',
    'feeSettings.save': 'Save settings',
    'feeSettings.saving': 'Saving...',
    'feeSettings.reset': 'Reset changes',
    'feeSettings.unsaved': 'You have unsaved delivery-fee changes. Review the preview and save when ready.',
    'feeSettings.perMile': 'Per mile',
    'feeSettings.deliveryRange': 'Delivery range',
    'feeSettings.freeOver': 'Free over',
    'feeSettings.baseRule': 'Base rule',
    'feeSettings.disabled': 'Disabled',
    'feeSettings.miles': 'miles',
    'feeSettings.feeConfig': 'Fee configuration',
    'feeSettings.feeConfigHelp': 'Set the main pricing rules customers see during delivery checkout.',
    'feeSettings.ratePerMile': 'Rate per mile',
    'feeSettings.rateHelp': 'Distance-based charge multiplied by delivery miles.',
    'feeSettings.freeThreshold': 'Free delivery threshold',
    'feeSettings.freeThresholdHelp': 'Orders at or above this subtotal receive free delivery.',
    'feeSettings.baseEnabled': 'Enable base delivery fee',
    'feeSettings.baseEnabledHelp': 'Adds a fixed charge for deliveries inside the distance window below.',
    'feeSettings.baseFee': 'Base delivery fee',
    'feeSettings.baseFeeHelp': 'Fixed amount added before the per-mile charge when the base rule applies.',
    'feeSettings.baseMin': 'Base minimum distance',
    'feeSettings.baseMinHelp': 'Optional lower distance before the base fee starts applying.',
    'feeSettings.baseMax': 'Base maximum distance',
    'feeSettings.baseMaxHelp': 'Highest distance where the base fee still applies.',
    'feeSettings.noMinimum': 'No minimum',
    'feeSettings.distanceLimits': 'Delivery distance limits',
    'feeSettings.distanceLimitsHelp': 'Set the supported distance window for delivery availability.',
    'feeSettings.minDistance': 'Minimum distance',
    'feeSettings.minDistanceHelp': 'Shortest distance allowed for delivery orders.',
    'feeSettings.maxDistance': 'Maximum distance',
    'feeSettings.maxDistanceHelp': 'Farthest distance the restaurant will deliver.',
    'feeSettings.validationTitle': 'Readiness check',
    'feeSettings.validationHelp': 'Fix these rules before saving.',
    'feeSettings.readyHelp': 'The current rules are valid and ready to save.',
    'feeSettings.readyTitle': 'Delivery-fee rules look good.',
    'feeSettings.livePreview': 'Live fee preview',
    'feeSettings.previewHelp': 'Test what customers will see at common delivery distances.',
    'feeSettings.previewOrderTotal': 'Preview order subtotal',
    'feeSettings.previewOrderTotalHelp': 'Change this to test free-delivery behavior.',
    'feeSettings.previewDistance': 'Preview distance',
    'feeSettings.freeDeliveryDetail': 'Free delivery because subtotal is at least {{threshold}}.',
    'feeSettings.baseApplies': 'Base fee applies: {{amount}}.',
    'feeSettings.baseSkipped': 'Base fee does not apply at this distance.',
    'feeSettings.distanceFeeDetail': '{{distance}} miles x {{rate}} per mile = {{amount}}.',
    'feeSettings.noDeliveryCharge': 'No delivery charge for this preview.',
    'feeSettings.rateNegative': 'Rate per mile cannot be negative.',
    'feeSettings.baseNegative': 'Base fee cannot be negative.',
    'feeSettings.baseMaxNegative': 'Base fee maximum distance cannot be negative.',
    'feeSettings.baseMinNegative': 'Base fee minimum distance cannot be negative.',
    'feeSettings.baseRangeInvalid': 'Base fee minimum distance must be less than the maximum distance.',
    'feeSettings.minNegative': 'Minimum delivery distance cannot be negative.',
    'feeSettings.maxInvalid': 'Maximum delivery distance must be greater than minimum distance.',
    'feeSettings.freeNegative': 'Free delivery threshold cannot be negative.',
    'feeSettings.locationPresets': 'Call-center delivery presets',
    'feeSettings.locationPresetsHelp': 'Create quick delivery areas with fixed fees for phone orders. Staff can still choose Custom when needed.',
    'feeSettings.noLocationPresets': 'No delivery presets yet.',
    'feeSettings.presetName': 'Area name',
    'feeSettings.presetNameAr': 'Arabic name',
    'feeSettings.presetFee': 'Fee',
    'feeSettings.sort': 'Sort',
    'feeSettings.active': 'Active',
    'feeSettings.inactive': 'Inactive',
    'feeSettings.addLocationPreset': 'Add delivery preset',
    'feeAdjustments.title': 'Delivery fee adjustments',
    'feeAdjustments.subtitle': 'Review driver and admin delivery-fee changes with a clear approval trail before order totals are updated.',
    'feeAdjustments.visible': 'Shown',
    'feeAdjustments.pending': 'Pending',
    'feeAdjustments.credits': 'Credits',
    'feeAdjustments.net': 'Net',
    'feeAdjustments.notAvailable': 'N/A',
    'feeAdjustments.signInRequired': 'You need to be signed in as an admin to approve or reject adjustments.',
    'feeAdjustments.approvedNotice': 'Adjustment for order #{{order}} approved and applied. Any amount owed remains outstanding until payment or refund is recorded.',
    'feeAdjustments.rejectedNotice': 'Adjustment for order #{{order}} rejected.',
    'feeAdjustments.appliedNotice': 'Adjustment for order #{{order}} applied to the order total.',
    'feeAdjustments.actionFailed': 'Could not complete this adjustment action. Please try again.',
    'feeAdjustments.loadErrorTitle': 'Could not load fee adjustments',
    'feeAdjustments.emptyTitle': 'No fee adjustments need attention',
    'feeAdjustments.emptyHelp': 'There are no {{status}} delivery-fee adjustments right now.',
    'feeAdjustments.anyStatus': 'any status',
    'feeAdjustments.order': 'Order',
    'feeAdjustments.adjustment': 'Adjustment',
    'feeAdjustments.feeImpact': 'Fee impact',
    'feeAdjustments.originalFee': 'Original fee',
    'feeAdjustments.adjustedFee': 'Adjusted fee',
    'feeAdjustments.reportedBy': 'Reported by',
    'feeAdjustments.locationDetails': 'Location mismatch details',
    'feeAdjustments.lockedDistance': 'Locked GPS distance',
    'feeAdjustments.actualDistance': 'Actual delivery distance',
    'feeAdjustments.difference': 'Difference',
    'feeAdjustments.rejectionReason': 'Rejection reason',
    'feeAdjustments.appliedOn': 'Applied on',
    'feeAdjustments.approve': 'Approve and apply',
    'feeAdjustments.reject': 'Reject',
    'feeAdjustments.apply': 'Apply to order',
    'feeAdjustments.approving': 'Approving...',
    'feeAdjustments.rejecting': 'Rejecting...',
    'feeAdjustments.applying': 'Applying...',
    'feeAdjustments.cancel': 'Cancel',
    'feeAdjustments.approveTitle': 'Approve fee adjustment',
    'feeAdjustments.approveBody': 'Approve {{amount}} for order #{{order}}? This marks it approved, but the order total will not change until it is applied.',
    'feeAdjustments.approveConfirm': 'Approve adjustment',
    'feeAdjustments.approveProcessing': 'Approving...',
    'feeAdjustments.rejectTitle': 'Reject fee adjustment',
    'feeAdjustments.rejectBody': 'Reject {{amount}} for order #{{order}}? Add a short reason so the team can understand the decision.',
    'feeAdjustments.rejectPlaceholder': 'Reason for rejection, optional',
    'feeAdjustments.rejectConfirm': 'Reject adjustment',
    'feeAdjustments.rejectProcessing': 'Rejecting...',
    'feeAdjustments.applyTitle': 'Apply to order total',
    'feeAdjustments.applyBody': 'Apply {{amount}} to order #{{order}} now? This updates the order total and marks the adjustment as applied.',
    'feeAdjustments.applyConfirm': 'Apply adjustment',
    'feeAdjustments.applyProcessing': 'Applying...',
    'feeAdjustments.status.all': 'All',
    'feeAdjustments.status.pending': 'Pending',
    'feeAdjustments.status.approved': 'Approved',
    'feeAdjustments.status.rejected': 'Rejected',
    'feeAdjustments.status.applied': 'Applied',
    'feeAdjustments.reason.location_mismatch': 'Location mismatch',
    'feeAdjustments.reason.driver_extra_distance': 'Extra distance',
    'feeAdjustments.reason.admin_correction': 'Admin correction',
    'feeAdjustments.reason.customer_complaint': 'Customer complaint',
    'admin.staff': 'Staff',
    'admin.customers': 'Customers',
    'admin.trustScores': 'Trust Scores',
    'trustScores.title': 'Customer trust scores',
    'trustScores.subtitle': 'Monitor location mismatches, complaints, and automatic customer restrictions before risky delivery behavior affects operations.',
    'trustScores.refresh': 'Refresh',
    'trustScores.loadFailed': 'Failed to load customer trust scores.',
    'trustScores.loadErrorTitle': 'Could not load trust scores',
    'trustScores.tryAgain': 'Try again',
    'trustScores.customersShown': 'Customers shown',
    'trustScores.avgScore': 'Avg score',
    'trustScores.critical': 'Critical',
    'trustScores.manualApproval': 'Manual approval',
    'trustScores.upfrontPayment': 'Upfront payment',
    'trustScores.allCustomers': 'All customers',
    'trustScores.belowScore': 'Below {{score}}',
    'trustScores.sort.score': 'Lowest score',
    'trustScores.sort.mismatches': 'Mismatches',
    'trustScores.sort.complaints': 'Complaints',
    'trustScores.sort.orders': 'Orders',
    'trustScores.emptyTitle': 'No risky customers in this view',
    'trustScores.emptyHelp': 'No customers match {{threshold}} right now.',
    'trustScores.customer': 'Customer',
    'trustScores.trustScore': 'Trust score',
    'trustScores.mismatches': 'Mismatches',
    'trustScores.complaints': 'Complaints',
    'trustScores.totalOrders': 'Total orders',
    'trustScores.flags': 'Flags',
    'trustScores.restrictions': 'Restrictions',
    'trustScores.none': 'None',
    'trustScores.restriction.manualApproval': 'Manual approval required',
    'trustScores.restriction.upfrontPayment': 'Upfront payment required',
    'trustScores.restriction.maxDistance': 'Max distance',
    'trustScores.lastUpdated': 'Last updated',
    'trustScores.infoTitle': 'How trust scoring works',
    'trustScores.infoRange': 'Scores range from 0-100, with 100 being the most reliable customer behavior.',
    'trustScores.infoSignals': 'Location mismatches, driver complaints, order history, and risk flags lower the score automatically.',
    'trustScores.infoRestrictions': 'Restrictions such as manual approval or upfront payment are applied when risk passes configured thresholds.',
    'trustScores.infoReview': 'Use this screen to spot accounts that need review before they create delivery or payment friction.',
    'admin.branding': 'Branding',
    'admin.reviews': 'Reviews',
    'printing.eyebrow': 'Kitchen printing',
    'printing.title': 'Station Tickets',
    'printing.subtitle': 'Route order items to kitchen, cashier, bar, or custom printers when staff starts preparing an order.',
    'printing.stations': 'Stations',
    'printing.agents': 'Agents',
    'printing.unmapped': 'Unmapped',
    'printing.failed': 'Failed',
    'printing.enabledStations': 'Enabled stations',
    'printing.enabledStationsReady': '{{count}} station{{plural}} ready',
    'printing.createStationFirst': 'Create at least one kitchen or cashier station.',
    'printing.localAgentOnline': 'Local agent online',
    'printing.agentsOnline': '{{count}} agent{{plural}} online',
    'printing.pairAgentBeforeService': 'Pair or open the Windows Local Agent before service.',
    'printing.categoryRouting': 'Category routing',
    'printing.allCategoriesMapped': 'All top-level categories are mapped.',
    'printing.categoriesNeedRoute': '{{count}} categor{{plural}} still need a printer route.',
    'printing.fallbackStation': 'Fallback station',
    'printing.fallbackAvailable': 'Fallback is available for unmapped or new items.',
    'printing.markFallback': 'Mark one reliable printer as fallback.',
    'printing.failedJobs': 'Failed jobs',
    'printing.noFailedJobs': 'No failed print jobs waiting.',
    'printing.failedJobsNeedRetry': '{{count}} failed job{{plural}} need retry or cancel.',
    'printing.registerReadiness': 'Register readiness',
    'printing.readyTitle': 'Printing is ready for service',
    'printing.attentionTitle': 'Printing needs attention before rush',
    'printing.readinessHelp': 'These checks cover station setup, Local Agent health, routing, fallback safety, and stuck jobs.',
    'printing.fixFirst': 'Fix first: {{detail}}',
    'printing.retryFailed': 'Retry failed ({{count}})',
    'printing.findingPrinters': 'Finding printers...',
    'printing.findPrinters': 'Find printers',
    'printing.generating': 'Generating...',
    'printing.pairAgent': 'Pair agent',
    'printing.printStations': 'Print stations',
    'printing.printStationsHelp': 'Use static printer IPs whenever possible.',
    'printing.noStations': 'No stations yet',
    'printing.noStationsHelp': 'Create a station for every kitchen section or printer route.',
    'printing.routingNeedsStation': 'Create a station first',
    'printing.routingNeedsStationHelp': 'Routing becomes available after at least one enabled station exists.',
    'printing.noPairedAgents': 'No paired agents',
    'printing.noPairedAgentsHelp': 'Generate a code, open the Local Agent on Windows, and enter the code.',
    'printing.noJobs': 'No jobs yet',
    'printing.noJobsHelp': 'Tickets will appear here after Start Preparing or a test print.',
    'printing.stationNameWindowsRequired': 'Station name and Windows printer name are required.',
    'printing.stationNameIpRequired': 'Station name and printer IP are required.',
    'printing.stationSaved': 'Print station saved.',
    'printing.stationSaveFailed': 'Failed to save print station.',
    'printing.stationUpdated': 'Print station updated.',
    'printing.stationUpdateFailed': 'Failed to update station.',
    'printing.routeSaved': 'Category route saved.',
    'printing.routeSaveFailed': 'Failed to save routing rule.',
    'printing.noApiUrl': 'No ForgedOS API URL is configured for this restaurant build.',
    'printing.signInRequired': 'You need to be signed in as a restaurant admin.',
    'printing.pairingCreateFailed': 'Failed to create pairing code.',
    'printing.pairingCreated': 'Pairing code created. Enter it in the Local Agent within 10 minutes.',
    'printing.pairingErrorWithUrls': '{{message}} Tried API URLs: {{details}}',
    'printing.pairingCopied': 'Pairing code copied.',
    'printing.askingLocalPrinters': 'Asking the ForgedOS Local Agent for Windows printer names...',
    'printing.checkingLocalPrinters': 'Checking this computer for Windows printers through the Local Agent.',
    'printing.foundWindowsPrinters': 'Found {{count}} Windows printer{{plural}} on this device.',
    'printing.noWindowsPrinters': 'No Windows printers found. Install the USB printer in Windows first.',
    'printing.localAgentTimeout': 'ForgedOS Local Agent did not respond. Open the agent on this same Windows computer and try again.',
    'printing.localPrintersLoadFailed': 'Could not load local printers.',
    'printing.selectedPrinter': 'Selected {{name}}. Save the station when ready.',
    'printing.retryQueued': 'Retry queued for {{count}} failed print job{{plural}}.',
    'printing.retryPrintJobsFailed': 'Could not retry failed print jobs.',
    'printing.stationName': 'Station name',
    'printing.stationNamePlaceholder': 'Shawarma station',
    'printing.stationType': 'Type',
    'printing.typeKitchen': 'Kitchen',
    'printing.typeCashier': 'Cashier',
    'printing.typeBar': 'Bar',
    'printing.typeCustom': 'Custom',
    'printing.printerConnection': 'Printer connection',
    'printing.networkPrinter': 'Network IP printer',
    'printing.windowsUsbPrinter': 'Windows / USB printer',
    'printing.windowsPrinterName': 'Windows printer name',
    'printing.windowsPrinterHelp': 'Open the Windows Local Agent on the cashier computer, choose USB and Windows printers, then copy the exact printer name here.',
    'printing.printerIp': 'Printer IP',
    'printing.port': 'Port',
    'printing.enabled': 'Enabled',
    'printing.fallback': 'Fallback station',
    'printing.saveStation': 'Save station',
    'printing.fallbackBadge': 'Fallback',
    'printing.disabledBadge': 'Disabled',
    'printing.test': 'Test',
    'printing.disable': 'Disable',
    'printing.enable': 'Enable',
    'printing.archive': 'Archive',
    'printing.categoryRoutingHelp': 'Assign top-level categories. Items in subcategories follow their parent category printer.',
    'printing.includesSubcategories': 'Includes {{count}} subcategor{{plural}}',
    'printing.topLevelCategory': 'Top-level category',
    'printing.unmappedOption': 'Unmapped',
    'printing.localAgentTitle': 'ForgedOS Local Agent',
    'printing.localAgentHelp': 'One Windows agent handles printing, drawers, and optional phone detection.',
    'printing.downloadWindowsAgent': 'Download Windows Agent',
    'printing.generateCode': 'Generate code',
    'printing.windowsSetupFlow': 'Windows setup flow',
    'printing.windowsSetupHelp': 'Download the installer on the restaurant Windows computer, open the Local Agent, then enter a pairing code from this screen.',
    'printing.localUsbTitle': 'Local USB / Windows printers',
    'printing.localUsbHelp': 'Use this on the cashier Windows computer to pull printer names directly from the Local Agent.',
    'printing.finding': 'Finding...',
    'printing.findLocalPrinters': 'Find local printers',
    'printing.defaultPrinter': 'default',
    'printing.copyName': 'Copy name',
    'printing.useForStation': 'Use for station',
    'printing.pairingCode': 'Pairing code',
    'printing.expiresOnce': 'Expires in 10 minutes and can be used once.',
    'printing.copyCode': 'Copy code',
    'printing.lastHeartbeat': 'Last heartbeat',
    'printing.online': 'Online',
    'printing.offline': 'Offline',
    'printing.unifiedAgentPaired': 'Unified Local Agent is paired on {{count}} workstation{{plural}}. Existing Print Agent installs can remain during the pilot, then be removed after printing checks pass.',
    'printing.recentPrintJobs': 'Recent print jobs',
    'printing.recentPrintJobsHelp': 'Failed and stuck jobs can be retried here.',
    'printing.itemsCount': '{{count}} item{{plural}}',
    'printing.retry': 'Retry',
    'printing.cancel': 'Cancel',
    'reviews.title': 'Customer reviews',
    'reviews.subtitle': 'Track customer feedback, verified reviews, photos, and low-rating signals in one clean reputation dashboard.',
    'reviews.refresh': 'Refresh',
    'reviews.loadFailed': 'Failed to load reviews. Please try again.',
    'reviews.loadErrorTitle': 'Could not load reviews',
    'reviews.tryAgain': 'Try again',
    'reviews.deleted': 'Review deleted.',
    'reviews.deleteFailed': 'Failed to delete review. Please try again.',
    'reviews.totalReviews': 'Total reviews',
    'reviews.averageRating': 'Average rating',
    'reviews.deliveryRating': 'Delivery rating',
    'reviews.verified': 'Verified',
    'reviews.lowRatings': 'Low ratings',
    'reviews.ratingDistribution': 'Rating distribution',
    'reviews.photoReviews': 'Photo reviews',
    'reviews.photoReviewsHelp': 'Reviews with customer-uploaded images are useful for quality checks and marketing follow-up.',
    'reviews.filter.all': 'All reviews',
    'reviews.filter.low': 'Low ratings',
    'reviews.filter.high': 'High ratings',
    'reviews.filter.photos': 'With photos',
    'reviews.filter.unverified': 'Unverified',
    'reviews.sort.newest': 'Newest',
    'reviews.sort.lowest': 'Lowest rating',
    'reviews.sort.highest': 'Highest rating',
    'reviews.sort.helpful': 'Most helpful',
    'reviews.emptyTitle': 'No reviews match this view',
    'reviews.emptyHelp': 'Customer reviews will appear here once feedback starts coming in.',
    'reviews.verifiedBadge': 'Verified',
    'reviews.order': 'Order',
    'reviews.overallRating': 'Overall',
    'reviews.menuItemReviews': 'Menu item reviews',
    'reviews.photos': 'Photos',
    'reviews.helpful': 'found helpful',
    'reviews.delete': 'Delete review',
    'reviews.deleteTitle': 'Delete review',
    'reviews.deleteBody': 'Delete this customer review? This action cannot be undone.',
    'reviews.cancel': 'Cancel',
    'reviews.deleting': 'Deleting...',
    'reviews.deleteConfirm': 'Delete review',
    'stats.activeOrders': 'Active Orders',
    'stats.preparing': 'Preparing',
    'stats.outForDelivery': 'Out for Delivery',
    'stats.completedToday': 'Completed Today',
    'manager.available': 'Available',
    'manager.unavailable': 'Unavailable',
    'manager.cancel': 'Cancel',
    'manager.delete': 'Delete',
    'manager.deleteAddonTitle': 'Delete add-on',
    'manager.deleteAddonBody': 'Delete "{{name}}"? This removes it from this item or category setup.',
    'manager.deleteRemovableTitle': 'Delete removable item',
    'manager.deleteRemovableBody': 'Delete "{{name}}"? Customers will no longer see it as a removable choice here.',
    'callCenter.title': 'Phone order command center',
    'callCenter.subtitle': 'Catch caller ID, build drafts fast, send the customer confirmation link, and push confirmed orders into the normal delivery flow.',
    'callCenter.liveCalls': 'Live calls',
    'callCenter.waiting': 'Waiting',
    'callCenter.active': 'Active',
    'callCenter.cancelDraft': 'Cancel draft',
    'callCenter.keepDraft': 'Keep draft',
    'callCenter.cancelDraftBody': 'Cancel this phone-order draft? The customer confirmation link will stop working.',
    'callCenter.cancelling': 'Cancelling...',
    'callCenter.workstation': 'This workstation',
    'callCenter.workstationHelp': 'Use this when each call-center computer should show calls for one phone extension.',
    'callCenter.extensionPlaceholder': 'Extension, e.g. 101',
    'callCenter.save': 'Save',
    'callCenter.allExtensions': 'Showing calls from all paired phone agents.',
    'callCenter.filteringExtension': 'Filtering incoming calls to extension {{extension}}.',
    'callCenter.extensionShort': 'Ext {{extension}}',
    'callCenter.incomingCalls': 'Incoming calls',
    'callCenter.live': 'Live',
    'callCenter.recent': 'Recent',
    'callCenter.manualCallerFallback': 'Manual caller fallback',
    'callCenter.manualExtension': 'Manual extension, optional',
    'callCenter.manualCallerNumber': 'Manual caller number',
    'callCenter.add': 'Add',
    'callCenter.noLiveShowRecent': 'No live calls. Show recent calls',
    'callCenter.noCallsLive': 'No calls ringing right now.',
    'callCenter.noCallsRecent': 'No calls yet. Use manual fallback until the Local Agent is paired.',
    'callCenter.callerProfile': 'Caller profile',
    'callCenter.noCaller': 'No caller selected',
    'callCenter.selectOrEnterPhone': 'Select or enter a phone number',
    'callCenter.remembered': 'Remembered from this phone number',
    'callCenter.startDraft': 'Start new delivery draft',
    'callCenter.failedMessageAlert': 'Message failed - use a fallback now',
    'callCenter.duplicateOrderAlert': 'Caller already has an active order',
    'callCenter.duplicateDraftAlert': 'Caller already has an active draft',
    'callCenter.callerRiskSummary': '{{drafts}} active draft(s), {{orders}} active order(s). Current stage: {{stage}}.',
    'callCenter.openDraft': 'Open draft',
    'callCenter.openOrder': 'Open order',
    'callCenter.sendLocationOnly': 'Send location only',
    'callCenter.duplicateDraftBlocked': 'This caller already has an active draft. I opened it instead of creating a duplicate.',
    'callCenter.stage.none': 'none',
    'callCenter.stage.draft': 'draft',
    'callCenter.stage.location_sent': 'location sent',
    'callCenter.stage.waiting_confirmation': 'waiting confirmation',
    'callCenter.stage.submitted': 'submitted',
    'callCenter.stage.cancelled': 'cancelled',
    'callCenter.stage.failed_message': 'message failed',
    'callCenter.draftWorkspace': 'Draft workspace',
    'callCenter.startDraftHelp': 'Start a draft to build a phone order.',
    'callCenter.draftReadOnly': 'This draft is {{status}}. It is kept as a read-only record so the caller history stays clear.',
    'callCenter.sendWhatsapp': 'Send WhatsApp',
    'callCenter.sendSms': 'Send SMS',
    'callCenter.copyLink': 'Copy Link',
    'callCenter.manualFinish': 'Manual finish',
    'callCenter.placeOrderNow': 'Place order now',
    'callCenter.sendLocationWhatsapp': 'Send location WhatsApp',
    'callCenter.holdForConfirmation': 'Hold for confirmation',
    'callCenter.hold': 'Hold',
    'callCenter.customerName': 'Customer name',
    'callCenter.addressNotes': 'Manual delivery address / landmark fallback',
    'callCenter.orderNotes': 'Order notes for kitchen/driver',
    'callCenter.deliveryFee': 'Manual delivery fee',
    'callCenter.deliveryArea': 'Delivery area',
    'callCenter.fulfillment': 'Fulfillment',
    'callCenter.pickup': 'Pickup',
    'callCenter.deliveryOrder': 'Delivery',
    'callCenter.paymentIntent': 'Customer payment',
    'callCenter.cash': 'Cash',
    'callCenter.card': 'Card',
    'callCenter.pickupClearsDelivery': 'Switching to pickup removes the delivery area, address, location, and fee from this draft.',
    'callCenter.keepDelivery': 'Keep delivery',
    'callCenter.switchToPickup': 'Switch to pickup',
    'callCenter.deliveryAreaName': 'Area name',
    'callCenter.deliveryAreaPlaceholder': 'Custom area or landmark',
    'callCenter.customDelivery': 'Custom',
    'callCenter.locationNeedsReview': 'Customer GPS may need a delivery-fee review.',
    'callCenter.needsFollowUp': 'Needs follow-up',
    'callCenter.waitingTooLong': 'Waiting more than 3 minutes for customer confirmation.',
    'callCenter.locationLink': 'Location link',
    'callCenter.locationLinkSent': 'Exact location link sent. The secure link was copied when browser permissions allowed it.',
    'callCenter.requestExactPin': 'Request exact pin',
    'callCenter.whatsappConsent': 'Customer agreed to receive this order confirmation on WhatsApp',
    'callCenter.saveDetails': 'Save details',
    'callCenter.details': 'Customer and delivery details',
    'callCenter.total': 'Total',
    'callCenter.subtotal': 'Subtotal',
    'callCenter.delivery': 'Delivery',
    'callCenter.whatsapp': 'WhatsApp',
    'callCenter.sms': 'SMS',
    'callCenter.noDraft': 'No draft selected.',
    'callCenter.whatsappReplies': 'WhatsApp replies',
    'callCenter.whatsappRepliesHelp': 'Replies from this caller stay attached to the phone-order draft.',
    'callCenter.useSmsFallback': 'Use SMS fallback',
    'callCenter.noWhatsappMessages': 'No WhatsApp messages for this draft yet.',
    'callCenter.replyPlaceholder': 'Reply on WhatsApp',
    'callCenter.send': 'Send',
    'callCenter.searchMenu': 'Search menu to add items',
    'callCenter.menuBuilder': 'Menu builder',
    'callCenter.menuBuilderHelp': 'Find items fast by category, image, variant, add-on, or Arabic menu text.',
    'callCenter.allMenu': 'All menu',
    'callCenter.quickPicks': 'Quick picks',
    'callCenter.fastPicks': 'Fast picks',
    'callCenter.fastPicksHelp': 'Popular and featured shortcuts for rush calls.',
    'callCenter.popular': 'Popular',
    'callCenter.noMenuMatches': 'No menu items match this search or category.',
    'callCenter.unavailable': 'Unavailable',
    'callCenter.variants': '{{count}} variants',
    'callCenter.singleItem': 'Single item',
    'callCenter.customOptions': 'Custom options',
    'callCenter.quickAdd': 'Quick add',
    'callCenter.customize': 'Customize',
    'callCenter.itemUnavailable': 'This item is currently unavailable.',
    'callCenter.chooseVariant': 'Choose variant',
    'callCenter.addons': 'Add-ons',
    'callCenter.removals': 'Remove',
    'callCenter.quantity': 'Qty',
    'callCenter.no': 'No',
    'callCenter.addToDraft': 'Add to draft',
    'callCenter.removeLine': 'Remove',
    'callCenter.draftCart': 'Draft cart',
    'callCenter.noItems': 'No items yet.',
    'callCenter.connectedOrders': 'Connected orders',
    'callCenter.noPreviousOrders': 'No previous orders for this caller.',
    'callCenter.drafts': 'Drafts',
    'callCenter.noDrafts': 'No phone-order drafts yet.',
    'callCenter.phoneDraft': 'Phone draft',
    'callCenter.itemsCount': '{{count}} item{{plural}}',
    'callCenter.finish': 'Finish',
    'callCenter.open': 'Open',
    'callCenter.repeat': 'Repeat',
    'callCenter.orderActive': 'Active',
    'callCenter.orderRecent': 'Recent',
    'callCenter.manualCallCreated': 'Manual call session created.',
    'callCenter.manualCallError': 'Could not create call session.',
    'callCenter.extensionSaved': 'This screen now shows calls for extension {{extension}}.',
    'callCenter.allExtensionsSaved': 'This screen now shows calls from all extensions.',
    'callCenter.enterCallerPhone': 'Enter or select a caller phone number first.',
    'callCenter.draftStarted': 'Phone order draft started.',
    'callCenter.draftFirst': 'Start a phone order draft first.',
    'callCenter.detailsSaved': 'Draft details saved.',
    'callCenter.saveDraftError': 'Could not save draft.',
    'callCenter.whatsappConsentRequired': 'Confirm that the customer agreed to receive the WhatsApp confirmation message first.',
    'callCenter.linkCopied': 'Secure link copied on this device when browser permissions allow it.',
    'callCenter.confirmationSent': '{{channel}} confirmation sent. The secure link was also copied when browser permissions allow it.',
    'callCenter.sendChannelError': 'Could not send {{channel}}.',
    'callCenter.replyQueued': 'WhatsApp reply queued.',
    'callCenter.replyError': 'Could not send WhatsApp reply.',
    'callCenter.manualSubmitted': 'Manual phone order submitted as #{{orderNumber}}.',
    'callCenter.manualSubmitError': 'Could not submit manual order.',
    'callCenter.draftCancelled': 'Phone-order draft cancelled.',
    'callCenter.cancelDraftError': 'Could not cancel draft.',
    'callCenter.loadedOrder': 'Loaded items from order #{{orderNumber}}.',
    'customer.title': 'Customer Management',
    'customer.subtitle': 'Search customers, review order behavior, and manage risk signals from one register-friendly workspace.',
    'customer.total': 'Customers',
    'customer.verified': 'Verified',
    'customer.restricted': 'Restricted',
    'customer.blocked': 'Blocked',
    'customer.searchPlaceholder': 'Search name, email, or phone...',
    'customer.shown': '{{count}} shown',
    'customer.clear': 'Clear',
    'customer.selectTitle': 'Select a customer',
    'customer.selectHelp': 'Customer history, risk, and order behavior will appear here.',
    'customer.loadFailed': 'Customer load failed',
    'customer.loadFailedDetail': 'Could not load customers for this restaurant. Check the connection, then try again.',
    'customer.tryAgain': 'Try again',
    'customer.notFound': 'No customers found',
    'customer.notFoundHelp': 'Try a different name, email, or phone number.',
    'customer.blacklisted': 'Blacklisted',
    'customer.review': 'Review',
    'customer.phoneVerified': 'Phone verified',
    'customer.phoneUnverified': 'Phone unverified',
    'customer.lastOrder': 'Last order #{{orderNumber}} on {{date}}.',
    'customer.removeBlacklist': 'Remove from Blacklist',
    'customer.blacklist': 'Blacklist Customer',
    'customer.blacklistWarning': 'This customer is blacklisted and cannot place new orders.',
    'customer.confirmBlacklistTitle': 'Blacklist customer?',
    'customer.confirmBlacklistBody': 'Blacklist {{name}}? They will not be able to place new orders until a manager removes the restriction.',
    'customer.confirmBlacklistAction': 'Blacklist customer',
    'customer.confirmUnblacklistTitle': 'Remove blacklist?',
    'customer.confirmUnblacklistBody': 'Remove {{name}} from the blacklist? They will be able to place new orders again.',
    'customer.confirmUnblacklistAction': 'Remove blacklist',
    'customer.keepStatus': 'Keep current status',
    'customer.updatingStatus': 'Updating...',
    'customer.totalOrders': 'Total Orders',
    'customer.activeOrders': 'Active Orders',
    'customer.cancelRate': 'Cancellation Rate',
    'customer.totalSpent': 'Total Spent',
    'customer.averageOrderValue': 'Average Order Value',
    'customer.completedOrders': 'Completed Orders',
    'customer.orderMix': 'Order Mix',
    'customer.orderMixValues': '{{delivery}} delivery | {{pickup}} pickup | {{cancelled}} cancelled/rejected',
    'customer.orderHistory': 'Order History',
    'customer.orderHistoryHelp': 'Latest orders first, optimized for quick customer lookup.',
    'customer.ordersCount': '{{count}} orders',
    'customer.noOrders': 'No orders found',
    'customer.orderNumber': 'Order #',
    'customer.date': 'Date',
    'customer.status': 'Status',
    'customer.type': 'Type',
    'customer.totalAmount': 'Total',
    'orders.liveBoard': 'Live order board',
    'orders.title': 'Orders',
    'orders.subtitle': 'Scan active tickets, edit items, print receipts, and move orders through service.',
    'orders.active': 'Active',
    'orders.scheduled': 'Scheduled',
    'orders.delivery': 'Delivery',
    'orders.assignedDrivers': 'Assigned',
    'orders.completed': 'Completed',
    'orders.searchPlaceholder': 'Search order number or customer...',
    'orders.compact': 'Compact',
    'orders.comfortable': 'Comfortable',
    'orders.hideClosed': 'Hide Closed',
    'orders.showClosed': 'Show Closed',
    'orders.activeSection': 'Active Orders',
    'orders.rejectedSection': 'Rejected Orders',
    'orders.cancelledSection': 'Cancelled Orders',
    'orders.completedSection': 'Completed Orders',
    'orders.notFound': 'No orders found',
    'orders.notFoundHelp': 'Try a different filter or search term.',
    'orders.noLiveMatches': 'No live orders match this view',
    'orders.closedHidden': '{{count}} closed order{{plural}} hidden for rush mode.',
    'orders.showClosedOrders': 'Show Closed Orders',
    'orders.filter': 'Filter',
    'orders.all': 'All',
    'orders.pickup': 'Pickup',
    'pos.searchPlaceholder': 'Search menu, category, or item...',
    'pos.keyboard': 'Keyboard',
    'pos.repeat': 'Repeat',
    'pos.allMenu': 'All Menu',
    'pos.itemsCount': '{{count}} item{{plural}}',
    'pos.loadingMenu': 'Loading menu...',
    'pos.noMenuMatches': 'No menu items match this view.',
    'pos.currentOrder': 'Current Order',
    'pos.lineItems': '{{count}} line item{{plural}}',
    'pos.parked': 'Held {{count}}',
    'pos.clear': 'Clear',
    'pos.startCounterOrder': 'Start a counter order',
    'pos.tapMenuTiles': 'Tap menu tiles to add items here.',
    'pos.available': 'Available',
    'pos.unavailable': 'Unavailable',
    'pos.variants': '{{count}} variants',
    'pos.singleItem': 'Single item',
    'pos.add': 'Add',
    'pos.searchKeyboardPlaceholder': 'Tap keys to search',
    'pos.customerNamePlaceholder': 'Customer name optional',
    'pos.customerPhonePlaceholder': 'Phone optional',
    'pos.lookup': 'Lookup',
    'pos.discountCodePlaceholder': 'Discount code',
    'pos.apply': 'Apply',
    'pos.subtotal': 'Subtotal',
    'pos.deliveryFee': 'Delivery fee',
    'pos.discount': 'Discount',
    'pos.total': 'Total',
    'pos.resumedParkedOrder': 'Resumed held cart',
    'pos.payNowOrParkAgain': 'Pay it now or hold it again to save changes.',
    'pos.settlingMobileOrder': 'Settling mobile order',
    'pos.editItemsNoReprint': 'Edit items if needed, then take payment. Kitchen tickets will not reprint.',
    'pos.parkOrder': 'Hold Cart',
    'pos.cannotParkSettlement': 'Cannot Hold Settlement',
    'pos.updateParkedOrder': 'Update Held Cart',
    'pos.cash': 'Cash',
    'pos.card': 'Card',
    'pos.closeShiftTitle': 'Close POS Shift',
    'pos.closeoutReport': 'Closeout Report',
    'pos.expected': 'Expected',
    'pos.counted': 'Counted',
    'pos.overShort': 'Over / Short',
    'pos.starting': 'Starting',
    'pos.cashSales': 'Cash sales',
    'pos.cardSales': 'Card sales',
    'pos.cashIn': 'Cash in',
    'pos.cashOut': 'Cash out',
    'pos.voids': 'Voids',
    'pos.refunds': 'Refunds',
    'pos.countedCashPlaceholder': 'Counted cash',
    'pos.closeoutNotesPlaceholder': 'Closeout notes',
    'pos.printCloseoutReport': 'Print Closeout Report',
    'pos.closeShift': 'Close Shift',
    'pos.cashPaymentTitle': 'Cash Payment',
    'pos.amountDue': 'Amount due',
    'pos.cashReceived': 'Cash received',
    'pos.changeDue': 'Change due',
    'pos.exact': 'Exact',
    'pos.back': 'Back',
    'pos.completeCashPayment': 'Complete Cash Payment',
    'pos.savedForLater': 'Saved for later',
    'pos.lineItemsStayEditable': '{{count}} line item{{plural}} will stay editable.',
    'pos.nameOrLabel': 'Name or label',
    'pos.parkLabelPlaceholder': 'Customer name, phone, or order label',
    'pos.internalNoteOptional': 'Internal note optional',
    'pos.parkNotesPlaceholder': 'Waiting for friend, customer will pay later, etc.',
    'pos.writeKeyboardPlaceholder': 'Tap keys to write',
    'pos.saveParkedChanges': 'Save Held Cart',
    'pos.parkThisOrder': 'Hold This Cart',
    'pos.parkedOrdersTitle': 'Held Carts',
    'pos.parkedOrdersHelp': 'Unfinished sales saved safely before an order is placed.',
    'pos.parkedSearchPlaceholder': 'Search held carts by name, phone, or note',
    'pos.noParkedOrders': 'No held carts right now.',
    'pos.open': 'Open',
    'pos.edit': 'Edit',
    'pos.payCash': 'Pay Cash',
    'pos.payCard': 'Pay Card',
    'pos.cancel': 'Cancel',
    'pos.pendingReplaceTitle': 'Replace current cart?',
    'pos.pendingCancelTitle': 'Discard held cart?',
    'pos.pendingReplaceBody': 'Load "{{label}}" and replace the current cart? Unsaved items in the current cart will be cleared.',
    'pos.pendingCancelBody': 'Discard held cart "{{label}}"? This removes the unfinished sale from the work queue.',
    'pos.keepCurrentCart': 'Keep current cart',
    'pos.keepParkedOrder': 'Keep held cart',
    'pos.working': 'Working...',
    'pos.replaceCart': 'Replace cart',
    'pos.cancelParkedOrder': 'Discard held cart',
    'pos.cardTerminalPaymentTitle': 'Card Terminal Payment',
    'pos.amountToApprove': 'Amount to approve on terminal',
    'pos.cardTerminalHelp': 'Complete the payment on the external card terminal first. Only tap approved after the terminal confirms the charge.',
    'pos.cardTerminalApproved': 'Card Terminal Approved',
    'pos.marketplaceOrderTitle': '{{provider}} Order',
    'pos.marketplaceClipReceipt': 'Marketplace clip receipt',
    'pos.marketplaceHelp': 'Kitchen tickets will print now, plus a cashier clip receipt for the platform paper slip. No drawer opens and no cash/card is added to the register.',
    'pos.orderTotal': 'Order total',
    'pos.ticketNumber': 'Ticket number',
    'pos.marketplaceTicketPlaceholder': '{{provider}} ticket',
    'pos.ticketNumberKeyboard': 'Enter ticket number',
    'pos.printAndSendToKitchen': 'Print And Send To Kitchen',
    'pos.managerApproval': 'Manager Approval',
    'pos.drawerApprovalHelp': 'A verified restaurant admin must approve this action. The request and drawer job are recorded together.',
    'pos.openDrawer': 'Open Drawer',
    'pos.orderComplete': 'Order Complete',
    'pos.reprintReceipt': 'Reprint Receipt',
    'pos.retryFailedPrints': 'Retry Failed Prints',
    'pos.viewOrder': 'View Order',
    'pos.newOrder': 'New Order',
    'pos.amountPlaceholder': 'Amount',
    'pos.reasonPlaceholder': 'Reason',
    'pos.saveMovement': 'Save Movement',
    'pos.voidTransaction': 'Void Transaction',
    'pos.voidApproval': 'Voiding #{{orderNumber}} for {{total}} requires manager approval.',
    'pos.voidTransactionButton': 'Void Transaction',
    'pos.refund': 'Refund',
    'pos.refunded': 'Refunded',
    'pos.refundTransaction': 'Refund Transaction',
    'pos.refundApproval': 'Refunding #{{orderNumber}} for {{total}} requires manager approval.',
    'pos.voidHelp': 'Use void for a same-shift mistake before the order leaves the restaurant. The sale and order will be cancelled.',
    'pos.refundHelp': 'This records a full refund. Complete card or marketplace refunds on the provider terminal first; cash refunds reduce the drawer balance.',
    'pos.adjustmentReason': 'Required reason',
    'pos.adminPassword': 'Admin password',
    'pos.adminPasswordPlaceholder': 'Restaurant admin account password',
    'pos.approveWithGoogle': 'Approve with Google',
    'pos.googleApprovalHelp': 'A Google approval window will verify the restaurant admin account.',
    'pos.secureManagerApprovalHelp': 'Approval is verified by Firebase and is valid only briefly. The password is never sent to BiteOS functions or stored.',
    'pos.approveVoid': 'Approve And Void',
    'pos.approveRefund': 'Approve Full Refund',
    'pos.managerApprovalUnavailable': 'This admin account has no password or Google sign-in method available for secure approval.',
    'pos.voidSuccess': 'Order #{{orderNumber}} was voided for {{total}}.',
    'pos.refundSuccess': 'Order #{{orderNumber}} was refunded for {{total}}.',
    'pos.adjustmentFailed': 'The transaction could not be adjusted.',
    'pos.cashControlsRequireOnline': 'Sensitive cash controls require an online connection for secure manager approval.',
    'pos.manualDrawerAccess': 'Manual cash drawer access',
    'pos.drawerReasonPlaceholder': 'Why does the drawer need to be opened?',
    'pos.approveAndOpenDrawer': 'Approve And Open Drawer',
    'pos.drawerRequestQueued': 'Secure drawer request was approved and queued.',
    'pos.drawerRequestFailed': 'The secure drawer request could not be completed.',
    'pos.cashInHelp': 'Record cash added to the drawer outside a sale.',
    'pos.cashOutHelp': 'Record cash removed from the drawer outside a refund.',
    'pos.cashMovementApprovalHelp': 'The shift, expected drawer balance, financial ledger, audit trail, and drawer job are saved together.',
    'pos.approveAndSaveMovement': 'Approve And Save Movement',
    'pos.cashMovementSaved': 'Cash movement of {{amount}} was saved and the drawer was queued.',
    'pos.cashMovementSavedNoDrawer': 'Cash movement of {{amount}} was saved, but no enabled drawer station was available.',
    'pos.cashMovementFailed': 'The cash movement could not be saved.',
    'pos.fastActions': 'Fast Actions',
    'pos.fastActionsHelp': 'Popular, featured, recent, and repeat shortcuts for rush time.',
    'pos.repeatLastOrder': 'Repeat Last Order',
    'pos.noFastActions': 'Featured and recent menu shortcuts will appear here.',
    'pos.recent': 'Recent',
    'pos.popular': 'Popular',
    'pos.quickPicks': 'Quick picks',
    'pos.undo': 'Undo last cart change',
    'pos.menuFallback': 'Menu',
    'pos.printJob': 'Print job',
    'pos.itemReady': 'Ready to add to cart.',
    'pos.chooseVariant': 'Choose variant',
    'pos.addons': 'Add-ons',
    'pos.remove': 'Remove',
    'pos.noModifier': 'No {{name}}',
    'pos.kitchenNotePlaceholder': 'Kitchen note optional',
    'pos.kitchenNoteKeyboard': 'Tap keys for kitchen note',
    'pos.quantity': 'Quantity',
    'pos.addToCart': 'Add to Cart - {{total}}',
    'pos.counterPos': 'Counter POS',
    'pos.openCashRegister': 'Open Cash Register',
    'pos.openRegisterHelp': 'Start a cashier shift before taking counter orders, opening the drawer, or tracking cash movements.',
    'pos.startingCashInDrawer': 'Starting cash in drawer',
    'pos.openPosShift': 'Open POS Shift',
    'pos.registerConnection': 'Register connection',
    'pos.onlineReady': 'Online and ready to open',
    'pos.offlineCannotOpenShift': 'Reconnect before opening a new shift',
    'pos.noStartingCash': 'No float',
    'pos.openShiftRequiresOnline': 'Reconnect before opening a new POS shift.',
    'pos.invalidStartingCash': 'Starting cash must be zero or greater.',
    'pos.shiftOpenedSuccess': 'POS shift opened with {{amount}}.',
    'pos.existingShiftJoined': 'The existing open shift was joined. Its starting cash remains {{amount}}.',
    'pos.duplicateShiftsDetected': '{{count}} legacy open shifts were detected. The oldest was selected and Ops Command now shows a critical review item.',
    'pos.openShiftFailed': 'The POS shift could not be opened.',
    'pos.cashRegister': 'Cash Register',
    'pos.menuTiles': '{{count}} menu tiles',
    'pos.backToAdmin': 'Back to admin',
    'pos.registerTools': 'Register tools',
    'pos.printerReady': 'Printer ready',
    'pos.printerOffline': 'Printer offline',
    'pos.parkedLabel': 'Held Carts',
    'pos.orders': 'Work Queue',
    'pos.orderDetails': 'Customer',
    'pos.online': 'Online',
    'pos.offlinePos': 'Offline POS',
    'pos.localAgent': 'Local agent',
    'pos.agentOff': 'Agent off',
    'pos.cloudAgent': 'Cloud agent',
    'pos.cloudOff': 'Cloud off',
    'pos.pending': '{{count}} pending',
    'pos.expectedDrawer': 'Expected drawer',
    'pos.availableMenu': 'Available menu',
    'pos.offlineQueue': 'Offline queue',
    'pos.sync': 'Sync',
    'pos.drawer': 'Drawer',
    'pos.closeShiftAction': 'Close Shift',
    'pos.recentPos': 'Recent POS',
    'pos.noPosSalesYet': 'No POS sales yet.',
    'pos.pendingOrder': 'Pending',
    'pos.void': 'Void',
    'pos.park': 'Hold',
    'pos.space': 'Space',
    'pos.hide': 'Hide',
    'pos.printDrawerStatus': 'Print + Drawer Status',
    'pos.waitingPrintJobs': 'Waiting for print jobs to appear.',
    'pos.offlinePrintStatus': 'Offline order printed through the local agent when available. Cloud print status will appear after sync.',
    'pos.printKitchen': 'Kitchen',
    'pos.printReceipt': 'Receipt',
    'pos.printMarketplaceReceipt': 'Marketplace Receipt',
    'pos.printSalesReport': 'Sales Report',
    'pos.printDrawer': 'Drawer',
    'pos.printTest': 'Test',
    'pos.printCloseout': 'Closeout',
    'pos.cardReceiptCustomer': 'Customer copy',
    'pos.cardReceiptMerchant': 'Merchant copy',
    'pos.cardTwoReceiptsNow': 'Sending two card receipts now.',
    'pos.cardTwoReceiptsKitchenNow': 'Sending kitchen tickets and two card receipts now.',
    'pos.cardTwoReceiptsInstant': 'Two card receipts were sent instantly.',
    'pos.cardTwoReceiptsKitchenInstant': 'Kitchen tickets and two card receipts were sent instantly.',
    'pos.cardTwoReceiptsQueued': 'Two card receipt print jobs were queued.',
    'pos.cardTwoReceiptsKitchenQueued': 'Kitchen and two card receipt print jobs were queued.',
    'staff.title': 'Staff Management',
    'staff.subtitle': 'Create driver and call-center accounts, check access roles, and keep the restaurant team ready for service.',
    'staff.createCallCenterDesk': 'Create Call Center Desk',
    'staff.createDriver': 'Create Driver',
    'staff.totalStaff': 'Staff',
    'staff.admins': 'Admins',
    'staff.drivers': 'Drivers',
    'staff.callCenterDesks': 'Call Center',
    'staff.searchPlaceholder': 'Search staff by name or email...',
    'staff.allUsers': 'All Users',
    'staff.noStaff': 'No staff found',
    'staff.noStaffHelp': 'Try a different search or role filter.',
    'staff.email': 'Email',
    'staff.phone': 'Phone',
    'staff.noPhone': 'No phone saved',
    'staff.editStaff': 'Edit Staff',
    'staff.loadFailed': 'Failed to load staff',
    'staff.loadFailedDetail': 'Could not load the staff roster for this restaurant. Check the connection, then try again.',
    'staff.tryAgain': 'Try again',
    'staff.createTitle': 'Create Staff Account',
    'staff.editTitle': 'Edit Staff Member',
    'staff.fullName': 'Full Name',
    'staff.fullNamePlaceholder': 'Enter full name',
    'staff.emailPlaceholder': 'Enter email address',
    'staff.password': 'Password',
    'staff.passwordPlaceholder': 'Enter password',
    'staff.role': 'Role',
    'staff.deliveryDriver': 'Delivery Driver',
    'staff.callCenterDesk': 'Call Center Desk',
    'staff.callCenterRoleHelp': 'Focused Call Center screen with silent order alarms',
    'staff.driverRoleHelp': 'Delivery driver mobile access',
    'staff.adminConfigOnly': 'Restaurant admin accounts are created from ForgedOS restaurant configuration only.',
    'staff.cancel': 'Cancel',
    'staff.creating': 'Creating...',
    'staff.createAccount': 'Create Staff Account',
    'staff.saving': 'Saving...',
    'staff.saveChanges': 'Save Changes',
    'staff.emailLocked': 'Email cannot be changed',
    'staff.phoneNumber': 'Phone Number',
    'staff.phonePlaceholder': 'XXX-XXX-XXXX',
    'staff.roleLocked': 'Role cannot be changed',
    'staff.invalidPhone': 'Please enter a valid phone number (XXX-XXX-XXXX)',
    'phoneSetup.eyebrow': 'Phone setup',
    'phoneSetup.title': 'Connect caller ID without slowing down the counter',
    'phoneSetup.subtitle': 'Install one Local Agent on each Windows workstation, pair it once with its own code, then select the nearby phone extension inside the agent.',
    'phoneSetup.onlineAgents': 'Online agents',
    'phoneSetup.phoneReady': 'Phone ready',
    'phoneSetup.addWorkstation': 'Add a workstation',
    'phoneSetup.addWorkstationHelp': 'The same installer handles kitchen printing, receipts, drawers, and phone detection.',
    'phoneSetup.downloadWindows': 'Download for Windows',
    'phoneSetup.singlePhone': 'Single phone',
    'phoneSetup.singlePhoneHelp': 'One nearby phone and one Windows computer.',
    'phoneSetup.callCenter': 'Call center',
    'phoneSetup.callCenterHelp': 'Repeat setup for every workstation extension.',
    'phoneSetup.workstationName': 'Workstation name',
    'phoneSetup.workstationPlaceholder': 'Front counter or Call Center Desk 1',
    'phoneSetup.expectedExtension': 'Expected extension',
    'phoneSetup.optional': 'optional',
    'phoneSetup.extensionPlaceholder': 'For example: 101',
    'phoneSetup.generating': 'Generating...',
    'phoneSetup.generateCode': 'Generate six-digit pairing code',
    'phoneSetup.enterInAgent': 'Enter this in the Local Agent',
    'phoneSetup.expires': 'Expires {{date}} and works once.',
    'phoneSetup.copyCode': 'Copy code',
    'phoneSetup.codeCreated': 'Pairing code created. Enter it in the ForgedOS Local Agent on this workstation.',
    'phoneSetup.codeCopied': 'Pairing code copied.',
    'phoneSetup.createCodeError': 'Could not create Local Agent pairing code.',
    'phoneSetup.revokeConfirm': 'Revoke workstation',
    'phoneSetup.revokeTitle': 'Revoke Local Agent',
    'phoneSetup.revokeBody': 'Revoke {{name}}? This workstation will stop syncing phone and printing events until it is paired again.',
    'phoneSetup.cancel': 'Cancel',
    'phoneSetup.revoking': 'Revoking...',
    'phoneSetup.revoked': 'Local Agent revoked. Pair the workstation again when you are ready.',
    'phoneSetup.revokeError': 'Could not revoke Local Agent.',
    'phoneSetup.afterPairing': 'What happens after pairing',
    'phoneSetup.afterPairingHelp': 'Open the Phone tab in the Local Agent, click Detect phone connection, choose the nearby extension, then start a 60-second test and call the restaurant.',
    'phoneSetup.adminDesk': 'Admin stays available as Desk 1',
    'phoneSetup.adminDeskHelp': 'For a single-phone restaurant, the main admin account can use Call Center directly. For larger call centers, create Call Center Desk accounts from Staff and generate a separate pairing code for every Windows computer.',
    'phoneSetup.workstationHealth': 'Workstation health',
    'phoneSetup.workstationHealthHelp': 'Each call-center desk should show its own extension and recent heartbeat.',
    'phoneSetup.extensionShort': 'Ext {{extension}}',
    'phoneSetup.notSelected': 'not selected',
    'phoneSetup.driverPending': 'Phone driver pending',
    'phoneSetup.agentOnline': 'Agent online',
    'phoneSetup.agentOffline': 'Agent offline',
    'phoneSetup.phoneConnected': 'Phone connected',
    'phoneSetup.phoneNeedsSetup': 'Phone setup needed',
    'phoneSetup.version': 'Version',
    'phoneSetup.lastHeartbeat': 'Last heartbeat',
    'phoneSetup.lastPhoneEvent': 'Last phone event',
    'phoneSetup.mode': 'Mode',
    'phoneSetup.callCenterDesk': 'Call center desk',
    'phoneSetup.revoke': 'Revoke and re-pair',
    'phoneSetup.emptyTitle': 'No unified Local Agents paired yet',
    'phoneSetup.emptyHelp': 'Install the Windows agent, generate a code, and pair the first workstation.',
    'phoneSetup.guideReadonlyTitle': 'Read-only by design',
    'phoneSetup.guideReadonlyText': 'ForgedOS watches caller ID events. It cannot answer, dial, hold, or transfer calls.',
    'phoneSetup.guideMissingTitle': 'Missing phone lines?',
    'phoneSetup.guideMissingText': "Ask the phone technician to install the PBX manufacturer's Windows phone driver. NEC SL2100 uses its first-party CTI driver.",
    'phoneSetup.guideFallbackTitle': 'Advanced fallback',
    'phoneSetup.guideFallbackText': 'SMDR can be enabled in the Local Agent technician drawer, but some PBX systems report calls only after they finish.',
    'phoneSetup.never': 'Never',
    'phoneSetup.unknown': 'Unknown',
    'discount.eyebrow': 'Promotions',
    'discount.title': 'Discount Codes',
    'discount.subtitle': 'Create and pause customer-facing discount codes without leaving the admin.',
    'discount.create': 'Create Discount',
    'discount.totalCodes': 'Codes',
    'discount.activeCodes': 'Active',
    'discount.pausedCodes': 'Paused',
    'discount.expiringCodes': 'Expiring',
    'discount.searchPlaceholder': 'Search code or description...',
    'discount.all': 'All',
    'discount.active': 'Active',
    'discount.paused': 'Paused',
    'discount.noCodes': 'No discount codes yet',
    'discount.noCodesHelp': 'Create a code when you want to run a controlled promotion.',
    'discount.noMatches': 'No discount codes match this view',
    'discount.noMatchesHelp': 'Try a different search or status filter.',
    'discount.dismiss': 'Dismiss',
    'discount.discount': 'Discount',
    'discount.minOrder': 'Min Order',
    'discount.usage': 'Usage',
    'discount.expires': 'Expires',
    'discount.status': 'Status',
    'discount.pause': 'Pause discount',
    'discount.activate': 'Activate discount',
    'discount.inactive': 'Inactive',
    'discount.createTitle': 'Create Discount Code',
    'discount.code': 'Code',
    'discount.description': 'Description',
    'discount.type': 'Type',
    'discount.value': 'Value',
    'discount.percentage': 'Percentage',
    'discount.fixed': 'Fixed Amount',
    'discount.minOrderOptional': 'Min Order Amount (optional)',
    'discount.maxUsesOptional': 'Max Uses (optional)',
    'discount.expiryOptional': 'Expiry Date (optional)',
    'discount.activeOnCreate': 'Active',
    'discount.cancel': 'Cancel',
    'discount.creating': 'Creating...',
    'discount.createButton': 'Create',
    'discount.loadFailed': 'Failed to load discount codes. Please refresh or try again.',
    'discount.requiredError': 'Code, description, and discount value are required.',
    'discount.percentError': 'Percentage discounts cannot be higher than 100%.',
    'discount.createFailed': 'Failed to create discount code. Check the details and try again.',
    'discount.created': 'Discount code {{code}} was created.',
    'discount.statusUpdated': 'Discount code {{status}}.',
    'discount.statusFailed': 'Failed to update discount status. Please try again.',
  },
  ar: {
    'language.english': 'English',
    'language.arabic': 'العربية',
    'language.switchToEnglish': 'تبديل واجهة الإدارة إلى الإنجليزية',
    'language.switchToArabic': 'تبديل واجهة الإدارة إلى العربية',
    'nav.logout': 'تسجيل الخروج',
    'nav.dashboard': 'لوحة التحكم',
    'nav.role.admin': 'مدير',
    'nav.role.driver': 'سائق',
    'nav.role.call_center': 'مركز الاتصال',
    'admin.restaurantOperations': 'عمليات المطعم',
    'admin.dashboard': 'لوحة تحكم الإدارة',
    'admin.enableOrderAlarm': 'تفعيل تنبيه الطلبات',
    'admin.notificationsOn': 'التنبيهات مفعلة',
    'admin.notificationsOff': 'التنبيهات متوقفة',
    'admin.notificationSettings': 'إعدادات التنبيهات',
    'admin.dismiss': 'إغلاق',
    'admin.hideTools': 'إخفاء الأدوات',
    'admin.tools': 'الأدوات',
    'admin.opsCommand': 'مركز العمليات',
    'admin.setupWizard': 'معالج الإعداد',
    'admin.menuWizard': 'معالج القائمة',
    'admin.orders': 'الطلبات',
    'admin.pos': 'الكاشير',
    'admin.callCenter': 'مركز الاتصال',
    'admin.kitchen': 'المطبخ',
    'admin.drivers': 'السائقون',
    'admin.printing': 'الطباعة',
    'admin.menu': 'المنيو',
    'admin.reports': 'التقارير',
    'admin.phoneSetup': 'إعداد الهاتف',
    'admin.categories': 'التصنيفات',
    'admin.bulkImport': 'استيراد جماعي',
    'admin.importAddonsRemovables': 'استيراد الإضافات والإزالات',
    'admin.discounts': 'الخصومات',
    'admin.feeAdjustments': 'تعديلات الرسوم',
    'admin.deliveryFees': 'رسوم التوصيل',
    'category.title': 'تصنيفات المنيو',
    'category.subtitle': 'نظم منيو العملاء وصور التصنيفات والتصنيفات الفرعية والإضافات والإزالات الافتراضية.',
    'category.addCategory': 'إضافة تصنيف',
    'category.topLevel': 'تصنيفات رئيسية',
    'category.subcategories': 'تصنيفات فرعية',
    'category.withAddons': 'بها إضافات',
    'category.withRemovables': 'بها إزالات',
    'category.structure': 'هيكل التصنيفات',
    'category.structureHelp': 'حافظ على ترتيب واضح حتى يجد العملاء والموظفون الأصناف بسرعة.',
    'category.emptyTitle': 'لا توجد تصنيفات منيو بعد',
    'category.emptyHelp': 'أنشئ أول تصنيف لبدء تنظيم المنيو.',
    'category.addonsCount': '{{count}} إضافات',
    'category.removablesCount': '{{count}} إزالات',
    'category.subcategoriesCount': '{{count}} فرعية',
    'category.addSubcategory': 'إضافة تصنيف فرعي',
    'category.edit': 'تعديل التصنيف',
    'category.delete': 'حذف التصنيف',
    'category.saved': 'تم تحديث التصنيف.',
    'category.created': 'تم إنشاء التصنيف.',
    'category.deleted': 'تم حذف التصنيف.',
    'category.imageTypeError': 'يسمح بملفات الصور فقط.',
    'category.imageSizeError': 'حجم الصورة يتجاوز حد 10 ميجابايت.',
    'category.nameRequired': 'اسم التصنيف مطلوب.',
    'category.saveFailed': 'تعذر حفظ التصنيف.',
    'category.deleteFailed': 'تعذر حذف التصنيف.',
    'category.deleteTitle': 'حذف التصنيف',
    'category.deleteBody': 'حذف "{{name}}"؟ سيتأثر أيضاً {{count}} تصنيفات فرعية وقد تصبح بعض الأصناف بلا تصنيف.',
    'category.cancel': 'إلغاء',
    'category.deleting': 'جار الحذف...',
    'category.deleteConfirm': 'حذف التصنيف',
    'category.editTitle': 'تعديل التصنيف',
    'category.addSubcategoryTitle': 'إضافة تصنيف فرعي',
    'category.addTitle': 'إضافة تصنيف',
    'category.categoryName': 'اسم التصنيف',
    'category.subcategoryName': 'اسم التصنيف الفرعي',
    'category.namePlaceholder': 'مثال: شاورما، برجر',
    'category.parentCategory': 'التصنيف الرئيسي',
    'category.noParent': 'لا يوجد (تصنيف رئيسي)',
    'category.categoryImage': 'صورة التصنيف',
    'category.previewAlt': 'معاينة صورة التصنيف',
    'category.changeImage': 'تغيير الصورة',
    'category.uploadImage': 'رفع صورة',
    'category.imageHelp': 'تستخدم هذه الصورة للأصناف داخل التصنيف إذا لم تكن لها صورة خاصة.',
    'category.defaultAddonsHelp': 'ستكون هذه الإضافات متاحة لكل الأصناف داخل هذا التصنيف.',
    'category.defaultRemovablesHelp': 'ستكون هذه الإزالات متاحة لكل الأصناف داخل هذا التصنيف.',
    'category.saving': 'جار الحفظ...',
    'category.update': 'تحديث',
    'category.create': 'إنشاء',
    'addonsImport.title': 'استيراد الإضافات والإزالات',
    'addonsImport.subtitle': 'حمّل الإعدادات الافتراضية للتصنيفات من ملفات CSV حتى ترث الأصناف الإضافات والإزالات الصحيحة بدون إدخال يدوي.',
    'addonsImport.step.upload': 'رفع الملفات',
    'addonsImport.step.preview': 'معاينة',
    'addonsImport.step.importing': 'الاستيراد',
    'addonsImport.step.complete': 'اكتمال',
    'addonsImport.uploadTitle': 'رفع ملفات الإعدادات الافتراضية',
    'addonsImport.uploadSubtitle': 'اختر استيراد الإضافات أو الإزالات أو الاثنين معاً. يتم فحص الملفات قبل حفظ أي شيء.',
    'addonsImport.whatImport': 'ماذا تريد استيراده؟',
    'addonsImport.type.both': 'الإضافات والإزالات معاً',
    'addonsImport.type.addons': 'الإضافات فقط',
    'addonsImport.type.removables': 'الإزالات فقط',
    'addonsImport.addonsFile': 'ملف إضافات CSV',
    'addonsImport.removablesFile': 'ملف إزالات CSV',
    'addonsImport.addonsHelp': 'التصنيف واسم الإضافة والسعر.',
    'addonsImport.removablesHelp': 'التصنيف واسم المكوّن القابل للإزالة.',
    'addonsImport.dropAddons': 'اسحب ملف الإضافات CSV هنا',
    'addonsImport.dropRemovables': 'اسحب ملف الإزالات CSV هنا',
    'addonsImport.browse': 'اختيار ملف CSV',
    'addonsImport.expectedFormat': 'الصيغة المتوقعة',
    'addonsImport.errorsFound': 'تم العثور على أخطاء',
    'addonsImport.preview': 'معاينة الاستيراد',
    'addonsImport.previewTitle': 'راجع قبل الاستيراد',
    'addonsImport.previewSubtitle': '{{addons}} إضافات و {{removables}} إزالات جاهزة للاستيراد.',
    'addonsImport.reset': 'إعادة ضبط',
    'addonsImport.filesLoaded': 'الملفات المرفوعة',
    'addonsImport.addons': 'الإضافات',
    'addonsImport.removables': 'الإزالات',
    'addonsImport.addonsPreview': 'معاينة الإضافات',
    'addonsImport.removablesPreview': 'معاينة الإزالات',
    'addonsImport.categoryName': 'التصنيف',
    'addonsImport.addonName': 'الإضافة',
    'addonsImport.removableName': 'الإزالة',
    'addonsImport.price': 'السعر',
    'addonsImport.showingAddons': 'عرض {{shown}} من {{total}} إضافات',
    'addonsImport.showingRemovables': 'عرض {{shown}} من {{total}} إزالات',
    'addonsImport.import': 'استيراد الآن',
    'addonsImport.starting': 'بدء الاستيراد...',
    'addonsImport.progress': 'تمت معالجة {{current}} من {{total}}',
    'addonsImport.completeTitle': 'اكتمل الاستيراد',
    'addonsImport.completeBody': 'تمت إضافة {{addons}} إضافات و {{removables}} إزالات إلى {{categories}} تصنيفات. سترث الأصناف داخل هذه التصنيفات الإعدادات الجديدة.',
    'addonsImport.issuesTitle': 'تم الاستيراد مع ملاحظات',
    'addonsImport.issuesBody': 'تمت إضافة {{addons}} إضافات و {{removables}} إزالات إلى {{categories}} تصنيفات، لكن بعض الصفوف تحتاج مراجعة.',
    'addonsImport.importMore': 'استيراد المزيد',
    'addonsImport.addonsCsvError': 'يرجى رفع ملف CSV للإضافات.',
    'addonsImport.removablesCsvError': 'يرجى رفع ملف CSV للإزالات.',
    'addonsImport.addonsParseError': 'تعذر قراءة ملف الإضافات CSV.',
    'addonsImport.removablesParseError': 'تعذر قراءة ملف الإزالات CSV.',
    'addonsImport.importFailed': 'فشل الاستيراد.',
    'bulkImport.title': 'استيراد المنيو جماعياً',
    'bulkImport.subtitle': 'أنشئ أصناف المنيو من ملف CSV وراجع التصنيفات المكتشفة وجهز الإعدادات الافتراضية قبل الاستيراد.',
    'bulkImport.step.upload': 'رفع الملف',
    'bulkImport.step.preview': 'معاينة',
    'bulkImport.step.categories': 'التصنيفات',
    'bulkImport.step.importing': 'الاستيراد',
    'bulkImport.step.complete': 'اكتمال',
    'bulkImport.uploadTitle': 'رفع ملف المنيو CSV',
    'bulkImport.uploadSubtitle': 'استورد عدة أصناف منيو دفعة واحدة. يتم فحص الملف أولاً حتى تراجع الأصناف والتصنيفات قبل الحفظ.',
    'bulkImport.dropTitle': 'اسحب ملف المنيو CSV هنا',
    'bulkImport.dropHelp': 'استخدم صيغة CSV تحتوي اسم الصنف والسعر والتصنيف والتصنيف الفرعي اختياريًا.',
    'bulkImport.selectFile': 'اختيار ملف CSV',
    'bulkImport.csvFormat': 'صيغة CSV',
    'bulkImport.csvColumns': 'يجب أن يحتوي الملف على هذه الأعمدة:',
    'bulkImport.requiredColumns': 'الأعمدة المطلوبة: name, price, category. الاختيارية: description, subcategory.',
    'bulkImport.addonsNote': 'بعد استيراد الأصناف، استخدم استيراد الإضافات والإزالات لتطبيق إعدادات التصنيفات دفعة واحدة.',
    'bulkImport.errorsFound': 'تم العثور على أخطاء',
    'bulkImport.rowsSkipped': '{{count}} صفوف بها ملاحظات وتم تخطيها',
    'bulkImport.previewTitle': 'معاينة استيراد المنيو',
    'bulkImport.previewSubtitle': 'تم العثور على {{count}} أصناف في {{file}}.',
    'bulkImport.reset': 'إعادة ضبط',
    'bulkImport.menuItems': 'أصناف المنيو',
    'bulkImport.categories': 'التصنيفات',
    'bulkImport.subcategories': 'التصنيفات الفرعية',
    'bulkImport.newCategories': 'سيتم إنشاء {{count}} تصنيفات جديدة',
    'bulkImport.newCategoriesHelp': 'يمكنك تعيين الصور والإضافات والإزالات الافتراضية لكل تصنيف جديد قبل الاستيراد.',
    'bulkImport.name': 'الاسم',
    'bulkImport.category': 'التصنيف',
    'bulkImport.subcategory': 'التصنيف الفرعي',
    'bulkImport.price': 'السعر',
    'bulkImport.showingItems': 'عرض {{shown}} من {{total}} أصناف',
    'bulkImport.setupCategories': 'تجهيز التصنيفات',
    'bulkImport.importItems': 'استيراد الأصناف',
    'bulkImport.setupSubcategory': 'تجهيز التصنيف الفرعي',
    'bulkImport.setupCategory': 'تجهيز التصنيف',
    'bulkImport.categoryStep': 'الخطوة {{current}} من {{total}}',
    'bulkImport.previewAlt': 'معاينة صورة التصنيف',
    'bulkImport.underParent': 'ضمن: {{parent}}',
    'bulkImport.itemCount': '{{count}} أصناف',
    'bulkImport.categoryImage': 'صورة التصنيف',
    'bulkImport.changeImage': 'تغيير الصورة',
    'bulkImport.uploadImage': 'رفع صورة',
    'bulkImport.imageHelp': 'ستستخدم هذه الصورة لكل الأصناف داخل هذا التصنيف.',
    'bulkImport.addonsHelp': 'ستكون هذه الإضافات متاحة لكل الأصناف وعددها {{count}}.',
    'bulkImport.removablesHelp': 'يمكن للعملاء إزالة هذه المكونات من أي من الأصناف وعددها {{count}}.',
    'bulkImport.back': 'رجوع',
    'bulkImport.nextCategory': 'التصنيف التالي',
    'bulkImport.startImport': 'بدء الاستيراد',
    'bulkImport.starting': 'بدء الاستيراد...',
    'bulkImport.progress': 'تمت معالجة {{current}} من {{total}}',
    'bulkImport.completeTitle': 'اكتمل الاستيراد',
    'bulkImport.completeBody': 'تم استيراد {{items}} أصناف وإنشاء {{categories}} تصنيفات.',
    'bulkImport.issuesTitle': 'تم الاستيراد مع ملاحظات',
    'bulkImport.issuesBody': 'تم استيراد {{items}} أصناف، لكن بعض الصفوف تحتاج مراجعة.',
    'bulkImport.importMore': 'استيراد المزيد',
    'bulkImport.csvError': 'يرجى رفع ملف CSV.',
    'bulkImport.parseError': 'تعذر قراءة ملف CSV.',
    'bulkImport.importFailed': 'فشل الاستيراد.',
    'notificationSettings.title': 'تنبيه الطلبات',
    'notificationSettings.subtitle': 'تحكم بتنبيه الطلبات العالي المستخدم في لوحة الإدارة عند وصول طلبات جديدة.',
    'notificationSettings.close': 'إغلاق إعدادات التنبيهات',
    'notificationSettings.testSuccess': 'تم تشغيل تنبيه تجريبي. إذا لم تسمعه، تحقق من صوت الجهاز وصلاحيات الصوت في المتصفح.',
    'notificationSettings.testFailed': 'تعذر تشغيل التنبيه. اضغط داخل صفحة الإدارة أولاً، وتحقق من صوت الجهاز، ثم حاول مرة أخرى.',
    'notificationSettings.alarmTitle': 'تنبيه طلبات عالي',
    'notificationSettings.alarmHelp': 'كرر تنبيهاً عالياً حتى يتم فتح الطلب أو تأكيده.',
    'notificationSettings.disableAlarm': 'إيقاف تنبيه الطلبات',
    'notificationSettings.enableAlarm': 'تفعيل تنبيه الطلبات',
    'notificationSettings.volume': 'مستوى الصوت',
    'notificationSettings.tipTitle': 'أبقِ هذه الصفحة جاهزة',
    'notificationSettings.tipBody': 'بعد تسجيل الدخول، اضغط تفعيل الصوت أو جرب التنبيه مرة واحدة. ستصدر الطلبات الجديدة صوتاً عالياً حتى يتم فتحها أو إغلاقها.',
    'notificationSettings.testing': 'جار الاختبار...',
    'notificationSettings.testAlarm': 'اختبار التنبيه',
    'notificationSettings.done': 'تم',
    'branding.title': 'هوية المطعم',
    'branding.subtitle': 'ارفع الشعار الذي يظهر للعملاء والموظفين في تجربة الطلب والوصولات ولوحات الإدارة.',
    'branding.currentLogo': 'الشعار الحالي',
    'branding.logoAlt': 'شعار المطعم',
    'branding.noLogo': 'لم يتم رفع شعار بعد',
    'branding.previewHelp': 'استخدم شعاراً واضحاً بتباين جيد حتى يظهر بشكل مناسب على الوصولات وشاشات العملاء.',
    'branding.uploadTitle': 'رفع شعار جديد',
    'branding.uploadHelp': 'يتم حفظ الشعار الجديد فور رفعه ويصبح علامة المطعم النشطة.',
    'branding.dropTitle': 'اضغط للرفع أو اسحب صورة هنا',
    'branding.fileRules': 'PNG أو JPG حتى 2MB',
    'branding.chooseFile': 'اختيار ملف',
    'branding.uploading': 'جار رفع الشعار...',
    'branding.tipTitle': 'نصيحة للهوية',
    'branding.tipBody': 'الشعارات المربعة أو الأفقية تعمل بشكل أفضل. تجنب النصوص الصغيرة لأنها قد لا تظهر بوضوح على الوصولات والشاشات الصغيرة.',
    'branding.imageTypeError': 'يرجى اختيار ملف صورة.',
    'branding.imageSizeError': 'يجب أن يكون حجم الصورة أقل من 2MB.',
    'branding.uploaded': 'تم رفع الشعار بنجاح.',
    'branding.uploadFailed': 'تعذر رفع الشعار. حاول مرة أخرى.',
    'feeSettings.title': 'إعدادات رسوم التوصيل',
    'feeSettings.subtitle': 'تحكم بتسعير التوصيل وحدود المسافة وحد التوصيل المجاني، وراجع ما سيُحسب على العميل قبل الحفظ.',
    'feeSettings.loadFailed': 'تعذر تحميل إعدادات رسوم التوصيل.',
    'feeSettings.saved': 'تم حفظ إعدادات رسوم التوصيل. تسري التغييرات فوراً.',
    'feeSettings.saveFailed': 'تعذر حفظ إعدادات رسوم التوصيل.',
    'feeSettings.save': 'حفظ الإعدادات',
    'feeSettings.saving': 'جار الحفظ...',
    'feeSettings.reset': 'إرجاع التغييرات',
    'feeSettings.unsaved': 'لديك تغييرات غير محفوظة في رسوم التوصيل. راجع المعاينة واحفظ عندما تكون جاهزاً.',
    'feeSettings.perMile': 'لكل ميل',
    'feeSettings.deliveryRange': 'نطاق التوصيل',
    'feeSettings.freeOver': 'مجاني فوق',
    'feeSettings.baseRule': 'قاعدة الرسم الثابت',
    'feeSettings.disabled': 'غير مفعل',
    'feeSettings.miles': 'ميل',
    'feeSettings.feeConfig': 'إعدادات الرسوم',
    'feeSettings.feeConfigHelp': 'اضبط قواعد التسعير الأساسية التي تظهر للعميل أثناء طلب التوصيل.',
    'feeSettings.ratePerMile': 'السعر لكل ميل',
    'feeSettings.rateHelp': 'رسوم المسافة مضروبة بعدد أميال التوصيل.',
    'feeSettings.freeThreshold': 'حد التوصيل المجاني',
    'feeSettings.freeThresholdHelp': 'الطلبات التي تصل إلى هذا المجموع أو تتجاوزه تحصل على توصيل مجاني.',
    'feeSettings.baseEnabled': 'تفعيل رسم توصيل ثابت',
    'feeSettings.baseEnabledHelp': 'يضيف رسماً ثابتاً للتوصيلات داخل نطاق المسافة أدناه.',
    'feeSettings.baseFee': 'رسم التوصيل الثابت',
    'feeSettings.baseFeeHelp': 'مبلغ ثابت يضاف قبل رسوم الميل عندما تنطبق قاعدة الرسم الثابت.',
    'feeSettings.baseMin': 'أقل مسافة للرسم الثابت',
    'feeSettings.baseMinHelp': 'حد أدنى اختياري للمسافة قبل بدء تطبيق الرسم الثابت.',
    'feeSettings.baseMax': 'أقصى مسافة للرسم الثابت',
    'feeSettings.baseMaxHelp': 'أعلى مسافة يبقى فيها الرسم الثابت مطبقاً.',
    'feeSettings.noMinimum': 'بدون حد أدنى',
    'feeSettings.distanceLimits': 'حدود مسافة التوصيل',
    'feeSettings.distanceLimitsHelp': 'حدد نطاق المسافة المدعوم لتوفر التوصيل.',
    'feeSettings.minDistance': 'أقل مسافة',
    'feeSettings.minDistanceHelp': 'أقصر مسافة مسموحة لطلبات التوصيل.',
    'feeSettings.maxDistance': 'أقصى مسافة',
    'feeSettings.maxDistanceHelp': 'أبعد مسافة يوصل لها المطعم.',
    'feeSettings.validationTitle': 'فحص الجاهزية',
    'feeSettings.validationHelp': 'أصلح هذه القواعد قبل الحفظ.',
    'feeSettings.readyHelp': 'القواعد الحالية صالحة وجاهزة للحفظ.',
    'feeSettings.readyTitle': 'قواعد رسوم التوصيل تبدو جيدة.',
    'feeSettings.livePreview': 'معاينة الرسوم مباشرة',
    'feeSettings.previewHelp': 'اختبر ما سيراه العملاء عند مسافات توصيل شائعة.',
    'feeSettings.previewOrderTotal': 'مجموع الطلب للمعاينة',
    'feeSettings.previewOrderTotalHelp': 'غيّر هذا الرقم لاختبار سلوك التوصيل المجاني.',
    'feeSettings.previewDistance': 'مسافة المعاينة',
    'feeSettings.freeDeliveryDetail': 'توصيل مجاني لأن المجموع لا يقل عن {{threshold}}.',
    'feeSettings.baseApplies': 'الرسم الثابت مطبق: {{amount}}.',
    'feeSettings.baseSkipped': 'الرسم الثابت لا ينطبق على هذه المسافة.',
    'feeSettings.distanceFeeDetail': '{{distance}} ميل x {{rate}} لكل ميل = {{amount}}.',
    'feeSettings.noDeliveryCharge': 'لا توجد رسوم توصيل في هذه المعاينة.',
    'feeSettings.rateNegative': 'السعر لكل ميل لا يمكن أن يكون سالباً.',
    'feeSettings.baseNegative': 'رسم التوصيل الثابت لا يمكن أن يكون سالباً.',
    'feeSettings.baseMaxNegative': 'أقصى مسافة للرسم الثابت لا يمكن أن تكون سالبة.',
    'feeSettings.baseMinNegative': 'أقل مسافة للرسم الثابت لا يمكن أن تكون سالبة.',
    'feeSettings.baseRangeInvalid': 'أقل مسافة للرسم الثابت يجب أن تكون أقل من أقصى مسافة.',
    'feeSettings.minNegative': 'أقل مسافة للتوصيل لا يمكن أن تكون سالبة.',
    'feeSettings.maxInvalid': 'أقصى مسافة للتوصيل يجب أن تكون أكبر من أقل مسافة.',
    'feeSettings.freeNegative': 'حد التوصيل المجاني لا يمكن أن يكون سالباً.',
    'feeSettings.locationPresets': 'مناطق توصيل مركز الاتصال',
    'feeSettings.locationPresetsHelp': 'أنشئ مناطق توصيل سريعة برسوم ثابتة لطلبات الهاتف. يمكن للموظف اختيار مخصص عند الحاجة.',
    'feeSettings.noLocationPresets': 'لا توجد مناطق توصيل محفوظة بعد.',
    'feeSettings.presetName': 'اسم المنطقة',
    'feeSettings.presetNameAr': 'الاسم بالعربية',
    'feeSettings.presetFee': 'الرسوم',
    'feeSettings.sort': 'الترتيب',
    'feeSettings.active': 'مفعلة',
    'feeSettings.inactive': 'معطلة',
    'feeSettings.addLocationPreset': 'إضافة منطقة توصيل',
    'feeAdjustments.title': 'تعديلات رسوم التوصيل',
    'feeAdjustments.subtitle': 'راجع تغييرات رسوم التوصيل من السائقين أو الإدارة بمسار موافقة واضح قبل تحديث إجمالي الطلب.',
    'feeAdjustments.visible': 'المعروض',
    'feeAdjustments.pending': 'بانتظار',
    'feeAdjustments.credits': 'خصومات',
    'feeAdjustments.net': 'الصافي',
    'feeAdjustments.notAvailable': 'غير متاح',
    'feeAdjustments.signInRequired': 'يجب تسجيل الدخول كمسؤول للموافقة على التعديلات أو رفضها.',
    'feeAdjustments.approvedNotice': 'تم اعتماد تعديل الطلب #{{order}} وتطبيقه. يبقى المبلغ مستحقاً حتى تسجيل التحصيل أو الاسترداد الفعلي.',
    'feeAdjustments.rejectedNotice': 'تم رفض تعديل الطلب #{{order}}.',
    'feeAdjustments.appliedNotice': 'تم تطبيق تعديل الطلب #{{order}} على إجمالي الطلب.',
    'feeAdjustments.actionFailed': 'تعذر إكمال إجراء التعديل. حاول مرة أخرى.',
    'feeAdjustments.loadErrorTitle': 'تعذر تحميل تعديلات الرسوم',
    'feeAdjustments.emptyTitle': 'لا توجد تعديلات رسوم تحتاج متابعة',
    'feeAdjustments.emptyHelp': 'لا توجد تعديلات رسوم توصيل بحالة {{status}} حالياً.',
    'feeAdjustments.anyStatus': 'أي حالة',
    'feeAdjustments.order': 'طلب',
    'feeAdjustments.adjustment': 'التعديل',
    'feeAdjustments.feeImpact': 'أثر الرسوم',
    'feeAdjustments.originalFee': 'الرسوم الأصلية',
    'feeAdjustments.adjustedFee': 'الرسوم المعدلة',
    'feeAdjustments.reportedBy': 'أبلغ عنه',
    'feeAdjustments.locationDetails': 'تفاصيل اختلاف الموقع',
    'feeAdjustments.lockedDistance': 'مسافة GPS المثبتة',
    'feeAdjustments.actualDistance': 'مسافة التوصيل الفعلية',
    'feeAdjustments.difference': 'الفرق',
    'feeAdjustments.rejectionReason': 'سبب الرفض',
    'feeAdjustments.appliedOn': 'تم التطبيق في',
    'feeAdjustments.approve': 'اعتماد وتطبيق',
    'feeAdjustments.reject': 'رفض',
    'feeAdjustments.apply': 'تطبيق على الطلب',
    'feeAdjustments.approving': 'جار الموافقة...',
    'feeAdjustments.rejecting': 'جار الرفض...',
    'feeAdjustments.applying': 'جار التطبيق...',
    'feeAdjustments.cancel': 'إلغاء',
    'feeAdjustments.approveTitle': 'الموافقة على تعديل الرسوم',
    'feeAdjustments.approveBody': 'هل تريد الموافقة على {{amount}} للطلب #{{order}}؟ سيتم تعليمه كموافق عليه، ولن يتغير إجمالي الطلب حتى يتم تطبيقه.',
    'feeAdjustments.approveConfirm': 'الموافقة على التعديل',
    'feeAdjustments.approveProcessing': 'جار الموافقة...',
    'feeAdjustments.rejectTitle': 'رفض تعديل الرسوم',
    'feeAdjustments.rejectBody': 'هل تريد رفض {{amount}} للطلب #{{order}}؟ أضف سبباً قصيراً حتى يفهم الفريق القرار.',
    'feeAdjustments.rejectPlaceholder': 'سبب الرفض، اختياري',
    'feeAdjustments.rejectConfirm': 'رفض التعديل',
    'feeAdjustments.rejectProcessing': 'جار الرفض...',
    'feeAdjustments.applyTitle': 'تطبيق على إجمالي الطلب',
    'feeAdjustments.applyBody': 'هل تريد تطبيق {{amount}} على الطلب #{{order}} الآن؟ سيؤدي ذلك إلى تحديث إجمالي الطلب وتعليم التعديل كمطبق.',
    'feeAdjustments.applyConfirm': 'تطبيق التعديل',
    'feeAdjustments.applyProcessing': 'جار التطبيق...',
    'feeAdjustments.status.all': 'الكل',
    'feeAdjustments.status.pending': 'بانتظار',
    'feeAdjustments.status.approved': 'موافق عليه',
    'feeAdjustments.status.rejected': 'مرفوض',
    'feeAdjustments.status.applied': 'مطبق',
    'feeAdjustments.reason.location_mismatch': 'اختلاف الموقع',
    'feeAdjustments.reason.driver_extra_distance': 'مسافة إضافية',
    'feeAdjustments.reason.admin_correction': 'تصحيح إداري',
    'feeAdjustments.reason.customer_complaint': 'شكوى عميل',
    'admin.staff': 'الموظفون',
    'admin.customers': 'العملاء',
    'admin.trustScores': 'درجات الثقة',
    'trustScores.title': 'درجات ثقة العملاء',
    'trustScores.subtitle': 'راقب اختلافات الموقع والشكاوى والقيود التلقائية على العملاء قبل أن تؤثر السلوكيات عالية المخاطر على التشغيل.',
    'trustScores.refresh': 'تحديث',
    'trustScores.loadFailed': 'تعذر تحميل درجات ثقة العملاء.',
    'trustScores.loadErrorTitle': 'تعذر تحميل درجات الثقة',
    'trustScores.tryAgain': 'حاول مرة أخرى',
    'trustScores.customersShown': 'العملاء المعروضون',
    'trustScores.avgScore': 'متوسط الدرجة',
    'trustScores.critical': 'حرج',
    'trustScores.manualApproval': 'موافقة يدوية',
    'trustScores.upfrontPayment': 'دفع مسبق',
    'trustScores.allCustomers': 'كل العملاء',
    'trustScores.belowScore': 'أقل من {{score}}',
    'trustScores.sort.score': 'الأقل ثقة',
    'trustScores.sort.mismatches': 'اختلافات الموقع',
    'trustScores.sort.complaints': 'الشكاوى',
    'trustScores.sort.orders': 'الطلبات',
    'trustScores.emptyTitle': 'لا يوجد عملاء عالي الخطورة في هذا العرض',
    'trustScores.emptyHelp': 'لا يوجد عملاء يطابقون {{threshold}} حالياً.',
    'trustScores.customer': 'العميل',
    'trustScores.trustScore': 'درجة الثقة',
    'trustScores.mismatches': 'اختلافات الموقع',
    'trustScores.complaints': 'الشكاوى',
    'trustScores.totalOrders': 'إجمالي الطلبات',
    'trustScores.flags': 'العلامات',
    'trustScores.restrictions': 'القيود',
    'trustScores.none': 'لا يوجد',
    'trustScores.restriction.manualApproval': 'يتطلب موافقة يدوية',
    'trustScores.restriction.upfrontPayment': 'يتطلب دفعاً مسبقاً',
    'trustScores.restriction.maxDistance': 'أقصى مسافة',
    'trustScores.lastUpdated': 'آخر تحديث',
    'trustScores.infoTitle': 'كيف تعمل درجات الثقة',
    'trustScores.infoRange': 'تتراوح الدرجات من 0 إلى 100، و100 تعني سلوك العميل الأكثر موثوقية.',
    'trustScores.infoSignals': 'اختلافات الموقع وشكاوى السائقين وتاريخ الطلبات وعلامات المخاطر تخفض الدرجة تلقائياً.',
    'trustScores.infoRestrictions': 'تطبق قيود مثل الموافقة اليدوية أو الدفع المسبق عندما تتجاوز المخاطر الحدود المحددة.',
    'trustScores.infoReview': 'استخدم هذه الشاشة لاكتشاف الحسابات التي تحتاج مراجعة قبل أن تسبب احتكاكاً في التوصيل أو الدفع.',
    'admin.branding': 'الهوية',
    'admin.reviews': 'التقييمات',
    'admin.inventory': 'المخزون',
    'printing.eyebrow': 'طباعة المطبخ',
    'printing.title': 'تذاكر المحطات',
    'printing.subtitle': 'وجّه أصناف الطلب إلى طابعات المطبخ أو الكاشير أو البار أو المحطات المخصصة عند بدء تحضير الطلب.',
    'printing.stations': 'المحطات',
    'printing.agents': 'الوكلاء',
    'printing.unmapped': 'غير موجّه',
    'printing.failed': 'فاشلة',
    'printing.enabledStations': 'المحطات المفعلة',
    'printing.enabledStationsReady': '{{count}} محطة جاهزة',
    'printing.createStationFirst': 'أنشئ محطة مطبخ أو كاشير واحدة على الأقل.',
    'printing.localAgentOnline': 'الوكيل المحلي متصل',
    'printing.agentsOnline': '{{count}} وكيل متصل',
    'printing.pairAgentBeforeService': 'اربط أو افتح وكيل ويندوز المحلي قبل الخدمة.',
    'printing.categoryRouting': 'توجيه التصنيفات',
    'printing.allCategoriesMapped': 'كل التصنيفات الرئيسية موجهة.',
    'printing.categoriesNeedRoute': '{{count}} تصنيف ما زال يحتاج مسار طابعة.',
    'printing.fallbackStation': 'محطة احتياطية',
    'printing.fallbackAvailable': 'المحطة الاحتياطية متاحة للأصناف الجديدة أو غير الموجهة.',
    'printing.markFallback': 'عيّن طابعة موثوقة واحدة كاحتياطية.',
    'printing.failedJobs': 'مهام فاشلة',
    'printing.noFailedJobs': 'لا توجد مهام طباعة فاشلة بانتظار الإجراء.',
    'printing.failedJobsNeedRetry': '{{count}} مهمة فاشلة تحتاج إعادة محاولة أو إلغاء.',
    'printing.registerReadiness': 'جاهزية الكاشير',
    'printing.readyTitle': 'الطباعة جاهزة للخدمة',
    'printing.attentionTitle': 'الطباعة تحتاج انتباهاً قبل الذروة',
    'printing.readinessHelp': 'تغطي هذه الفحوص إعداد المحطات، حالة الوكيل المحلي، التوجيه، الطابعة الاحتياطية، والمهام العالقة.',
    'printing.fixFirst': 'ابدأ بهذا: {{detail}}',
    'printing.retryFailed': 'إعادة الفاشلة ({{count}})',
    'printing.findingPrinters': 'جار البحث عن الطابعات...',
    'printing.findPrinters': 'البحث عن الطابعات',
    'printing.generating': 'جار الإنشاء...',
    'printing.pairAgent': 'ربط وكيل',
    'printing.printStations': 'محطات الطباعة',
    'printing.printStationsHelp': 'استخدم عناوين IP ثابتة للطابعات كلما أمكن.',
    'printing.noStations': 'لا توجد محطات بعد',
    'printing.noStationsHelp': 'أنشئ محطة لكل قسم مطبخ أو مسار طابعة.',
    'printing.routingNeedsStation': 'أنشئ محطة أولاً',
    'printing.routingNeedsStationHelp': 'يتوفر التوجيه بعد وجود محطة مفعلة واحدة على الأقل.',
    'printing.noPairedAgents': 'لا يوجد وكلاء مرتبطون',
    'printing.noPairedAgentsHelp': 'أنشئ كوداً، افتح الوكيل المحلي على ويندوز، ثم أدخل الكود.',
    'printing.noJobs': 'لا توجد مهام بعد',
    'printing.noJobsHelp': 'ستظهر التذاكر هنا بعد بدء التحضير أو طباعة اختبار.',
    'printing.stationNameWindowsRequired': 'اسم المحطة واسم طابعة ويندوز مطلوبان.',
    'printing.stationNameIpRequired': 'اسم المحطة وعنوان IP للطابعة مطلوبان.',
    'printing.stationSaved': 'تم حفظ محطة الطباعة.',
    'printing.stationSaveFailed': 'تعذر حفظ محطة الطباعة.',
    'printing.stationUpdated': 'تم تحديث محطة الطباعة.',
    'printing.stationUpdateFailed': 'تعذر تحديث المحطة.',
    'printing.routeSaved': 'تم حفظ مسار التصنيف.',
    'printing.routeSaveFailed': 'تعذر حفظ قاعدة التوجيه.',
    'printing.noApiUrl': 'لا يوجد رابط API لـ ForgedOS مهيأ لهذا الإصدار.',
    'printing.signInRequired': 'يجب تسجيل الدخول كمدير مطعم.',
    'printing.pairingCreateFailed': 'تعذر إنشاء كود الربط.',
    'printing.pairingCreated': 'تم إنشاء كود الربط. أدخله في الوكيل المحلي خلال 10 دقائق.',
    'printing.pairingErrorWithUrls': '{{message}} تمت تجربة روابط API: {{details}}',
    'printing.pairingCopied': 'تم نسخ كود الربط.',
    'printing.askingLocalPrinters': 'يتم طلب أسماء طابعات ويندوز من وكيل ForgedOS المحلي...',
    'printing.checkingLocalPrinters': 'يتم فحص طابعات ويندوز على هذا الجهاز عبر الوكيل المحلي.',
    'printing.foundWindowsPrinters': 'تم العثور على {{count}} طابعة ويندوز على هذا الجهاز.',
    'printing.noWindowsPrinters': 'لم يتم العثور على طابعات ويندوز. ثبّت طابعة USB في ويندوز أولاً.',
    'printing.localAgentTimeout': 'لم يستجب وكيل ForgedOS المحلي. افتح الوكيل على نفس جهاز ويندوز وحاول مرة أخرى.',
    'printing.localPrintersLoadFailed': 'تعذر تحميل الطابعات المحلية.',
    'printing.selectedPrinter': 'تم اختيار {{name}}. احفظ المحطة عندما تكون جاهزاً.',
    'printing.retryQueued': 'تمت إضافة إعادة محاولة لـ {{count}} مهمة طباعة فاشلة.',
    'printing.retryPrintJobsFailed': 'تعذر إعادة محاولة مهام الطباعة الفاشلة.',
    'printing.stationName': 'اسم المحطة',
    'printing.stationNamePlaceholder': 'محطة الشاورما',
    'printing.stationType': 'النوع',
    'printing.typeKitchen': 'مطبخ',
    'printing.typeCashier': 'كاشير',
    'printing.typeBar': 'بار',
    'printing.typeCustom': 'مخصص',
    'printing.printerConnection': 'اتصال الطابعة',
    'printing.networkPrinter': 'طابعة شبكة IP',
    'printing.windowsUsbPrinter': 'طابعة ويندوز / USB',
    'printing.windowsPrinterName': 'اسم طابعة ويندوز',
    'printing.windowsPrinterHelp': 'افتح الوكيل المحلي على جهاز كاشير ويندوز، اختر USB وطابعات ويندوز، ثم انسخ اسم الطابعة الصحيح هنا.',
    'printing.printerIp': 'عنوان IP للطابعة',
    'printing.port': 'المنفذ',
    'printing.enabled': 'مفعلة',
    'printing.fallback': 'محطة احتياطية',
    'printing.saveStation': 'حفظ المحطة',
    'printing.fallbackBadge': 'احتياطية',
    'printing.disabledBadge': 'معطلة',
    'printing.test': 'اختبار',
    'printing.disable': 'تعطيل',
    'printing.enable': 'تفعيل',
    'printing.archive': 'أرشفة',
    'printing.categoryRoutingHelp': 'عيّن التصنيفات الرئيسية. الأصناف داخل التصنيفات الفرعية تتبع طابعة التصنيف الأب.',
    'printing.includesSubcategories': 'يتضمن {{count}} تصنيف فرعي',
    'printing.topLevelCategory': 'تصنيف رئيسي',
    'printing.unmappedOption': 'غير موجّه',
    'printing.localAgentTitle': 'وكيل ForgedOS المحلي',
    'printing.localAgentHelp': 'وكيل ويندوز واحد يدير الطباعة والأدراج وكشف الهاتف الاختياري.',
    'printing.downloadWindowsAgent': 'تحميل وكيل ويندوز',
    'printing.generateCode': 'إنشاء كود',
    'printing.windowsSetupFlow': 'خطوات إعداد ويندوز',
    'printing.windowsSetupHelp': 'حمّل المثبت على جهاز ويندوز في المطعم، افتح الوكيل المحلي، ثم أدخل كود الربط من هذه الشاشة.',
    'printing.localUsbTitle': 'طابعات USB / ويندوز المحلية',
    'printing.localUsbHelp': 'استخدم هذا على جهاز كاشير ويندوز لسحب أسماء الطابعات مباشرة من الوكيل المحلي.',
    'printing.finding': 'جار البحث...',
    'printing.findLocalPrinters': 'البحث عن الطابعات المحلية',
    'printing.defaultPrinter': 'افتراضية',
    'printing.copyName': 'نسخ الاسم',
    'printing.useForStation': 'استخدام للمحطة',
    'printing.pairingCode': 'كود الربط',
    'printing.expiresOnce': 'ينتهي خلال 10 دقائق ويستخدم مرة واحدة.',
    'printing.copyCode': 'نسخ الكود',
    'printing.lastHeartbeat': 'آخر نبض',
    'printing.online': 'متصل',
    'printing.offline': 'غير متصل',
    'printing.unifiedAgentPaired': 'الوكيل المحلي الموحد مرتبط على {{count}} جهاز عمل. يمكن إبقاء تثبيتات وكيل الطباعة الحالية أثناء التجربة ثم إزالتها بعد نجاح فحوص الطباعة.',
    'printing.recentPrintJobs': 'آخر مهام الطباعة',
    'printing.recentPrintJobsHelp': 'يمكن إعادة محاولة المهام الفاشلة أو العالقة من هنا.',
    'printing.itemsCount': '{{count}} صنف',
    'printing.retry': 'إعادة المحاولة',
    'printing.cancel': 'إلغاء',
    'reviews.title': 'تقييمات العملاء',
    'reviews.subtitle': 'تابع ملاحظات العملاء والتقييمات الموثقة والصور وإشارات التقييم المنخفض في لوحة سمعة واضحة.',
    'reviews.refresh': 'تحديث',
    'reviews.loadFailed': 'تعذر تحميل التقييمات. حاول مرة أخرى.',
    'reviews.loadErrorTitle': 'تعذر تحميل التقييمات',
    'reviews.tryAgain': 'حاول مرة أخرى',
    'reviews.deleted': 'تم حذف التقييم.',
    'reviews.deleteFailed': 'تعذر حذف التقييم. حاول مرة أخرى.',
    'reviews.totalReviews': 'إجمالي التقييمات',
    'reviews.averageRating': 'متوسط التقييم',
    'reviews.deliveryRating': 'تقييم التوصيل',
    'reviews.verified': 'موثق',
    'reviews.lowRatings': 'تقييمات منخفضة',
    'reviews.ratingDistribution': 'توزيع التقييمات',
    'reviews.photoReviews': 'تقييمات مع صور',
    'reviews.photoReviewsHelp': 'التقييمات التي تحتوي صوراً من العملاء مفيدة لفحص الجودة والمتابعة التسويقية.',
    'reviews.filter.all': 'كل التقييمات',
    'reviews.filter.low': 'تقييمات منخفضة',
    'reviews.filter.high': 'تقييمات عالية',
    'reviews.filter.photos': 'مع صور',
    'reviews.filter.unverified': 'غير موثق',
    'reviews.sort.newest': 'الأحدث',
    'reviews.sort.lowest': 'الأقل تقييماً',
    'reviews.sort.highest': 'الأعلى تقييماً',
    'reviews.sort.helpful': 'الأكثر فائدة',
    'reviews.emptyTitle': 'لا توجد تقييمات تطابق هذا العرض',
    'reviews.emptyHelp': 'ستظهر تقييمات العملاء هنا عندما تبدأ الملاحظات بالوصول.',
    'reviews.verifiedBadge': 'موثق',
    'reviews.order': 'طلب',
    'reviews.overallRating': 'عام',
    'reviews.menuItemReviews': 'تقييمات الأصناف',
    'reviews.photos': 'الصور',
    'reviews.helpful': 'وجدوه مفيداً',
    'reviews.delete': 'حذف التقييم',
    'reviews.deleteTitle': 'حذف التقييم',
    'reviews.deleteBody': 'هل تريد حذف تقييم العميل؟ لا يمكن التراجع عن هذا الإجراء.',
    'reviews.cancel': 'إلغاء',
    'reviews.deleting': 'جار الحذف...',
    'reviews.deleteConfirm': 'حذف التقييم',
    'stats.activeOrders': 'طلبات نشطة',
    'stats.preparing': 'قيد التحضير',
    'stats.outForDelivery': 'في التوصيل',
    'stats.completedToday': 'مكتملة اليوم',
    'manager.available': 'متاح',
    'manager.unavailable': 'غير متاح',
    'manager.cancel': 'إلغاء',
    'manager.delete': 'حذف',
    'manager.deleteAddonTitle': 'حذف الإضافة',
    'manager.deleteAddonBody': 'حذف "{{name}}"؟ ستتم إزالتها من إعداد هذا الصنف أو التصنيف.',
    'manager.deleteRemovableTitle': 'حذف عنصر الإزالة',
    'manager.deleteRemovableBody': 'حذف "{{name}}"؟ لن يراه العملاء كخيار إزالة هنا.',
    'callCenter.title': 'مركز أوامر الهاتف',
    'callCenter.subtitle': 'التقط رقم المتصل، أنشئ مسودة بسرعة، أرسل رابط التأكيد للعميل، ثم ادفع الطلب إلى سير التوصيل الطبيعي.',
    'callCenter.liveCalls': 'مكالمات مباشرة',
    'callCenter.waiting': 'بانتظار العميل',
    'callCenter.active': 'نشطة',
    'callCenter.cancelDraft': 'إلغاء المسودة',
    'callCenter.keepDraft': 'إبقاء المسودة',
    'callCenter.cancelDraftBody': 'هل تريد إلغاء مسودة طلب الهاتف؟ سيتوقف رابط تأكيد العميل عن العمل.',
    'callCenter.cancelling': 'جار الإلغاء...',
    'callCenter.workstation': 'هذا الجهاز',
    'callCenter.workstationHelp': 'استخدم هذا الخيار عندما يجب أن يعرض كل جهاز مركز اتصال مكالمات تحويلة واحدة.',
    'callCenter.extensionPlaceholder': 'التحويلة، مثل 101',
    'callCenter.save': 'حفظ',
    'callCenter.allExtensions': 'يتم عرض المكالمات من جميع وكلاء الهاتف المرتبطين.',
    'callCenter.filteringExtension': 'تصفية المكالمات الواردة إلى التحويلة {{extension}}.',
    'callCenter.extensionShort': 'تحويلة {{extension}}',
    'callCenter.incomingCalls': 'المكالمات الواردة',
    'callCenter.live': 'مباشر',
    'callCenter.recent': 'الأخيرة',
    'callCenter.manualCallerFallback': 'إدخال متصل يدوي',
    'callCenter.manualExtension': 'تحويلة يدوية، اختياري',
    'callCenter.manualCallerNumber': 'رقم المتصل اليدوي',
    'callCenter.add': 'إضافة',
    'callCenter.noLiveShowRecent': 'لا توجد مكالمات مباشرة. عرض المكالمات الأخيرة',
    'callCenter.noCallsLive': 'لا توجد مكالمات ترن الآن.',
    'callCenter.noCallsRecent': 'لا توجد مكالمات بعد. استخدم الإدخال اليدوي حتى يتم ربط الوكيل المحلي.',
    'callCenter.callerProfile': 'ملف المتصل',
    'callCenter.noCaller': 'لم يتم اختيار متصل',
    'callCenter.selectOrEnterPhone': 'اختر أو أدخل رقم هاتف',
    'callCenter.remembered': 'محفوظ لهذا الرقم',
    'callCenter.startDraft': 'بدء مسودة توصيل جديدة',
    'callCenter.failedMessageAlert': 'فشل إرسال الرسالة - استخدم بديلاً الآن',
    'callCenter.duplicateOrderAlert': 'لدى المتصل طلب نشط بالفعل',
    'callCenter.duplicateDraftAlert': 'لدى المتصل مسودة نشطة بالفعل',
    'callCenter.callerRiskSummary': '{{drafts}} مسودة نشطة، {{orders}} طلب نشط. المرحلة الحالية: {{stage}}.',
    'callCenter.openDraft': 'فتح المسودة',
    'callCenter.openOrder': 'فتح الطلب',
    'callCenter.sendLocationOnly': 'إرسال الموقع فقط',
    'callCenter.duplicateDraftBlocked': 'هذا المتصل لديه مسودة نشطة بالفعل. تم فتحها بدلاً من إنشاء مسودة مكررة.',
    'callCenter.stage.none': 'لا يوجد',
    'callCenter.stage.draft': 'مسودة',
    'callCenter.stage.location_sent': 'تم إرسال الموقع',
    'callCenter.stage.waiting_confirmation': 'بانتظار التأكيد',
    'callCenter.stage.submitted': 'تم الإرسال',
    'callCenter.stage.cancelled': 'ملغاة',
    'callCenter.stage.failed_message': 'فشل الرسالة',
    'callCenter.draftWorkspace': 'مساحة المسودة',
    'callCenter.startDraftHelp': 'ابدأ مسودة لبناء طلب هاتفي.',
    'callCenter.draftReadOnly': 'هذه المسودة بحالة {{status}}. تبقى كسجل للقراءة فقط حتى يبقى تاريخ المتصل واضحاً.',
    'callCenter.sendWhatsapp': 'إرسال واتساب',
    'callCenter.sendSms': 'إرسال SMS',
    'callCenter.copyLink': 'نسخ الرابط',
    'callCenter.manualFinish': 'إنهاء يدوي',
    'callCenter.placeOrderNow': 'تثبيت الطلب الآن',
    'callCenter.sendLocationWhatsapp': 'إرسال رابط الموقع واتساب',
    'callCenter.holdForConfirmation': 'انتظار تأكيد العميل',
    'callCenter.hold': 'تعليق',
    'callCenter.customerName': 'اسم العميل',
    'callCenter.addressNotes': 'عنوان التوصيل اليدوي / علامة مميزة',
    'callCenter.orderNotes': 'ملاحظات الطلب للمطبخ/السائق',
    'callCenter.deliveryFee': 'رسوم التوصيل اليدوية',
    'callCenter.deliveryArea': 'منطقة التوصيل',
    'callCenter.fulfillment': 'طريقة الاستلام',
    'callCenter.pickup': 'استلام من المطعم',
    'callCenter.deliveryOrder': 'توصيل',
    'callCenter.paymentIntent': 'طريقة دفع الزبون',
    'callCenter.cash': 'نقدي',
    'callCenter.card': 'بطاقة',
    'callCenter.pickupClearsDelivery': 'التحويل إلى الاستلام من المطعم سيحذف منطقة التوصيل والعنوان والموقع والرسوم من هذه المسودة.',
    'callCenter.keepDelivery': 'إبقاء التوصيل',
    'callCenter.switchToPickup': 'التحويل إلى استلام',
    'callCenter.deliveryAreaName': 'اسم المنطقة',
    'callCenter.deliveryAreaPlaceholder': 'منطقة مخصصة أو علامة مميزة',
    'callCenter.customDelivery': 'مخصص',
    'callCenter.locationNeedsReview': 'موقع العميل قد يحتاج مراجعة رسوم التوصيل.',
    'callCenter.needsFollowUp': 'يحتاج متابعة',
    'callCenter.waitingTooLong': 'بانتظار تأكيد العميل منذ أكثر من 3 دقائق.',
    'callCenter.locationLink': 'رابط الموقع',
    'callCenter.locationLinkSent': 'تم إرسال رابط الموقع الدقيق. تم نسخ الرابط الآمن إذا سمحت صلاحيات المتصفح.',
    'callCenter.requestExactPin': 'طلب موقع دقيق',
    'callCenter.whatsappConsent': 'وافق العميل على استلام تأكيد هذا الطلب عبر واتساب',
    'callCenter.saveDetails': 'حفظ التفاصيل',
    'callCenter.details': 'تفاصيل العميل والتوصيل',
    'callCenter.total': 'الإجمالي',
    'callCenter.subtotal': 'المجموع',
    'callCenter.delivery': 'التوصيل',
    'callCenter.whatsapp': 'واتساب',
    'callCenter.sms': 'SMS',
    'callCenter.noDraft': 'لا توجد مسودة محددة.',
    'callCenter.whatsappReplies': 'ردود واتساب',
    'callCenter.whatsappRepliesHelp': 'تبقى ردود هذا المتصل مرتبطة بمسودة طلب الهاتف.',
    'callCenter.useSmsFallback': 'استخدام SMS بديل',
    'callCenter.noWhatsappMessages': 'لا توجد رسائل واتساب لهذه المسودة بعد.',
    'callCenter.replyPlaceholder': 'رد عبر واتساب',
    'callCenter.send': 'إرسال',
    'callCenter.searchMenu': 'ابحث في المنيو لإضافة أصناف',
    'callCenter.menuBuilder': 'بناء الطلب من المنيو',
    'callCenter.menuBuilderHelp': 'اعثر على الأصناف بسرعة حسب القسم أو الصورة أو النوع أو الإضافات أو نص المنيو العربي.',
    'callCenter.allMenu': 'كل المنيو',
    'callCenter.quickPicks': 'اختيارات سريعة',
    'callCenter.fastPicks': 'اختيارات سريعة',
    'callCenter.fastPicksHelp': 'اختصارات شائعة ومميزة وقت الضغط.',
    'callCenter.popular': 'شائع',
    'callCenter.noMenuMatches': 'لا توجد أصناف تطابق هذا البحث أو القسم.',
    'callCenter.unavailable': 'غير متاح',
    'callCenter.variants': '{{count}} خيارات',
    'callCenter.singleItem': 'صنف واحد',
    'callCenter.customOptions': 'خيارات مخصصة',
    'callCenter.quickAdd': 'إضافة سريعة',
    'callCenter.customize': 'تخصيص',
    'callCenter.itemUnavailable': 'هذا الصنف غير متاح حالياً.',
    'callCenter.chooseVariant': 'اختر النوع',
    'callCenter.addons': 'إضافات',
    'callCenter.removals': 'إزالة',
    'callCenter.quantity': 'الكمية',
    'callCenter.no': 'بدون',
    'callCenter.addToDraft': 'إضافة للمسودة',
    'callCenter.removeLine': 'إزالة',
    'callCenter.draftCart': 'سلة المسودة',
    'callCenter.noItems': 'لا توجد أصناف بعد.',
    'callCenter.connectedOrders': 'طلبات مرتبطة',
    'callCenter.noPreviousOrders': 'لا توجد طلبات سابقة لهذا المتصل.',
    'callCenter.drafts': 'المسودات',
    'callCenter.noDrafts': 'لا توجد مسودات طلبات هاتف بعد.',
    'callCenter.phoneDraft': 'مسودة هاتف',
    'callCenter.itemsCount': '{{count}} صنف',
    'callCenter.finish': 'إنهاء',
    'callCenter.open': 'فتح',
    'callCenter.repeat': 'تكرار',
    'callCenter.orderActive': 'نشط',
    'callCenter.orderRecent': 'سابق',
    'callCenter.manualCallCreated': 'تم إنشاء جلسة اتصال يدوية.',
    'callCenter.manualCallError': 'تعذر إنشاء جلسة الاتصال.',
    'callCenter.extensionSaved': 'تعرض هذه الشاشة الآن مكالمات التحويلة {{extension}}.',
    'callCenter.allExtensionsSaved': 'تعرض هذه الشاشة الآن مكالمات جميع التحويلات.',
    'callCenter.enterCallerPhone': 'أدخل أو اختر رقم هاتف المتصل أولاً.',
    'callCenter.draftStarted': 'تم بدء مسودة طلب الهاتف.',
    'callCenter.draftFirst': 'ابدأ مسودة طلب هاتف أولاً.',
    'callCenter.detailsSaved': 'تم حفظ تفاصيل المسودة.',
    'callCenter.saveDraftError': 'تعذر حفظ المسودة.',
    'callCenter.whatsappConsentRequired': 'أكد أن العميل وافق على استلام رسالة تأكيد واتساب أولاً.',
    'callCenter.linkCopied': 'تم نسخ الرابط الآمن على هذا الجهاز إذا سمحت صلاحيات المتصفح.',
    'callCenter.confirmationSent': 'تم إرسال تأكيد {{channel}}. وتم نسخ الرابط الآمن أيضاً إذا سمحت صلاحيات المتصفح.',
    'callCenter.sendChannelError': 'تعذر إرسال {{channel}}.',
    'callCenter.replyQueued': 'تمت إضافة رد واتساب إلى قائمة الإرسال.',
    'callCenter.replyError': 'تعذر إرسال رد واتساب.',
    'callCenter.manualSubmitted': 'تم إرسال طلب الهاتف اليدوي برقم #{{orderNumber}}.',
    'callCenter.manualSubmitError': 'تعذر إرسال الطلب اليدوي.',
    'callCenter.draftCancelled': 'تم إلغاء مسودة طلب الهاتف.',
    'callCenter.cancelDraftError': 'تعذر إلغاء المسودة.',
    'callCenter.loadedOrder': 'تم تحميل الأصناف من الطلب #{{orderNumber}}.',
    'customer.title': 'إدارة العملاء',
    'customer.subtitle': 'ابحث عن العملاء، راجع سلوك الطلبات، وتحكم بإشارات المخاطر من مساحة مناسبة للكاشير.',
    'customer.total': 'العملاء',
    'customer.verified': 'موثق',
    'customer.restricted': 'مقيّد',
    'customer.blocked': 'محظور',
    'customer.searchPlaceholder': 'ابحث بالاسم أو البريد أو الهاتف...',
    'customer.shown': '{{count}} ظاهر',
    'customer.clear': 'مسح',
    'customer.selectTitle': 'اختر عميلاً',
    'customer.selectHelp': 'سيظهر هنا تاريخ العميل والمخاطر وسلوك الطلبات.',
    'customer.loadFailed': 'فشل تحميل العملاء',
    'customer.loadFailedDetail': 'تعذر تحميل عملاء هذا المطعم. تحقق من الاتصال ثم حاول مرة أخرى.',
    'customer.tryAgain': 'حاول مرة أخرى',
    'customer.notFound': 'لم يتم العثور على عملاء',
    'customer.notFoundHelp': 'جرب اسماً أو بريداً أو رقم هاتف آخر.',
    'customer.blacklisted': 'محظور',
    'customer.review': 'مراجعة',
    'customer.phoneVerified': 'الهاتف موثق',
    'customer.phoneUnverified': 'الهاتف غير موثق',
    'customer.lastOrder': 'آخر طلب #{{orderNumber}} بتاريخ {{date}}.',
    'customer.removeBlacklist': 'إزالة من الحظر',
    'customer.blacklist': 'حظر العميل',
    'customer.blacklistWarning': 'هذا العميل محظور ولا يمكنه إنشاء طلبات جديدة.',
    'customer.confirmBlacklistTitle': 'حظر العميل؟',
    'customer.confirmBlacklistBody': 'هل تريد حظر {{name}}؟ لن يتمكن من إنشاء طلبات جديدة حتى يزيل المدير هذا القيد.',
    'customer.confirmBlacklistAction': 'حظر العميل',
    'customer.confirmUnblacklistTitle': 'إزالة الحظر؟',
    'customer.confirmUnblacklistBody': 'هل تريد إزالة {{name}} من الحظر؟ سيتمكن من إنشاء طلبات جديدة مرة أخرى.',
    'customer.confirmUnblacklistAction': 'إزالة الحظر',
    'customer.keepStatus': 'إبقاء الحالة الحالية',
    'customer.updatingStatus': 'جار التحديث...',
    'customer.totalOrders': 'إجمالي الطلبات',
    'customer.activeOrders': 'طلبات نشطة',
    'customer.cancelRate': 'نسبة الإلغاء',
    'customer.totalSpent': 'إجمالي الإنفاق',
    'customer.averageOrderValue': 'متوسط قيمة الطلب',
    'customer.completedOrders': 'طلبات مكتملة',
    'customer.orderMix': 'مزيج الطلبات',
    'customer.orderMixValues': '{{delivery}} توصيل | {{pickup}} استلام | {{cancelled}} ملغي/مرفوض',
    'customer.orderHistory': 'سجل الطلبات',
    'customer.orderHistoryHelp': 'أحدث الطلبات أولاً، بتنسيق سريع للبحث عن العميل.',
    'customer.ordersCount': '{{count}} طلب',
    'customer.noOrders': 'لا توجد طلبات',
    'customer.orderNumber': 'رقم الطلب',
    'customer.date': 'التاريخ',
    'customer.status': 'الحالة',
    'customer.type': 'النوع',
    'customer.totalAmount': 'الإجمالي',
    'orders.liveBoard': 'لوحة الطلبات المباشرة',
    'orders.title': 'الطلبات',
    'orders.subtitle': 'راجع الطلبات النشطة، عدّل الأصناف، اطبع الوصولات، وحرّك الطلبات خلال الخدمة.',
    'orders.active': 'نشطة',
    'orders.scheduled': 'مجدولة',
    'orders.delivery': 'توصيل',
    'orders.assignedDrivers': 'مسندة لسائق',
    'orders.completed': 'مكتملة',
    'orders.searchPlaceholder': 'ابحث برقم الطلب أو اسم العميل...',
    'orders.compact': 'مضغوط',
    'orders.comfortable': 'مريح',
    'orders.hideClosed': 'إخفاء المغلقة',
    'orders.showClosed': 'عرض المغلقة',
    'orders.activeSection': 'الطلبات النشطة',
    'orders.rejectedSection': 'الطلبات المرفوضة',
    'orders.cancelledSection': 'الطلبات الملغاة',
    'orders.completedSection': 'الطلبات المكتملة',
    'orders.notFound': 'لم يتم العثور على طلبات',
    'orders.notFoundHelp': 'جرّب فلتر أو بحثاً مختلفاً.',
    'orders.noLiveMatches': 'لا توجد طلبات مباشرة تطابق هذا العرض',
    'orders.closedHidden': '{{count}} طلب مغلق مخفي لوضع الذروة.',
    'orders.showClosedOrders': 'عرض الطلبات المغلقة',
    'orders.filter': 'فلتر',
    'orders.all': 'الكل',
    'orders.pickup': 'استلام',
    'pos.searchPlaceholder': 'ابحث في المنيو أو التصنيف أو الصنف...',
    'pos.keyboard': 'لوحة المفاتيح',
    'pos.repeat': 'تكرار',
    'pos.allMenu': 'كل المنيو',
    'pos.itemsCount': '{{count}} صنف',
    'pos.loadingMenu': 'جار تحميل المنيو...',
    'pos.noMenuMatches': 'لا توجد أصناف تطابق هذا العرض.',
    'pos.currentOrder': 'الطلب الحالي',
    'pos.lineItems': '{{count}} سطر',
    'pos.parked': 'محفوظة {{count}}',
    'pos.clear': 'مسح',
    'pos.startCounterOrder': 'ابدأ طلب كاونتر',
    'pos.tapMenuTiles': 'اضغط على أصناف المنيو لإضافتها هنا.',
    'pos.available': 'متاح',
    'pos.unavailable': 'غير متاح',
    'pos.variants': '{{count}} خيارات',
    'pos.singleItem': 'صنف واحد',
    'pos.add': 'إضافة',
    'pos.searchKeyboardPlaceholder': 'اضغط للبحث',
    'pos.customerNamePlaceholder': 'اسم العميل اختياري',
    'pos.customerPhonePlaceholder': 'الهاتف اختياري',
    'pos.lookup': 'بحث',
    'pos.discountCodePlaceholder': 'كود الخصم',
    'pos.apply': 'تطبيق',
    'pos.subtotal': 'المجموع الفرعي',
    'pos.deliveryFee': 'رسوم التوصيل',
    'pos.discount': 'الخصم',
    'pos.total': 'الإجمالي',
    'pos.resumedParkedOrder': 'سلة محفوظة مستعادة',
    'pos.payNowOrParkAgain': 'حصّل الدفع الآن أو احفظ السلة مرة أخرى.',
    'pos.settlingMobileOrder': 'تسوية طلب الموبايل',
    'pos.editItemsNoReprint': 'عدّل الأصناف عند الحاجة ثم حصّل الدفع. لن تتم إعادة طباعة تذاكر المطبخ.',
    'pos.parkOrder': 'حفظ السلة مؤقتاً',
    'pos.cannotParkSettlement': 'لا يمكن حفظ طلب التسوية',
    'pos.updateParkedOrder': 'تحديث السلة المحفوظة',
    'pos.cash': 'نقدي',
    'pos.card': 'بطاقة',
    'pos.closeShiftTitle': 'إغلاق وردية الكاشير',
    'pos.closeoutReport': 'تقرير الإغلاق',
    'pos.expected': 'المتوقع',
    'pos.counted': 'المعدود',
    'pos.overShort': 'زيادة / نقص',
    'pos.starting': 'البداية',
    'pos.cashSales': 'مبيعات نقدية',
    'pos.cardSales': 'مبيعات بطاقة',
    'pos.cashIn': 'نقد داخل',
    'pos.cashOut': 'نقد خارج',
    'pos.voids': 'إلغاءات',
    'pos.refunds': 'مبالغ مستردة',
    'pos.countedCashPlaceholder': 'النقد المعدود',
    'pos.closeoutNotesPlaceholder': 'ملاحظات الإغلاق',
    'pos.printCloseoutReport': 'طباعة تقرير الإغلاق',
    'pos.closeShift': 'إغلاق الوردية',
    'pos.cashPaymentTitle': 'دفع نقدي',
    'pos.amountDue': 'المبلغ المطلوب',
    'pos.cashReceived': 'النقد المستلم',
    'pos.changeDue': 'الباقي',
    'pos.exact': 'بالضبط',
    'pos.back': 'رجوع',
    'pos.completeCashPayment': 'إتمام الدفع النقدي',
    'pos.savedForLater': 'محفوظ لوقت لاحق',
    'pos.lineItemsStayEditable': '{{count}} سطر سيبقى قابلاً للتعديل.',
    'pos.nameOrLabel': 'الاسم أو التسمية',
    'pos.parkLabelPlaceholder': 'اسم العميل أو الهاتف أو تسمية الطلب',
    'pos.internalNoteOptional': 'ملاحظة داخلية اختيارية',
    'pos.parkNotesPlaceholder': 'بانتظار صديق، سيدفع العميل لاحقاً، إلخ.',
    'pos.writeKeyboardPlaceholder': 'اضغط للكتابة',
    'pos.saveParkedChanges': 'حفظ السلة',
    'pos.parkThisOrder': 'حفظ هذه السلة',
    'pos.parkedOrdersTitle': 'السلات المحفوظة',
    'pos.parkedOrdersHelp': 'مبيعات غير مكتملة محفوظة بأمان قبل إنشاء الطلب.',
    'pos.parkedSearchPlaceholder': 'ابحث في السلات بالاسم أو الهاتف أو الملاحظة',
    'pos.noParkedOrders': 'لا توجد سلات محفوظة الآن.',
    'pos.open': 'مفتوح',
    'pos.edit': 'تعديل',
    'pos.payCash': 'دفع نقدي',
    'pos.payCard': 'دفع بطاقة',
    'pos.cancel': 'إلغاء',
    'pos.pendingReplaceTitle': 'استبدال السلة الحالية؟',
    'pos.pendingCancelTitle': 'حذف السلة المحفوظة؟',
    'pos.pendingReplaceBody': 'تحميل "{{label}}" واستبدال السلة الحالية؟ سيتم مسح الأصناف غير المحفوظة في السلة الحالية.',
    'pos.pendingCancelBody': 'حذف السلة المحفوظة "{{label}}"؟ ستتم إزالة عملية البيع غير المكتملة من طابور العمل.',
    'pos.keepCurrentCart': 'إبقاء السلة الحالية',
    'pos.keepParkedOrder': 'إبقاء السلة المحفوظة',
    'pos.working': 'جار العمل...',
    'pos.replaceCart': 'استبدال السلة',
    'pos.cancelParkedOrder': 'حذف السلة المحفوظة',
    'pos.cardTerminalPaymentTitle': 'دفع جهاز البطاقة',
    'pos.amountToApprove': 'المبلغ المطلوب اعتماده على الجهاز',
    'pos.cardTerminalHelp': 'أكمل الدفع على جهاز البطاقة الخارجي أولاً. اضغط اعتماد فقط بعد تأكيد العملية على الجهاز.',
    'pos.cardTerminalApproved': 'تم اعتماد جهاز البطاقة',
    'pos.marketplaceOrderTitle': 'طلب {{provider}}',
    'pos.marketplaceClipReceipt': 'وصل منصة مرفق',
    'pos.marketplaceHelp': 'ستتم طباعة تذاكر المطبخ الآن مع وصل كاشير لإرفاقه بإيصال المنصة. لن يفتح الدرج ولن يضاف نقد أو بطاقة للصندوق.',
    'pos.orderTotal': 'إجمالي الطلب',
    'pos.ticketNumber': 'رقم التذكرة',
    'pos.marketplaceTicketPlaceholder': 'تذكرة {{provider}}',
    'pos.ticketNumberKeyboard': 'أدخل رقم التذكرة',
    'pos.printAndSendToKitchen': 'طباعة وإرسال للمطبخ',
    'pos.managerApproval': 'موافقة المدير',
    'pos.drawerApprovalHelp': 'يجب أن يوافق مدير مطعم موثّق على هذا الإجراء. يتم تسجيل الطلب ومهمة فتح الدرج معاً.',
    'pos.openDrawer': 'فتح الدرج',
    'pos.orderComplete': 'تم إكمال الطلب',
    'pos.reprintReceipt': 'إعادة طباعة الوصل',
    'pos.retryFailedPrints': 'إعادة محاولة الطباعة الفاشلة',
    'pos.viewOrder': 'عرض الطلب',
    'pos.newOrder': 'طلب جديد',
    'pos.amountPlaceholder': 'المبلغ',
    'pos.reasonPlaceholder': 'السبب',
    'pos.saveMovement': 'حفظ الحركة',
    'pos.voidTransaction': 'إلغاء العملية',
    'pos.voidApproval': 'إلغاء #{{orderNumber}} بقيمة {{total}} يتطلب موافقة المدير.',
    'pos.voidTransactionButton': 'إلغاء العملية',
    'pos.refund': 'استرداد',
    'pos.refunded': 'مستردة',
    'pos.refundTransaction': 'استرداد العملية',
    'pos.refundApproval': 'استرداد #{{orderNumber}} بقيمة {{total}} يتطلب موافقة المدير.',
    'pos.voidHelp': 'استخدم الإلغاء لخطأ ضمن نفس الوردية قبل خروج الطلب من المطعم. سيتم إلغاء البيع والطلب.',
    'pos.refundHelp': 'يسجل هذا استرداداً كاملاً. أكمل استرداد البطاقة أو المنصة على جهاز المزود أولاً؛ الاسترداد النقدي يخفض رصيد الدرج.',
    'pos.adjustmentReason': 'السبب مطلوب',
    'pos.adminPassword': 'كلمة مرور المدير',
    'pos.adminPasswordPlaceholder': 'كلمة مرور حساب مدير المطعم',
    'pos.approveWithGoogle': 'موافقة عبر Google',
    'pos.googleApprovalHelp': 'ستفتح نافذة Google للتحقق من حساب مدير المطعم.',
    'pos.secureManagerApprovalHelp': 'تتحقق Firebase من الموافقة وتبقى صالحة لفترة قصيرة فقط. لا تُرسل كلمة المرور إلى وظائف BiteOS ولا يتم حفظها.',
    'pos.approveVoid': 'الموافقة والإلغاء',
    'pos.approveRefund': 'الموافقة على الاسترداد الكامل',
    'pos.managerApprovalUnavailable': 'لا يملك حساب المدير طريقة تسجيل دخول بكلمة مرور أو Google متاحة للموافقة الآمنة.',
    'pos.voidSuccess': 'تم إلغاء الطلب #{{orderNumber}} بقيمة {{total}}.',
    'pos.refundSuccess': 'تم استرداد الطلب #{{orderNumber}} بقيمة {{total}}.',
    'pos.adjustmentFailed': 'تعذر تعديل العملية.',
    'pos.cashControlsRequireOnline': 'تتطلب عمليات النقد الحساسة اتصالاً بالإنترنت لموافقة المدير الآمنة.',
    'pos.manualDrawerAccess': 'فتح درج النقد يدوياً',
    'pos.drawerReasonPlaceholder': 'لماذا يجب فتح الدرج؟',
    'pos.approveAndOpenDrawer': 'الموافقة وفتح الدرج',
    'pos.drawerRequestQueued': 'تمت الموافقة على طلب فتح الدرج الآمن وإضافته للطابور.',
    'pos.drawerRequestFailed': 'تعذر إكمال طلب فتح الدرج الآمن.',
    'pos.cashInHelp': 'سجّل النقد المضاف إلى الدرج خارج عملية بيع.',
    'pos.cashOutHelp': 'سجّل النقد المسحوب من الدرج خارج عملية استرداد.',
    'pos.cashMovementApprovalHelp': 'يتم حفظ الوردية ورصيد الدرج المتوقع والسجل المالي والتدقيق ومهمة الدرج معاً.',
    'pos.approveAndSaveMovement': 'الموافقة وحفظ الحركة',
    'pos.cashMovementSaved': 'تم حفظ حركة نقدية بقيمة {{amount}} وإضافة فتح الدرج للطابور.',
    'pos.cashMovementSavedNoDrawer': 'تم حفظ حركة نقدية بقيمة {{amount}}، لكن لا توجد محطة درج مفعلة.',
    'pos.cashMovementFailed': 'تعذر حفظ الحركة النقدية.',
    'pos.fastActions': 'إجراءات سريعة',
    'pos.fastActionsHelp': 'اختصارات الشائع والمميز والحديث والتكرار لوقت الذروة.',
    'pos.repeatLastOrder': 'تكرار آخر طلب',
    'pos.noFastActions': 'ستظهر اختصارات الأصناف المميزة والحديثة هنا.',
    'pos.recent': 'حديث',
    'pos.popular': 'شائع',
    'pos.quickPicks': 'اختيارات سريعة',
    'pos.undo': 'تراجع عن آخر تعديل',
    'pos.menuFallback': 'المنيو',
    'pos.printJob': 'مهمة طباعة',
    'pos.itemReady': 'جاهز للإضافة إلى السلة.',
    'pos.chooseVariant': 'اختر الخيار',
    'pos.addons': 'الإضافات',
    'pos.remove': 'إزالة',
    'pos.noModifier': 'بدون {{name}}',
    'pos.kitchenNotePlaceholder': 'ملاحظة مطبخ اختيارية',
    'pos.kitchenNoteKeyboard': 'اضغط لكتابة ملاحظة المطبخ',
    'pos.quantity': 'الكمية',
    'pos.addToCart': 'إضافة إلى السلة - {{total}}',
    'pos.counterPos': 'نقطة بيع الكاونتر',
    'pos.openCashRegister': 'فتح صندوق الكاشير',
    'pos.openRegisterHelp': 'ابدأ وردية الكاشير قبل أخذ طلبات الكاونتر أو فتح الدرج أو تتبع حركات النقد.',
    'pos.startingCashInDrawer': 'النقد الافتتاحي في الدرج',
    'pos.openPosShift': 'فتح وردية الكاشير',
    'pos.registerConnection': 'اتصال الصندوق',
    'pos.onlineReady': 'متصل وجاهز للفتح',
    'pos.offlineCannotOpenShift': 'أعد الاتصال قبل فتح وردية جديدة',
    'pos.noStartingCash': 'بدون عهدة',
    'pos.openShiftRequiresOnline': 'أعد الاتصال قبل فتح وردية نقطة بيع جديدة.',
    'pos.invalidStartingCash': 'يجب أن يكون النقد الافتتاحي صفراً أو أكثر.',
    'pos.shiftOpenedSuccess': 'تم فتح وردية نقطة البيع بمبلغ {{amount}}.',
    'pos.existingShiftJoined': 'تم الانضمام إلى الوردية المفتوحة الحالية. بقي النقد الافتتاحي {{amount}}.',
    'pos.duplicateShiftsDetected': 'تم اكتشاف {{count}} ورديات قديمة مفتوحة. تم اختيار الأقدم وأضيفت مراجعة حرجة إلى مركز العمليات.',
    'pos.openShiftFailed': 'تعذر فتح وردية نقطة البيع.',
    'pos.cashRegister': 'صندوق الكاشير',
    'pos.menuTiles': '{{count}} بطاقة منيو',
    'pos.backToAdmin': 'العودة إلى الإدارة',
    'pos.registerTools': 'أدوات الصندوق',
    'pos.printerReady': 'الطابعة جاهزة',
    'pos.printerOffline': 'الطابعة غير متصلة',
    'pos.parkedLabel': 'سلات محفوظة',
    'pos.orders': 'طابور العمل',
    'pos.orderDetails': 'العميل',
    'pos.online': 'متصل',
    'pos.offlinePos': 'نقطة البيع غير متصلة',
    'pos.localAgent': 'الوكيل المحلي',
    'pos.agentOff': 'الوكيل متوقف',
    'pos.cloudAgent': 'وكيل السحابة',
    'pos.cloudOff': 'السحابة متوقفة',
    'pos.pending': '{{count}} معلّق',
    'pos.expectedDrawer': 'الدرج المتوقع',
    'pos.availableMenu': 'المنيو المتاح',
    'pos.offlineQueue': 'طابور دون اتصال',
    'pos.sync': 'مزامنة',
    'pos.drawer': 'الدرج',
    'pos.closeShiftAction': 'إغلاق الوردية',
    'pos.recentPos': 'آخر مبيعات نقطة البيع',
    'pos.noPosSalesYet': 'لا توجد مبيعات نقطة بيع بعد.',
    'pos.pendingOrder': 'قيد الانتظار',
    'pos.void': 'إلغاء',
    'pos.park': 'حفظ',
    'pos.space': 'مسافة',
    'pos.hide': 'إخفاء',
    'pos.printDrawerStatus': 'حالة الطباعة والدرج',
    'pos.waitingPrintJobs': 'بانتظار ظهور مهام الطباعة.',
    'pos.offlinePrintStatus': 'تمت طباعة الطلب غير المتصل عبر الوكيل المحلي عند توفره. ستظهر حالة الطباعة السحابية بعد المزامنة.',
    'pos.printKitchen': 'المطبخ',
    'pos.printReceipt': 'وصل',
    'pos.printMarketplaceReceipt': 'وصل منصة',
    'pos.printSalesReport': 'تقرير مبيعات',
    'pos.printDrawer': 'الدرج',
    'pos.printTest': 'اختبار',
    'pos.printCloseout': 'إغلاق',
    'pos.cardReceiptCustomer': 'نسخة العميل',
    'pos.cardReceiptMerchant': 'نسخة التاجر',
    'pos.cardTwoReceiptsNow': 'يتم الآن إرسال وصلين للبطاقة.',
    'pos.cardTwoReceiptsKitchenNow': 'يتم الآن إرسال تذاكر المطبخ ووصلين للبطاقة.',
    'pos.cardTwoReceiptsInstant': 'تم إرسال وصلي البطاقة فوراً.',
    'pos.cardTwoReceiptsKitchenInstant': 'تم إرسال تذاكر المطبخ ووصلي البطاقة فوراً.',
    'pos.cardTwoReceiptsQueued': 'تمت إضافة مهمتي طباعة لوصلَي البطاقة.',
    'pos.cardTwoReceiptsKitchenQueued': 'تمت إضافة تذاكر المطبخ ومهمتي طباعة لوصلَي البطاقة.',
    'staff.title': 'إدارة الموظفين',
    'staff.subtitle': 'أنشئ حسابات السائقين ومكاتب مركز الاتصال، راجع الصلاحيات، واجعل فريق المطعم جاهزاً للخدمة.',
    'staff.createCallCenterDesk': 'إنشاء مكتب مركز اتصال',
    'staff.createDriver': 'إنشاء سائق',
    'staff.totalStaff': 'الموظفون',
    'staff.admins': 'المديرون',
    'staff.drivers': 'السائقون',
    'staff.callCenterDesks': 'مركز الاتصال',
    'staff.searchPlaceholder': 'ابحث عن موظف بالاسم أو البريد...',
    'staff.allUsers': 'كل المستخدمين',
    'staff.noStaff': 'لم يتم العثور على موظفين',
    'staff.noStaffHelp': 'جرّب بحثاً أو فلتر دور مختلف.',
    'staff.email': 'البريد',
    'staff.phone': 'الهاتف',
    'staff.noPhone': 'لا يوجد هاتف محفوظ',
    'staff.editStaff': 'تعديل الموظف',
    'staff.loadFailed': 'فشل تحميل الموظفين',
    'staff.loadFailedDetail': 'تعذر تحميل قائمة موظفي هذا المطعم. تحقق من الاتصال ثم حاول مرة أخرى.',
    'staff.tryAgain': 'حاول مرة أخرى',
    'staff.createTitle': 'إنشاء حساب موظف',
    'staff.editTitle': 'تعديل موظف',
    'staff.fullName': 'الاسم الكامل',
    'staff.fullNamePlaceholder': 'أدخل الاسم الكامل',
    'staff.emailPlaceholder': 'أدخل البريد الإلكتروني',
    'staff.password': 'كلمة المرور',
    'staff.passwordPlaceholder': 'أدخل كلمة المرور',
    'staff.role': 'الدور',
    'staff.deliveryDriver': 'سائق توصيل',
    'staff.callCenterDesk': 'مكتب مركز اتصال',
    'staff.callCenterRoleHelp': 'شاشة مركز اتصال مركزة مع تنبيهات طلبات صامتة',
    'staff.driverRoleHelp': 'وصول تطبيق السائق للموبايل',
    'staff.adminConfigOnly': 'يتم إنشاء حسابات مدير المطعم فقط من إعدادات مطعم ForgedOS.',
    'staff.cancel': 'إلغاء',
    'staff.creating': 'جار الإنشاء...',
    'staff.createAccount': 'إنشاء حساب موظف',
    'staff.saving': 'جار الحفظ...',
    'staff.saveChanges': 'حفظ التغييرات',
    'staff.emailLocked': 'لا يمكن تغيير البريد الإلكتروني',
    'staff.phoneNumber': 'رقم الهاتف',
    'staff.phonePlaceholder': 'XXX-XXX-XXXX',
    'staff.roleLocked': 'لا يمكن تغيير الدور',
    'staff.invalidPhone': 'يرجى إدخال رقم هاتف صحيح (XXX-XXX-XXXX)',
    'phoneSetup.eyebrow': 'إعداد الهاتف',
    'phoneSetup.title': 'ربط رقم المتصل بدون إبطاء الكاونتر',
    'phoneSetup.subtitle': 'ثبّت الوكيل المحلي على كل جهاز ويندوز، اربطه مرة واحدة بكوده الخاص، ثم اختر تحويلة الهاتف القريبة داخل الوكيل.',
    'phoneSetup.onlineAgents': 'وكلاء متصلون',
    'phoneSetup.phoneReady': 'الهاتف جاهز',
    'phoneSetup.addWorkstation': 'إضافة جهاز عمل',
    'phoneSetup.addWorkstationHelp': 'نفس المثبّت يتعامل مع طباعة المطبخ والوصولات والأدراج وكشف الهاتف.',
    'phoneSetup.downloadWindows': 'تحميل لويندوز',
    'phoneSetup.singlePhone': 'هاتف واحد',
    'phoneSetup.singlePhoneHelp': 'هاتف قريب واحد وجهاز ويندوز واحد.',
    'phoneSetup.callCenter': 'مركز اتصال',
    'phoneSetup.callCenterHelp': 'كرر الإعداد لكل تحويلة وجهاز عمل.',
    'phoneSetup.workstationName': 'اسم جهاز العمل',
    'phoneSetup.workstationPlaceholder': 'الكاونتر الأمامي أو مكتب مركز الاتصال 1',
    'phoneSetup.expectedExtension': 'التحويلة المتوقعة',
    'phoneSetup.optional': 'اختياري',
    'phoneSetup.extensionPlaceholder': 'مثال: 101',
    'phoneSetup.generating': 'جار الإنشاء...',
    'phoneSetup.generateCode': 'إنشاء كود ربط من ستة أرقام',
    'phoneSetup.enterInAgent': 'أدخل هذا في الوكيل المحلي',
    'phoneSetup.expires': 'ينتهي في {{date}} ويعمل مرة واحدة.',
    'phoneSetup.copyCode': 'نسخ الكود',
    'phoneSetup.codeCreated': 'تم إنشاء كود الربط. أدخله في وكيل ForgedOS المحلي على هذا الجهاز.',
    'phoneSetup.codeCopied': 'تم نسخ كود الربط.',
    'phoneSetup.createCodeError': 'تعذر إنشاء كود ربط الوكيل المحلي.',
    'phoneSetup.revokeConfirm': 'إلغاء ربط الجهاز',
    'phoneSetup.revokeTitle': 'إلغاء ربط الوكيل المحلي',
    'phoneSetup.revokeBody': 'إلغاء ربط {{name}}؟ سيتوقف هذا الجهاز عن مزامنة أحداث الهاتف والطباعة حتى يتم ربطه مرة أخرى.',
    'phoneSetup.cancel': 'إلغاء',
    'phoneSetup.revoking': 'جار إلغاء الربط...',
    'phoneSetup.revoked': 'تم إلغاء ربط الوكيل المحلي. اربط الجهاز مرة أخرى عندما تكون جاهزاً.',
    'phoneSetup.revokeError': 'تعذر إلغاء ربط الوكيل المحلي.',
    'phoneSetup.afterPairing': 'ماذا يحدث بعد الربط',
    'phoneSetup.afterPairingHelp': 'افتح تبويب الهاتف في الوكيل المحلي، اضغط كشف اتصال الهاتف، اختر التحويلة القريبة، ثم ابدأ اختبار 60 ثانية واتصل بالمطعم.',
    'phoneSetup.adminDesk': 'يبقى المدير متاحاً كمكتب 1',
    'phoneSetup.adminDeskHelp': 'في مطعم به هاتف واحد، يستطيع حساب المدير استخدام مركز الاتصال مباشرة. في المراكز الأكبر، أنشئ حسابات مكتب مركز اتصال من الموظفين وأنشئ كود ربط منفصل لكل جهاز ويندوز.',
    'phoneSetup.workstationHealth': 'حالة أجهزة العمل',
    'phoneSetup.workstationHealthHelp': 'يجب أن يعرض كل مكتب مركز اتصال تحويلته ونبضه الأخير.',
    'phoneSetup.extensionShort': 'تحويلة {{extension}}',
    'phoneSetup.notSelected': 'غير محددة',
    'phoneSetup.driverPending': 'تعريف الهاتف بانتظار الإعداد',
    'phoneSetup.agentOnline': 'الوكيل متصل',
    'phoneSetup.agentOffline': 'الوكيل غير متصل',
    'phoneSetup.phoneConnected': 'الهاتف متصل',
    'phoneSetup.phoneNeedsSetup': 'الهاتف يحتاج إعداد',
    'phoneSetup.version': 'الإصدار',
    'phoneSetup.lastHeartbeat': 'آخر نبض',
    'phoneSetup.lastPhoneEvent': 'آخر حدث هاتف',
    'phoneSetup.mode': 'الوضع',
    'phoneSetup.callCenterDesk': 'مكتب مركز اتصال',
    'phoneSetup.revoke': 'إلغاء الربط وإعادة الربط',
    'phoneSetup.emptyTitle': 'لا يوجد وكلاء محليون موحدون مرتبطون بعد',
    'phoneSetup.emptyHelp': 'ثبّت وكيل ويندوز، أنشئ كوداً، واربط أول جهاز عمل.',
    'phoneSetup.guideReadonlyTitle': 'قراءة فقط حسب التصميم',
    'phoneSetup.guideReadonlyText': 'يراقب ForgedOS أحداث رقم المتصل. لا يمكنه الرد أو الاتصال أو التعليق أو تحويل المكالمات.',
    'phoneSetup.guideMissingTitle': 'خطوط الهاتف غير ظاهرة؟',
    'phoneSetup.guideMissingText': 'اطلب من فني الهاتف تثبيت تعريف هاتف ويندوز الخاص بالشركة المصنعة للـ PBX. يستخدم NEC SL2100 تعريف CTI الرسمي.',
    'phoneSetup.guideFallbackTitle': 'بديل متقدم',
    'phoneSetup.guideFallbackText': 'يمكن تفعيل SMDR من درج الفني في الوكيل المحلي، لكن بعض أنظمة PBX ترسل المكالمات فقط بعد انتهائها.',
    'phoneSetup.never': 'أبداً',
    'phoneSetup.unknown': 'غير معروف',
    'discount.eyebrow': 'العروض',
    'discount.title': 'أكواد الخصم',
    'discount.subtitle': 'أنشئ وأوقف أكواد الخصم الموجهة للعملاء بدون مغادرة لوحة الإدارة.',
    'discount.create': 'إنشاء خصم',
    'discount.totalCodes': 'الأكواد',
    'discount.activeCodes': 'نشطة',
    'discount.pausedCodes': 'متوقفة',
    'discount.expiringCodes': 'تنتهي قريباً',
    'discount.searchPlaceholder': 'ابحث بالكود أو الوصف...',
    'discount.all': 'الكل',
    'discount.active': 'نشط',
    'discount.paused': 'متوقف',
    'discount.noCodes': 'لا توجد أكواد خصم بعد',
    'discount.noCodesHelp': 'أنشئ كوداً عندما تريد تشغيل عرض مضبوط.',
    'discount.noMatches': 'لا توجد أكواد خصم تطابق هذا العرض',
    'discount.noMatchesHelp': 'جرّب بحثاً أو فلتر حالة مختلفاً.',
    'discount.dismiss': 'إغلاق',
    'discount.discount': 'الخصم',
    'discount.minOrder': 'الحد الأدنى',
    'discount.usage': 'الاستخدام',
    'discount.expires': 'ينتهي',
    'discount.status': 'الحالة',
    'discount.pause': 'إيقاف الخصم',
    'discount.activate': 'تفعيل الخصم',
    'discount.inactive': 'غير نشط',
    'discount.createTitle': 'إنشاء كود خصم',
    'discount.code': 'الكود',
    'discount.description': 'الوصف',
    'discount.type': 'النوع',
    'discount.value': 'القيمة',
    'discount.percentage': 'نسبة مئوية',
    'discount.fixed': 'مبلغ ثابت',
    'discount.minOrderOptional': 'الحد الأدنى للطلب (اختياري)',
    'discount.maxUsesOptional': 'أقصى استخدام (اختياري)',
    'discount.expiryOptional': 'تاريخ الانتهاء (اختياري)',
    'discount.activeOnCreate': 'نشط',
    'discount.cancel': 'إلغاء',
    'discount.creating': 'جار الإنشاء...',
    'discount.createButton': 'إنشاء',
    'discount.loadFailed': 'فشل تحميل أكواد الخصم. يرجى التحديث أو المحاولة مرة أخرى.',
    'discount.requiredError': 'الكود والوصف وقيمة الخصم مطلوبة.',
    'discount.percentError': 'لا يمكن أن تتجاوز خصومات النسبة 100%.',
    'discount.createFailed': 'تعذر إنشاء كود الخصم. تحقق من التفاصيل وحاول مرة أخرى.',
    'discount.created': 'تم إنشاء كود الخصم {{code}}.',
    'discount.statusUpdated': 'تم {{status}} كود الخصم.',
    'discount.statusFailed': 'تعذر تحديث حالة الخصم. حاول مرة أخرى.',
  },
};

const getStoredLanguage = (): AdminLanguage => {
  if (typeof window === 'undefined') return 'en';
  return window.localStorage.getItem(STORAGE_KEY) === 'ar' ? 'ar' : 'en';
};

const applyLanguage = (language: AdminLanguage) => {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.dataset.adminLanguage = language;
};

const interpolate = (template: string, params?: Record<string, string | number>) => {
  if (!params) return template;
  return Object.entries(params).reduce(
    (text, [key, value]) => text.replace(new RegExp(`{{${key}}}`, 'g'), String(value)),
    template,
  );
};

export const useAdminI18n = (options: { applyToDocument?: boolean } = {}) => {
  const applyToDocument = Boolean(options.applyToDocument);
  const [language, setLanguageState] = useState<AdminLanguage>(() => getStoredLanguage());

  useEffect(() => {
    if (applyToDocument) applyLanguage(language);
  }, [applyToDocument, language]);

  useEffect(() => {
    const handleLanguageChange = () => setLanguageState(getStoredLanguage());
    if (typeof window === 'undefined') return undefined;
    window.addEventListener(LANGUAGE_EVENT, handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_EVENT, handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const setLanguage = useCallback((nextLanguage: AdminLanguage) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    setLanguageState(nextLanguage);
    if (applyToDocument) applyLanguage(nextLanguage);
    window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT));
  }, [applyToDocument]);

  const t = useCallback((key: TranslationKey, params?: Record<string, string | number>) => (
    interpolate(translations[language][key] || translations.en[key] || key, params)
  ), [language]);

  return useMemo(() => ({
    language,
    direction: language === 'ar' ? 'rtl' as const : 'ltr' as const,
    isArabic: language === 'ar',
    setLanguage,
    t,
  }), [language, setLanguage, t]);
};

export type AdminTranslationKey = TranslationKey;
